import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Sales from './Sales'; // Phase 415 — embed inside Finance content pane
import Purchases from './Purchases'; // Phase 421 — embed inside Finance content pane (Sales-mirror for suppliers)
import { Bank, CurrencyEur, TrendUp, TrendDown, Plus, PencilSimple, Trash, Funnel, Percent, ArrowRight, ArrowsClockwise, SlidersHorizontal, Eye, EyeSlash, ArrowSquareOut, FilePdf, Link, MagnifyingGlass } from '@phosphor-icons/react';
import { formatDateDMY } from '../utils/dateUtils';
import DatePickerDMY from '../components/DatePickerDMY';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Check, CaretUpDown } from '@phosphor-icons/react';
import { openPdfInBrowser } from '../utils/pdfUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const VAT_RATE = 0.19;

function calcVat(amount, vatIncluded) {
  const v = parseFloat(amount) || 0;
  if (v <= 0) return { gross: 0, net: 0, vat: 0, alloc: 0, reserve: 0, socialIns: 0, finalNet: 0 };
  let net, vat;
  if (vatIncluded) {
    net = Math.round((v / (1 + VAT_RATE)) * 100) / 100;
    vat = Math.round((v - net) * 100) / 100;
  } else {
    net = v;
    vat = Math.round(v * VAT_RATE * 100) / 100;
  }
  const alloc = Math.round(net * 0.125 * 100) / 100;
  const reserve = Math.round(net * 0.10 * 100) / 100;
  const socialIns = Math.round(net * 0.10 * 100) / 100;
  return { gross: vatIncluded ? v : Math.round((net + vat) * 100) / 100, net, vat, alloc, reserve, socialIns, finalNet: Math.round((net - alloc - reserve - socialIns) * 100) / 100 };
}

// Phase 450 — Searchable BankAccount picker used by the Supplier
// Payment form (and any other place that needs to pick a bank account
// from Finance → Bank Accounts). Shows an empty state with a "Create"
// shortcut when the user has no bank accounts yet.
function BankAccountCombobox({ t, bankAccounts, value, onChange, onCreateBank, testid = 'bank-combobox' }) {
  const [open, setOpen] = React.useState(false);
  const selected = (bankAccounts || []).find(b => b.account_id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="rounded-none mt-1 w-full justify-between font-normal"
          data-testid={`${testid}-trigger`}
        >
          <span className={selected ? '' : 'text-zinc-400'}>
            {selected ? selected.name : (t('selectBankAccount') || 'Select bank account…')}
          </span>
          <CaretUpDown size={14} className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0 rounded-none" align="start" data-testid={`${testid}-popover`}>
        {(!bankAccounts || bankAccounts.length === 0) ? (
          <div className="p-4 text-center space-y-3" data-testid={`${testid}-empty`}>
            <p className="text-sm text-zinc-600">{t('noBankAccountsCreated') || 'No bank accounts created'}</p>
            <Button
              type="button"
              size="sm"
              className="rounded-none bg-emerald-700 hover:bg-emerald-800 w-full"
              onClick={() => { setOpen(false); onCreateBank?.(); }}
              data-testid={`${testid}-create-shortcut`}
            >
              <Plus size={14} className="mr-1" /> {t('createBankAccount') || 'Create bank account'}
            </Button>
          </div>
        ) : (
          <Command>
            <CommandInput placeholder={t('searchBankAccount') || 'Search bank account…'} data-testid={`${testid}-search`} />
            <CommandList>
              <CommandEmpty>{t('noMatches') || 'No matches.'}</CommandEmpty>
              <CommandGroup>
                {bankAccounts.map(b => (
                  <CommandItem
                    key={b.account_id}
                    value={b.name}
                    onSelect={() => { onChange?.(b.account_id); setOpen(false); }}
                    data-testid={`${testid}-option-${b.account_id}`}
                  >
                    <Check size={14} className={`mr-2 ${value === b.account_id ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="flex-1">{b.name}</span>
                    {b.iban && <span className="text-[10px] text-zinc-400 font-mono ml-2">{b.iban.slice(-6)}</span>}
                  </CommandItem>
                ))}
                {onCreateBank && (
                  <CommandItem
                    value="__create"
                    onSelect={() => { setOpen(false); onCreateBank(); }}
                    className="border-t border-zinc-100 mt-1 pt-2 text-emerald-700"
                    data-testid={`${testid}-create-inline`}
                  >
                    <Plus size={14} className="mr-2" />
                    {t('createBankAccount') || 'Create bank account'}
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}



// Phase 416 — Compact Finance Overview cards: Opening Balance + 3 invoice
// totals (Customer Sales, Supplier Invoices, Expenses), each with Without
// VAT / VAT / With VAT.
function FinanceOverviewCards({ t, dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  // Phase 419 — Audit dialog: show every row that contributes to Total
  // Expenses so the user can verify the calculation in one click. Uses the
  // existing `?debug=1` mode of /api/company/finance-overview-totals which
  // returns a per-row breakdown sourced ONLY from db.expenses.
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  useEffect(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    axios.get(`${API}/company/finance-overview-totals`, { params, withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, [dateFrom, dateTo]);
  const fmt = (v) => `\u20ac${Number(v || 0).toFixed(2)}`;
  const openBreakdown = async () => {
    setBreakdownOpen(true);
    setBreakdownLoading(true);
    try {
      const params = { debug: 1 };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/company/finance-overview-totals`, { params, withCredentials: true });
      setBreakdown(r.data);
    } catch (e) {
      setBreakdown({ error: e?.response?.data?.detail || 'Failed to load breakdown' });
    } finally {
      setBreakdownLoading(false);
    }
  };
  const card = (titleKey, totals, testidPrefix, accent, footer) => (
    <div className="bg-white border border-zinc-200 p-4" data-testid={`overview-card-${testidPrefix}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">{t(titleKey) || titleKey}</div>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between text-zinc-500">
          <span>{t('withoutVat') || 'Without VAT'}</span>
          <span className="font-mono" data-testid={`${testidPrefix}-without-vat`} style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(totals?.without_vat)}</span>
        </div>
        <div className="flex justify-between text-zinc-500">
          <span>{t('vat') || 'VAT'}</span>
          <span className="font-mono" data-testid={`${testidPrefix}-vat`} style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(totals?.vat)}</span>
        </div>
        <div className={`flex justify-between border-t border-zinc-200 pt-1.5 mt-1 font-semibold ${accent || 'text-zinc-900'}`}>
          <span>{t('totalWithVat') || 'Total With VAT'}</span>
          <span className="font-mono" data-testid={`${testidPrefix}-with-vat`} style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(totals?.with_vat)}</span>
        </div>
        {footer}
      </div>
    </div>
  );
  return (
    <>
      {/* Phase 1026b — Opening Balance and Total Income cards removed from
          the Finance Overview per user request. Underlying data
          (`data.opening_balance`, `data.customer_invoices`) is unchanged
          and still returned by /finance-overview-totals; it is simply
          not surfaced here. VAT Settlement below still uses
          `data.customer_invoices.vat`. The remaining two cards expand
          to a 2-column grid on medium+ screens so there are no gaps. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="finance-overview-cards">
        {card('totalSupplierExpenses', data?.supplier_invoices, 'supplier-expenses', 'text-red-700')}
        {card('totalExpenses', data?.expenses, 'expenses', 'text-red-700',
          <button
            type="button"
            onClick={openBreakdown}
            className="w-full text-[10px] text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline text-left mt-1 pt-1 border-t border-zinc-100"
            data-testid="overview-expenses-breakdown-btn"
          >{t('viewBreakdown') || 'View breakdown →'}</button>
        )}
      </div>

      {/* Phase 529 — Internal Cash Tracking summary card removed from
          the Finance Overview dashboard per user request. Internal
          bookkeeping lives in its dedicated module at
          Finance → Customers → Internal Cash (and Internal Invoices),
          so a duplicated dashboard card created clutter without adding
          value. Backend `data.internal_cash_tracking` is unchanged and
          remains available for any future use. */}

      {/* Phase 568 — VAT Settlement panel.
          Reuses the VAT figures already returned by
          /api/company/finance-overview-totals (no new endpoint, no
          duplicate calc):
            income VAT  = data.customer_invoices.vat
            supplier VAT = data.supplier_invoices.vat
            expenses VAT = data.expenses.vat (Vendor Expenses only — Phase 567)
          Settlement = income − supplier − expenses.
          If positive: VAT TO PAY (owed to authority).
          If negative: VAT CREDIT / REFUND (authority owes us).
          Date range from the global Finance FROM/TO inputs flows through
          automatically because the underlying endpoint already filters by
          date_from / date_to. */}
      {(() => {
        const incomeVat = Number(data?.customer_invoices?.vat || 0);
        const supplierVat = Number(data?.supplier_invoices?.vat || 0);
        const expensesVat = Number(data?.expenses?.vat || 0);
        const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
        const settlement = r2(incomeVat - supplierVat - expensesVat);
        const owesAuthority = settlement >= 0;
        return (
          <div className="bg-white border border-zinc-200 mt-3" data-testid="finance-vat-settlement">
            <div className="px-4 py-2.5 border-b border-zinc-200 flex items-center justify-between">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-600">{t('vatSettlement') || 'VAT Settlement'}</div>
              <div className="text-[10px] text-zinc-400">{t('vatSettlementHint') || 'Income VAT − Supplier VAT − Expenses VAT'}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 divide-x divide-zinc-100">
              <div className="px-4 py-3 flex flex-col">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">{t('totalIncomeVat') || 'Total Income VAT'}</span>
                <span className="font-mono text-lg font-semibold text-emerald-700 mt-1" data-testid="vat-settle-income" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(incomeVat)}</span>
                <span className="text-[10px] text-zinc-400 mt-0.5">{t('fromCustomerInvoices') || 'from customer invoices'}</span>
              </div>
              <div className="px-4 py-3 flex flex-col">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">− {t('supplierVat') || 'Supplier VAT'}</span>
                <span className="font-mono text-lg font-semibold text-zinc-800 mt-1" data-testid="vat-settle-supplier" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(supplierVat)}</span>
                <span className="text-[10px] text-zinc-400 mt-0.5">{t('fromSupplierInvoices') || 'from supplier invoices'}</span>
              </div>
              <div className="px-4 py-3 flex flex-col">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">− {t('expensesVat') || 'Expenses VAT'}</span>
                <span className="font-mono text-lg font-semibold text-zinc-800 mt-1" data-testid="vat-settle-expenses" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(expensesVat)}</span>
                <span className="text-[10px] text-zinc-400 mt-0.5">{t('fromVendorExpenses') || 'from Vendors → Expenses'}</span>
              </div>
              <div className={`px-4 py-3 flex flex-col border-l-4 ${owesAuthority ? 'border-red-500 bg-red-50/40' : 'border-emerald-500 bg-emerald-50/40'}`}>
                <span className={`text-[10px] uppercase tracking-wider font-bold ${owesAuthority ? 'text-red-700' : 'text-emerald-700'}`}>{owesAuthority ? (t('vatToPay') || 'VAT To Pay') : (t('vatCreditRefund') || 'VAT Credit / Refund')}</span>
                <span className={`font-mono text-2xl font-bold mt-1 ${owesAuthority ? 'text-red-700' : 'text-emerald-700'}`} data-testid="vat-settle-result" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(Math.abs(settlement))}
                </span>
                <span className="text-[10px] text-zinc-500 mt-0.5">{owesAuthority ? (t('payableToAuthority') || 'payable to tax authority') : (t('owedByAuthority') || 'owed by tax authority')}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Phase 419 — Total Expenses audit modal */}
      <Dialog open={breakdownOpen} onOpenChange={setBreakdownOpen}>
        <DialogContent className="max-w-4xl rounded-none" data-testid="expenses-breakdown-dialog">
          <DialogHeader>
            <DialogTitle>{t('totalExpenses') || 'Total Expenses'} — {t('breakdown') || 'Breakdown'}</DialogTitle>
          </DialogHeader>
          <div className="text-[11px] text-zinc-500 mb-2">
            {t('breakdownHelp') || 'Every row contributing to Total Expenses. Source: Finance → Vendors → Expenses (db.company_expenses). Building-level expenses, invoices, payments, supplier bills and internal cash are NOT included.'}
          </div>
          {breakdownLoading ? (
            <div className="p-8 text-center text-zinc-500 text-sm">{t('loading') || 'Loading…'}</div>
          ) : breakdown?.error ? (
            <div className="p-8 text-center text-red-600 text-sm">{breakdown.error}</div>
          ) : breakdown ? (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="excel-table text-xs" data-testid="breakdown-table">
                <thead className="sticky top-0 bg-zinc-50">
                  <tr>
                    <th>#</th>
                    <th>{t('expenseDate') || 'Date'}</th>
                    <th>{t('expenseCategory') || 'Category'}</th>
                    <th>{t('source') || 'Source'}</th>
                    <th>{t('description') || 'Description'}</th>
                    <th className="text-right">{t('amount') || 'Amount'}</th>
                  </tr>
                </thead>
                <tbody>
                  {(breakdown.expenses_breakdown || []).map((r, i) => (
                    <tr key={r.expense_id || i}>
                      <td className="text-zinc-400 font-mono">{i + 1}</td>
                      <td className="font-mono">{r.date}</td>
                      <td><span className="px-1.5 py-0.5 border text-[10px] font-mono">{r.category || '—'}</span></td>
                      <td>
                        <span className={`px-1.5 py-0.5 text-[10px] border ${
                          r.source === 'elevator' ? 'border-blue-200 bg-blue-50 text-blue-700' :
                          r.source === 'repair' ? 'border-amber-200 bg-amber-50 text-amber-700' :
                          'border-zinc-200 bg-zinc-50 text-zinc-600'
                        }`}>{r.source}</span>
                      </td>
                      <td className="text-zinc-600 truncate max-w-[260px]" title={r.description}>{r.description || <span className="text-zinc-300">—</span>}</td>
                      <td className="font-mono text-right font-semibold">{fmt(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 bg-zinc-100">
                  <tr className="border-t-2 border-zinc-300 font-bold">
                    <td colSpan={5} className="text-right">{t('total') || 'Total'} ({breakdown.expenses_breakdown_count || 0} {t('rows') || 'rows'})</td>
                    <td className="font-mono text-right text-red-700">{fmt(breakdown.expenses?.with_vat)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Finance() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const axCfg = { withCredentials: true };

  const [summary, setSummary] = useState(null);
  const [income, setIncome] = useState([]);
  // Phase 98 — Unfiltered copies used by Bank tabs so per-bank From/To filters can
  // compute pre-range running balances even when the global filter is set.
  const [allIncome, setAllIncome] = useState([]);
  const [allBankPayments, setAllBankPayments] = useState([]);
  // Phase 527 — Internal Cash bank account uses a unified "Payments" list
  // that combines company_income (Finance → Income) with sales_payments
  // (Sales → Customer Payments) — both tagged as internal cash.
  const [salesPayments, setSalesPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  // Phase 95 — Purchases categories are independent of Finance "Expense Categories"
  const [purchaseCategories, setPurchaseCategories] = useState([]);
  // Phase 96 — Bank Payments (money out of a bank account; distinct from Purchases)
  const [bankPayments, setBankPayments] = useState([]);
  const [suppliersList, setSuppliersList] = useState([]);
  const [clients, setClients] = useState([]);

  // Phase 98 — per-bank and per-cash date filter (independent of global filter)
  const [bankDateRanges, setBankDateRanges] = useState({});
  const getBankRange = (id) => bankDateRanges[id] || { from: '', to: '' };
  const setBankRange = (id, partial) => setBankDateRanges(prev => ({ ...prev, [id]: { ...(prev[id] || { from: '', to: '' }), ...partial } }));
  const [cashDateRange, setCashDateRange] = useState({ from: '', to: '' });
  const inRange = (date, from, to) => (!from || (date || '') >= from) && (!to || (date || '') <= to);
  const [activeTab, setActiveTab] = useState('overview');
  // Phase 113 — legacy guard: any old `purchaseCategories` tab selection now coerces to expenses.
  useEffect(() => { if (activeTab === 'purchaseCategories') setActiveTab('expensesList'); }, [activeTab]);

  // Income filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [incomeTypeFilter, setIncomeTypeFilter] = useState('all'); // Phase 221 — filter by income_type
  const [stmtViewRow, setStmtViewRow] = useState(null); // Phase 223 — Statement row View modal

  // Phase 221 — Income type taxonomy. `client_payment` is the only type
  // that requires a client and may sync to Cleaning / Building modules.
  const INCOME_TYPES = [
    { key: 'client_payment',      labelKey: 'incomeType_client_payment',      badgeKey: 'incomeTypeBadgeClient',     color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    { key: 'bank_refund',         labelKey: 'incomeType_bank_refund',         badgeKey: 'incomeTypeBadgeRefund',     color: 'bg-amber-100 text-amber-800 border-amber-200' },
    { key: 'bank_adjustment',     labelKey: 'incomeType_bank_adjustment',     badgeKey: 'incomeTypeBadgeAdjustment', color: 'bg-purple-100 text-purple-800 border-purple-200' },
    { key: 'interest',            labelKey: 'incomeType_interest',            badgeKey: 'incomeTypeBadgeInterest',   color: 'bg-blue-100 text-blue-800 border-blue-200' },
    { key: 'transfer_in',         labelKey: 'incomeType_transfer_in',         badgeKey: 'incomeTypeBadgeTransfer',   color: 'bg-sky-100 text-sky-800 border-sky-200' },
    { key: 'owner_contribution',  labelKey: 'incomeType_owner_contribution',  badgeKey: 'incomeTypeBadgeOwnerContrib', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
    { key: 'other',               labelKey: 'incomeType_other',               badgeKey: 'incomeTypeBadgeOther',      color: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  ];
  const incomeTypeMeta = (key) => INCOME_TYPES.find(x => x.key === (key || 'other')) || INCOME_TYPES[INCOME_TYPES.length - 1];

  // Dialogs
  const [bankDialog, setBankDialog] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [bankForm, setBankForm] = useState({ name: '', bank_name: '', account_number: '', iban: '', opening_balance: '', opening_balance_date: new Date().toISOString().split('T')[0], notes: '', include_in_summary: true });

  const [incomeDialog, setIncomeDialog] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [incomeForm, setIncomeForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', description: '', client_id: '', source: 'cash', bank_account_id: '', notes: '', vat_included: true, sync_to_cleaning: true, income_type: 'client_payment', affects_treasury: true });

  // Phase 483 — Automatic Customer Payment Allocation.
  // After saving an income tied to a client, if open invoices exist for
  // that client we prompt the user to allocate the payment.
  const [allocDialog, setAllocDialog] = useState(false);
  const [allocIncome, setAllocIncome] = useState(null);       // {income_id, amount, client_name, ...}
  const [allocOpenInvoices, setAllocOpenInvoices] = useState([]);
  const [allocMode, setAllocMode] = useState('multi');         // 'single' | 'multi' | 'auto'
  const [allocAmounts, setAllocAmounts] = useState({});        // { invoice_id: amount }
  const [allocSingle, setAllocSingle] = useState('');          // invoice_id for single mode
  const [allocSubmitting, setAllocSubmitting] = useState(false);

  const [expenseDialog, setExpenseDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  // Phase 95 — standalone Purchases form. No description, deduction_type, source, or bank_account_id.
  const [expenseForm, setExpenseForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', net_amount: '', vat_amount: '', vat_rate: 19, category_id: '', notes: '', description: '', vendor_id: '', vendor_name: '', attachment_name: '', attachment_data: '', attachment_url: '' });
  // Phase 435 — Expense Vendors catalog (independent of Suppliers).
  const [expenseVendors, setExpenseVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);

  // Finance → Vendors → Expenses — panel-local filters. The Expenses list
  // does NOT auto-refetch when the user changes these filters; the table
  // only refreshes when the user clicks Search or presses Enter inside a
  // filter/search field. The Clear button resets every filter and reloads
  // the default (unfiltered) Expenses list.
  const EMPTY_EXP_FILTERS = { from: '', to: '', category_id: '', vendor_id: '', q: '' };
  const [expDraft, setExpDraft] = useState(EMPTY_EXP_FILTERS);
  const [expApplied, setExpApplied] = useState(EMPTY_EXP_FILTERS);
  const [panelExpenses, setPanelExpenses] = useState([]);
  const [panelExpLoading, setPanelExpLoading] = useState(false);
  const fetchPanelExpenses = useCallback(async (filters) => {
    const params = new URLSearchParams();
    if (filters.from) params.append('date_from', filters.from);
    if (filters.to) params.append('date_to', filters.to);
    if (filters.category_id) params.append('category_id', filters.category_id);
    if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
    if (filters.q && filters.q.trim()) params.append('q', filters.q.trim());
    const qs = params.toString() ? `?${params}` : '';
    setPanelExpLoading(true);
    try {
      const res = await axios.get(`${API}/company/expenses${qs}`, axCfg);
      setPanelExpenses(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load expenses');
    } finally {
      setPanelExpLoading(false);
    }
  }, []);
  useEffect(() => { fetchPanelExpenses(expApplied); }, [expApplied, fetchPanelExpenses]);
  const applyExpFilters = () => {
    if (panelExpLoading) return;
    setExpApplied({ ...expDraft });
  };
  const clearExpFilters = () => {
    setExpDraft(EMPTY_EXP_FILTERS);
    setExpApplied(EMPTY_EXP_FILTERS);
  };
  const onExpFilterKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); applyExpFilters(); } };
  // Phase 437 — Expense Vendors page state (managed independently from
  // Suppliers; surfaced as Finance → Purchases → Expenses → Vendors).
  const [vendorDialog, setVendorDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [vendorPageSearch, setVendorPageSearch] = useState('');
  const [vendorDeleteTarget, setVendorDeleteTarget] = useState(null);

  const [catDialog, setCatDialog] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  // Phase 95 — catScope tracks which category list the dialog is editing: 'finance' or 'purchase'
  const [catScope, setCatScope] = useState('finance');
  const [catForm, setCatForm] = useState({ name_en: '', name_el: '', bucket: '' });

  // Phase 96 — Bank Payment dialog (Phase 101: simplified to amount-only)
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentBankId, setPaymentBankId] = useState('');
  const [paymentForm, setPaymentForm] = useState({ date: new Date().toISOString().split('T')[0], amount: '', category_id: '', supplier_id: '', paid_from: '', notes: '', bill_id: '', vendor_account_id: '' });
  // Phase 462 — Linked-bill UI is off by default. Most bank payments are
  // simple cash withdrawals / fuel / bank charges and have no associated
  // bill. The user opts in via a small "Link to a bill" toggle.
  const [showBillLink, setShowBillLink] = useState(false);
  const [payableBills, setPayableBills] = useState([]); // Phase 224 — Bills picker source for the Pay form
  const [payableVendors, setPayableVendors] = useState([]); // Phase 230 — Vendor-account picker (replaces individual bills)
  // Phase 134 — Internal Transfer state. Independent of payments (no VAT/deductions math).
  const [transfers, setTransfers] = useState([]);
  const [transferDialog, setTransferDialog] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [transferForm, setTransferForm] = useState({ date: new Date().toISOString().split('T')[0], from_account_id: '', to_account_id: '', amount: '', reference: '', notes: '' });

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Inner tab state per bank (keyed by account_id) — independent from the sidebar section tabs
  const [bankTabs, setBankTabs] = useState({});
  const setBankTab = (bankId, tab) => setBankTabs(prev => ({ ...prev, [bankId]: tab }));
  // Phase 218 — Per-bank Statement type filter (income / payments /
  // transfers / all). Defaults to "all" so the statement still shows
  // everything chronologically by default.
  const [bankStmtFilters, setBankStmtFilters] = useState({});
  const setBankStmtFilter = (bankId, val) => setBankStmtFilters(prev => ({ ...prev, [bankId]: val }));
  // Phase 96 — valid tabs: income / payments / statement. Legacy 'expenses' coerced to 'income'.
  const getBankTab = (bankId) => {
    const v = bankTabs[bankId] || 'income';
    return v === 'expenses' ? 'income' : v;
  };

  // Cash block inner tab
  const [cashTab, setCashTabRaw] = useState('income');
  // Phase 92 — expenses tab removed; coerce legacy values to 'income'.
  const setCashTab = (v) => setCashTabRaw(v === 'expenses' ? 'income' : v);

  // Phase 83 — Deductions drill-down modal
  const [deductionsModal, setDeductionsModal] = useState(false);
  // Phase 124 — Deductions ledger (combined transaction view across all 4 buckets).
  const [deductionsLedger, setDeductionsLedger] = useState(null);
  const [deductionsView, setDeductionsView] = useState('summary'); // summary | transactions
  useEffect(() => {
    if (!deductionsModal) return;
    // Fetch ledger on modal open; refresh whenever modal reopens so payments reflect immediately.
    axios.get(`${API}/company/deductions-ledger`, axCfg)
      .then(r => setDeductionsLedger(r.data))
      .catch(() => setDeductionsLedger({ rows: [], count: 0, totals: { per_bucket: {}, combined: 0 } }));
  }, [deductionsModal]); // eslint-disable-line react-hooks/exhaustive-deps
  // Phase 85 — Expense Category detail view (master-detail with independent From/To filters)
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [catFilterFrom, setCatFilterFrom] = useState('');
  const [catFilterTo, setCatFilterTo] = useState('');
  const [catExpenses, setCatExpenses] = useState([]);
  const [catExpensesLoading, setCatExpensesLoading] = useState(false);

  // Phase 86 — Suppliers (Purchases) master-detail
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [supplierTab, setSupplierTab] = useState('invoices'); // invoices | payments | statement
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [supplierPayments, setSupplierPayments] = useState([]);
  // Dialogs & forms for supplier child records
  const [supplierDialog, setSupplierDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({ name: '', notes: '' });
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ date: new Date().toISOString().split('T')[0], invoice_number: '', net: '', vat: '', total: '', notes: '' });
  const [payDialog, setPayDialog] = useState(false);
  const [editingPay, setEditingPay] = useState(null);
  const [payForm, setPayForm] = useState({ date: new Date().toISOString().split('T')[0], payment_number: '', amount: '', method: 'bank', bank_account_id: '', notes: '', allocations: [] });
  // Phase 452 — Open (unpaid + partial) supplier invoices loaded when the
  // Add Payment dialog opens. Lets the user split the payment across one
  // or more invoices, oldest first.
  const [payOpenInvoices, setPayOpenInvoices] = useState([]);
  // Phase 88 — independent per-tab From/To filters for the supplier detail panes
  const [invFilter, setInvFilter] = useState({ from: '', to: '', type: 'all', status: 'all' });
  const [payFilter, setPayFilter] = useState({ from: '', to: '' });
  const [stmtFilter, setStmtFilter] = useState({ from: '', to: '' });

  // Master-detail: which single account is selected inside the Bank Accounts view.
  // Values: bank account_id (string) or special token 'cash'.
  const [selectedBankId, setSelectedBankId] = useState(null);
  // Phase 466 — Debug Inspector. When enabled, every Bank Statement row
  // shows its raw record IDs (income_id / bank_payment_id / synced_payment_id
  // / synced_building_payment_id), the source module, audit timestamps,
  // and the bank_account_id it's stored under. Toggled inline per user
  // request to diagnose "missing transaction" reports without DB access.
  const [debugMode, setDebugMode] = useState(() => {
    try { return localStorage.getItem('finance_debug_mode') === '1'; } catch { return false; }
  });
  const toggleDebugMode = () => {
    setDebugMode(v => {
      const next = !v;
      try { localStorage.setItem('finance_debug_mode', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // Column visibility — default simplified view
  const [visibleCols, setVisibleCols] = useState(() => {
    try { const s = localStorage.getItem('finance_cols'); if (s) return JSON.parse(s); } catch {}
    return { vat: false, socialIns: false, alloc: false, reserve: false };
  });
  const toggleCol = (key) => {
    setVisibleCols(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('finance_cols', JSON.stringify(next));
      return next;
    });
  };
  const [colMenuOpen, setColMenuOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (clientFilter && clientFilter !== 'all') params.append('client_id', clientFilter);
    const qs = params.toString() ? `?${params}` : '';
    try {
      const [sumR, incR, incAllR, expR, baR, catR, pcatR, bpR, bpAllR, supR, clR, xferR, billsR, pvR, spR] = await Promise.all([
        axios.get(`${API}/company/financial-summary${qs}`, axCfg),
        axios.get(`${API}/company/income${qs}`, axCfg),
        axios.get(`${API}/company/income`, axCfg),
        axios.get(`${API}/company/expenses${qs}`, axCfg),
        axios.get(`${API}/bank-accounts`, axCfg),
        axios.get(`${API}/company/expense-categories`, axCfg),
        axios.get(`${API}/company/purchase-categories`, axCfg),
        axios.get(`${API}/bank-payments${qs}`, axCfg),
        axios.get(`${API}/bank-payments`, axCfg),
        axios.get(`${API}/suppliers`, axCfg),
        axios.get(`${API}/cleaning/clients`, axCfg),
        axios.get(`${API}/internal-transfers`, axCfg),
        axios.get(`${API}/bills/payable`, axCfg),
        axios.get(`${API}/bills/payable-vendors`, axCfg),
        // Phase 527 — Sales payments are needed for the Internal Cash
        // wallet's unified Payments list (customer Internal Cash payments
        // live in sales_payments, not company_income).
        axios.get(`${API}/sales/payments`, axCfg),
      ]);
      setSummary(sumR.data);
      setIncome(incR.data);
      setAllIncome(incAllR.data || []);
      setExpenses(expR.data);
      setBankAccounts(baR.data);
      setCategories(catR.data);
      setPurchaseCategories(pcatR.data);
      setBankPayments(bpR.data || []);
      setAllBankPayments(bpAllR.data || []);
      setSuppliersList(Array.isArray(supR.data) ? supR.data : []);
      setClients(Array.isArray(clR.data) ? clR.data.filter(c => !c.is_deleted) : []);
      setTransfers(Array.isArray(xferR.data) ? xferR.data : []);
      setPayableBills(Array.isArray(billsR.data) ? billsR.data : []);
      setPayableVendors(Array.isArray(pvR.data) ? pvR.data : []);
      setSalesPayments(Array.isArray(spR.data) ? spR.data : []);
    } catch { toast.error('Failed to load financial data'); }
  }, [dateFrom, dateTo, clientFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Phase 100 — honor ?client_id=... when arriving from Clients module (pre-selects client filter + switches to Income view).
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const cid = p.get('client_id');
      if (cid) {
        setClientFilter(cid);
        setActiveTab('banks');
      }
    } catch { /* ignore URL parse issues */ }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 85 — Fetch a selected Finance Expense Category's history.
  // Phase 104 — History pulls from bank_payments (since Purchases uses purchase_categories).
  // Phase 110 — Switched to /ledger which also includes income allocations when the category
  //             has a linked bucket (VAT/12.5%/Reserve/Social).
  const [catLedger, setCatLedger] = useState({ bucket: null, entries: [], allocated: 0, paid: 0, balance: 0 });
  useEffect(() => {
    if (!selectedCategoryId) { setCatExpenses([]); setCatLedger({ bucket: null, entries: [], allocated: 0, paid: 0, balance: 0 }); return; }
    setCatExpensesLoading(true);
    axios.get(`${API}/company/expense-categories/${selectedCategoryId}/ledger`, axCfg)
      .then(r => {
        const data = r.data || { bucket: null, entries: [] };
        setCatLedger(data);
        // Keep catExpenses (payments-only, for date-range filter compat)
        const payments = (data.entries || []).filter(x => x.type === 'payment').map(x => ({
          expense_id: x.id.replace(/^pay-/, ''),
          date: x.date,
          amount: Math.abs(x.amount || 0),
          source: 'bank',
          bank_account_name: x.ref,
          notes: x.notes,
          description: '',
        }));
        // Apply date-range filter
        const from = catFilterFrom, to = catFilterTo;
        setCatExpenses(payments.filter(p => (!from || (p.date || '') >= from) && (!to || (p.date || '') <= to)));
      })
      .catch(() => { setCatExpenses([]); setCatLedger({ bucket: null, entries: [], allocated: 0, paid: 0, balance: 0 }); })
      .finally(() => setCatExpensesLoading(false));
  }, [selectedCategoryId, catFilterFrom, catFilterTo, bankPayments, income]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 86 — Suppliers
  const fetchSuppliers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/suppliers`, axCfg);
      setSuppliers(Array.isArray(r.data) ? r.data : []);
    } catch { /* silent */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  // Fetch the selected supplier's detail (invoices + payments)
  const fetchSupplierDetail = useCallback(async (sid) => {
    if (!sid) { setSupplierInvoices([]); setSupplierPayments([]); return; }
    try {
      const [invR, payR] = await Promise.all([
        axios.get(`${API}/suppliers/${sid}/invoices`, axCfg),
        axios.get(`${API}/suppliers/${sid}/payments`, axCfg),
      ]);
      setSupplierInvoices(invR.data || []); setSupplierPayments(payR.data || []);
    } catch { /* silent */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSupplierDetail(selectedSupplierId); }, [selectedSupplierId, fetchSupplierDetail]);

  // Supplier CRUD helpers (Phase 87: always statement-based; no type picker)
  const openSupplierAdd = () => { setEditingSupplier(null); setSupplierForm({ name: '', notes: '' }); setSupplierDialog(true); };
  const openSupplierEdit = (s) => { setEditingSupplier(s); setSupplierForm({ name: s.name || '', notes: s.notes || '' }); setSupplierDialog(true); };
  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) await axios.put(`${API}/suppliers/${editingSupplier.supplier_id}`, supplierForm, axCfg);
      else await axios.post(`${API}/suppliers`, supplierForm, axCfg);
      toast.success(t('savedSuccess'));
      setSupplierDialog(false); setEditingSupplier(null);
      fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  // Phase 108 — use the shared AlertDialog pattern instead of native window.confirm
  // (which is often suppressed inside browser iframes / embedded previews).
  const deleteSupplier = (sid) => confirmDelete('supplier', sid);
  const deleteInvoice = (iid) => confirmDelete('supplier_invoice', iid, null, selectedSupplierId);
  const deletePay = (p) => confirmDelete('supplier_payment', p.payment_id, null, selectedSupplierId, {
    source: p.source,
    bank_account_id: p.bank_account_id,
  });

  // Invoice / Payment CRUD (Purchase helpers removed in Phase 87)
  const openInvoiceAdd = () => { setEditingInvoice(null); setInvoiceForm({ date: new Date().toISOString().split('T')[0], invoice_number: '', net: '', vat: '', total: '', notes: '', invoice_type: 'credit' }); setInvoiceDialog(true); };
  const openInvoiceEdit = (i) => { setEditingInvoice(i); setInvoiceForm({ date: i.date, invoice_number: i.invoice_number || '', net: String(i.net ?? ''), vat: String(i.vat ?? ''), total: String(i.total ?? ''), notes: i.notes || '', invoice_type: i.invoice_type || 'credit' }); setInvoiceDialog(true); };
  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...invoiceForm,
      net: parseFloat(invoiceForm.net) || 0,
      vat: parseFloat(invoiceForm.vat) || 0,
      total: invoiceForm.total === '' ? null : parseFloat(invoiceForm.total),
      // Phase 944 — Explicit type ("credit" or "cash").
      invoice_type: invoiceForm.invoice_type === 'cash' ? 'cash' : 'credit',
    };
    try {
      if (editingInvoice) await axios.put(`${API}/suppliers/${selectedSupplierId}/invoices/${editingInvoice.invoice_id}`, payload, axCfg);
      else await axios.post(`${API}/suppliers/${selectedSupplierId}/invoices`, payload, axCfg);
      toast.success(t('savedSuccess'));
      setInvoiceDialog(false); setEditingInvoice(null);
      fetchSupplierDetail(selectedSupplierId); fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const deleteInvoiceLegacy_removed = null; // Phase 108 — superseded by confirmDelete above

  const openPayAdd = async () => {
    setEditingPay(null);
    setPayForm({ date: new Date().toISOString().split('T')[0], payment_number: '', amount: '', method: 'bank', bank_account_id: bankAccounts[0]?.account_id || '', notes: '', allocations: [] });
    setPayDialog(true);
    // Phase 452 — Fetch open supplier invoices to populate the allocation table.
    if (selectedSupplierId) {
      try {
        const r = await axios.get(`${API}/suppliers/${selectedSupplierId}/invoices?status=open`);
        setPayOpenInvoices(r.data || []);
      } catch { setPayOpenInvoices([]); }
    } else { setPayOpenInvoices([]); }
  };
  const openPayEdit = (p) => { setEditingPay(p); setPayForm({ date: p.date, payment_number: p.payment_number || '', amount: String(p.amount ?? ''), method: p.method || 'bank', bank_account_id: p.bank_account_id || '', notes: p.notes || '', allocations: p.allocations || [] }); setPayDialog(true); setPayOpenInvoices([]); };
  const setSupplierAllocation = (invoiceId, value) => {
    const amt = Math.max(0, parseFloat(value) || 0);
    setPayForm(f => {
      const others = (f.allocations || []).filter(a => a.invoice_id !== invoiceId);
      if (amt > 0) others.push({ invoice_id: invoiceId, amount: amt });
      return { ...f, allocations: others };
    });
  };
  const handlePaySubmit = async (e) => {
    e.preventDefault();
    const payload = { ...payForm, amount: parseFloat(payForm.amount) || 0 };
    if (payload.method !== 'bank') payload.bank_account_id = null;
    try {
      if (editingPay) await axios.put(`${API}/suppliers/${selectedSupplierId}/payments/${editingPay.payment_id}`, payload, axCfg);
      else await axios.post(`${API}/suppliers/${selectedSupplierId}/payments`, payload, axCfg);
      toast.success(t('savedSuccess'));
      setPayDialog(false); setEditingPay(null);
      fetchSupplierDetail(selectedSupplierId); fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const deletePayLegacy_removed = null; // Phase 108 — superseded by confirmDelete above

  // Auto-select the first bank account when data loads (so master-detail always has a default).
  // Never auto-select Cash — user must click it explicitly.
  useEffect(() => {
    if (selectedBankId === null && bankAccounts.length > 0) {
      setSelectedBankId(bankAccounts[0].account_id);
    }
  }, [bankAccounts, selectedBankId]);

  const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
  const getCatName = (cat_id, cat_name_en, cat_name_el) => {
    // Language-aware with bilateral fallback (Phase 80): if preferred lang is empty, fall back to the other.
    if (cat_name_en || cat_name_el) return language === 'el' ? (cat_name_el || cat_name_en) : (cat_name_en || cat_name_el);
    const c = categories.find(x => x.category_id === cat_id);
    if (!c) return '';
    return language === 'el' ? (c.name_el || c.name_en || '') : (c.name_en || c.name_el || '');
  };
  const getBankName = (id) => { const b = bankAccounts.find(x => x.account_id === id); return b ? b.name : ''; };

  // Phase 135/138/216 — Global date sort for transaction tables. Default
  // is "Newest first" (DESC). Within the same date, the tiebreaker is
  // `created_at` in the SAME direction as the primary sort, so the most
  // recently created entry always wins on DESC and the oldest on ASC.
  // This matches the user spec: ORDER BY date DESC, created_at DESC.
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' (newest first) | 'asc'

  const byDateDesc = (a, b) => {
    const cmp = (b.date || '').localeCompare(a.date || '');
    if (cmp !== 0) return cmp;
    // Phase 216 — created_at DESC tiebreaker (was ASC).
    const cc = (b.created_at || '').localeCompare(a.created_at || '');
    if (cc !== 0) return cc;
    const aid = a.id || a.income_id || a.payment_id || a.expense_id || a.invoice_id || a.transfer_id || '';
    const bid = b.id || b.income_id || b.payment_id || b.expense_id || b.invoice_id || b.transfer_id || '';
    return bid.localeCompare(aid);
  };
  const byDateAsc = (a, b) => -byDateDesc(a, b);
  // Phase 216 — `sortDesc` now respects the global toggle. Old call sites
  // keep the same name but flip when the user picks "Oldest first".
  const sortDesc = (arr) => [...arr].sort(sortOrder === 'asc' ? byDateAsc : byDateDesc);

  // Phase 136 — Source-of-truth per-bank current balance.
  // Must ALWAYS equal the last running balance of that bank's statement.
  // Formula: opening + all-time bank income − all-time bank payments + transfers in − transfers out.
  // Uses the same raw collections the statement iterates, so the two numbers can never diverge.
  // Phase 525 — Internal Cash virtual wallet (auto-routed). Used by the
  // bank balance helper to invert the affects_treasury filter when the
  // user opens this special account.
  const INTERNAL_CASH_ACCOUNT_ID = 'bank_internal_cash';

  const computeBankBalance = (accountId) => {
    const acc = bankAccounts.find(x => x.account_id === accountId);
    const opening = acc?.opening_balance || 0;
    const isInternalAccount = accountId === INTERNAL_CASH_ACCOUNT_ID;
    // Phase 523/525 — Income flagged as Internal Cash (`affects_treasury === false`)
    // only contributes to the Internal Cash wallet; every other account
    // explicitly excludes those rows from its running balance.
    const incomeSum = allIncome
      .filter(e => e.bank_account_id === accountId
        && (isInternalAccount
          ? e.affects_treasury === false
          : (e.source !== 'cash' && e.affects_treasury !== false)))
      .reduce((s, e) => s + (e.amount || 0), 0);
    // Phase 527 — Internal Cash wallet ALSO holds customer payments
    // tagged `payment_account === 'internal_cash_tracking'`. Add them
    // to the balance so the wallet shows the true total of all
    // internal-cash money received.
    const salesPaymentsSum = isInternalAccount
      ? salesPayments
          .filter(p => p.payment_account === 'internal_cash_tracking' && !p.is_deleted)
          .reduce((s, p) => s + (p.amount || 0), 0)
      : 0;
    const paymentsSum = allBankPayments
      .filter(p => p.bank_account_id === accountId)
      .reduce((s, p) => s + (p.amount || 0), 0);
    const transfersIn = transfers
      .filter(x => x.to_account_id === accountId)
      .reduce((s, x) => s + (x.amount || 0), 0);
    const transfersOut = transfers
      .filter(x => x.from_account_id === accountId)
      .reduce((s, x) => s + (x.amount || 0), 0);
    return Math.round((opening + incomeSum + salesPaymentsSum - paymentsSum + transfersIn - transfersOut) * 100) / 100;
  };
  const bucketLabel = (dt) => ({ vat: t('bucketVat'), allocation_12_5: t('bucketAllocation'), reserve_10: t('bucketReserve'), social_insurance_10: t('bucketSocialInsurance'), net: t('bucketNet') }[dt] || t('bucketNet'));
  const bucketColor = (dt) => ({ vat: 'border-amber-200 bg-amber-50 text-amber-700', allocation_12_5: 'border-violet-200 bg-violet-50 text-violet-700', reserve_10: 'border-orange-200 bg-orange-50 text-orange-700', social_insurance_10: 'border-teal-200 bg-teal-50 text-teal-700', net: 'border-blue-200 bg-blue-50 text-blue-700' }[dt] || 'border-zinc-200 bg-zinc-50 text-zinc-600');

  const incomePreview = useMemo(() => calcVat(incomeForm.amount, incomeForm.vat_included), [incomeForm.amount, incomeForm.vat_included]);

  const bankIncome = useMemo(() => income.filter(e => e.source !== 'cash'), [income]);
  // Phase 525 — Cash bucket excludes incomes auto-routed to the Internal
  // Cash virtual wallet so the regular Cash totals only show official cash.
  const cashIncome = useMemo(() => income.filter(e => e.source === 'cash' && e.affects_treasury !== false), [income]);
  // Phase 98 — unfiltered cash income used by the Cash block so its own date range works correctly.
  const allCashIncome = useMemo(() => allIncome.filter(e => e.source === 'cash' && e.affects_treasury !== false), [allIncome]);

  // Bank CRUD
  const openBankAdd = () => { setEditingBank(null); setBankForm({ name: '', bank_name: '', account_number: '', iban: '', opening_balance: '', opening_balance_date: new Date().toISOString().split('T')[0], notes: '', include_in_summary: true }); setBankDialog(true); };
  const openBankEdit = (b) => { setEditingBank(b); setBankForm({ name: b.name, bank_name: b.bank_name || '', account_number: b.account_number || '', iban: b.iban || '', opening_balance: String(b.opening_balance || 0), opening_balance_date: b.opening_balance_date || new Date().toISOString().split('T')[0], notes: b.notes || '', include_in_summary: b.include_in_summary !== false }); setBankDialog(true); };
  const handleBankSubmit = async (e) => {
    e.preventDefault();
    if (!bankForm.opening_balance_date) { toast.error(t('openingBalanceDateRequired') || 'Opening balance date is required'); return; }
    const payload = { ...bankForm, opening_balance: parseFloat(bankForm.opening_balance) || 0 };
    try {
      if (editingBank) await axios.put(`${API}/bank-accounts/${editingBank.account_id}`, payload, axCfg);
      else await axios.post(`${API}/bank-accounts`, payload, axCfg);
      toast.success(t('savedSuccess')); setBankDialog(false); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Income CRUD
  const openIncomeAdd = () => {
    setEditingIncome(null);
    setIncomeForm({ date: new Date().toISOString().split('T')[0], amount: '', description: '', client_id: '', source: 'cash', bank_account_id: '', notes: '', vat_included: true, sync_to_cleaning: true, income_type: 'client_payment', affects_treasury: true });
    setIncomeDialog(true);
  };
  // Phase 465 — Auto-fill the bank account when the user selects a client
  // we've recorded a payment for before (per-user, per-browser memory).
  const onIncomeClientChange = (newClientId) => {
    setIncomeForm(f => {
      const next = { ...f, client_id: newClientId };
      if (newClientId && f.source === 'bank' && !f.bank_account_id) {
        try {
          const cur = JSON.parse(localStorage.getItem('lastBankByClient') || '{}');
          if (cur[newClientId]) next.bank_account_id = cur[newClientId];
        } catch (_) { /* ignore */ }
      }
      return next;
    });
  };
  const openIncomeEdit = (e) => { setEditingIncome(e); setIncomeForm({ date: e.date, amount: String(e.amount), description: e.description || '', client_id: e.client_id || '', source: e.source || 'cash', bank_account_id: e.bank_account_id || '', notes: e.notes || '', vat_included: e.vat_included !== false, sync_to_cleaning: e.sync_to_cleaning !== false, income_type: e.income_type || (e.client_id ? 'client_payment' : 'other'), affects_treasury: e.affects_treasury !== false }); setIncomeDialog(true); };
  const handleIncomeSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...incomeForm, amount: parseFloat(incomeForm.amount) || 0 };
    // Phase 221 — Client only required for `client_payment`.
    if (payload.income_type === 'client_payment' && !payload.client_id) { toast.error(t('clientRequired')); return; }
    if (payload.income_type !== 'client_payment') {
      payload.client_id = '';
      payload.sync_to_cleaning = false;
    }
    if (payload.source !== 'bank') delete payload.bank_account_id;
    // Phase 530 — Bank-source income MUST have a bank account selected.
    // Without this guard the row silently lands as Cash everywhere
    // (Finance, Cleaning Management, bank statements) even though the
    // user picked "bank" as the source.
    if (payload.source === 'bank' && !payload.bank_account_id) {
      toast.error(t('bankAccountRequired') || 'Please select a bank account when the source is "bank".');
      return;
    }
    // Phase 465 — Soft duplicate-detection. Before saving NEW incomes,
    // query the backend for similar entries (same amount + client + date
    // ±1 day) so the user can confirm or cancel. Editing existing incomes
    // skips this so the form doesn't flag itself.
    if (!editingIncome) {
      try {
        const r = await axios.post(`${API}/company/income/check-duplicate`, {
          amount: payload.amount, date: payload.date, client_id: payload.client_id || null,
        }, axCfg);
        const dupes = r.data?.duplicates || [];
        if (dupes.length > 0 && !window.__lastDupConfirmed) {
          const lines = dupes.map(d => `• ${d.date} · €${(d.amount || 0).toFixed(2)} · ${d.bank_account_name || (d.source === 'cash' ? 'Cash' : 'Bank')}${d.description ? ` · ${d.description}` : ''}`).join('\n');
          const ok = window.confirm(`${t('possibleDuplicatePayment') || 'Possible duplicate payment detected'}:\n\n${lines}\n\n${t('saveAnyway') || 'Save anyway?'}`);
          if (!ok) return;
        }
      } catch (_) { /* duplicate check is best-effort; never blocks save */ }
    }
    try {
      let savedIncomeId = editingIncome?.income_id || null;
      if (editingIncome) await axios.put(`${API}/company/income/${editingIncome.income_id}`, payload, axCfg);
      else {
        const r = await axios.post(`${API}/company/income`, payload, axCfg);
        savedIncomeId = r.data?.income_id || null;
      }
      // Phase 465 — Tell the user EXACTLY where the money landed. The
      // most common cause of "missing" payments is selecting the wrong
      // bank account on save.
      const bk = payload.source === 'bank'
        ? bankAccounts.find(b => b.account_id === payload.bank_account_id)
        : null;
      const where = bk ? (bk.name || bk.bank_name) : (payload.source === 'cash' ? (t('cash') || 'Cash') : (t('bank') || 'Bank'));
      toast.success(`${t('savedSuccess')} — ${t('savedTo') || 'Saved to'}: ${where}`);
      // Remember the last bank account used for this client so the form
      // can default to it next time (UX fix to prevent wrong-bank entries).
      if (payload.client_id && payload.bank_account_id) {
        try {
          const key = 'lastBankByClient';
          const cur = JSON.parse(localStorage.getItem(key) || '{}');
          cur[payload.client_id] = payload.bank_account_id;
          localStorage.setItem(key, JSON.stringify(cur));
        } catch (_) { /* localStorage best-effort */ }
      }
      setIncomeDialog(false);
      // Phase 1026c — Bank Accounts is now a pure reference log.
      // The Phase 483 auto-allocate prompt (which asked the user to
      // allocate the new client-payment to open invoices) is disabled.
      // Existing allocations in the DB remain untouched; new rows are
      // simply never linked to invoices.
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Phase 483 — Open the allocation dialog for an existing income row
  // (used from the link icon on each income line). Re-fetches open
  // invoices so the user always sees the latest invoice state.
  const openAllocateForIncome = async (income) => {
    if (!income?.client_id) {
      toast.error(t('clientRequired') || 'Customer is required');
      return;
    }
    try {
      const r = await axios.get(`${API}/sales/invoices/open`, { ...axCfg, params: { client_id: income.client_id } });
      const openInvs = Array.isArray(r.data) ? r.data : [];
      if (openInvs.length === 0) {
        toast.info(t('noOpenInvoices') || 'No open invoices for this customer');
        return;
      }
      const cust = clients.find(c => c.client_id === income.client_id);
      // Pre-fill the multi inputs with the previously stored allocations.
      const prior = {};
      for (const a of (income.invoice_allocations || [])) {
        if (a.invoice_id) prior[a.invoice_id] = String(a.amount || '');
      }
      setAllocIncome({
        income_id: income.income_id,
        amount: income.amount,
        client_id: income.client_id,
        client_name: cust?.name || income.client_name || '',
        date: income.date,
      });
      setAllocOpenInvoices(openInvs);
      setAllocMode('multi');
      setAllocAmounts(prior);
      setAllocSingle(openInvs[0]?.invoice_id || '');
      setAllocDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error loading open invoices');
    }
  };

  // Phase 483 — Helpers for the Allocation Dialog.
  const _round2_local = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;
  const allocAllocatedTotal = useMemo(() => {
    if (allocMode === 'single') {
      if (!allocSingle) return 0;
      const inv = allocOpenInvoices.find(i => i.invoice_id === allocSingle);
      if (!inv) return 0;
      return Math.min(inv.remaining, allocIncome?.amount || 0);
    }
    if (allocMode === 'multi') {
      return Object.values(allocAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    }
    // auto FIFO preview
    let remaining = allocIncome?.amount || 0;
    let total = 0;
    for (const inv of allocOpenInvoices) {
      if (remaining <= 0.01) break;
      const take = Math.min(inv.remaining, remaining);
      total += take;
      remaining -= take;
    }
    return total;
  }, [allocMode, allocSingle, allocAmounts, allocOpenInvoices, allocIncome]);

  const allocFifoPreview = useMemo(() => {
    if (allocMode !== 'auto') return [];
    let remaining = allocIncome?.amount || 0;
    const out = [];
    for (const inv of allocOpenInvoices) {
      if (remaining <= 0.01) break;
      const take = Math.min(inv.remaining, remaining);
      if (take <= 0.01) continue;
      out.push({ ...inv, allocate: _round2_local(take) });
      remaining -= take;
    }
    return out;
  }, [allocMode, allocOpenInvoices, allocIncome]);

  function _round2_unused() { return 0; } // Phase 483 — placeholder

  const handleAllocationSubmit = async () => {
    if (!allocIncome) return;
    const payAmt = _round2_local(allocIncome.amount || 0);
    let body = { mode: allocMode };
    if (allocMode === 'auto') {
      body.auto_allocate = true;
    } else if (allocMode === 'single') {
      if (!allocSingle) { toast.error(t('selectInvoice') || 'Select an invoice'); return; }
      const inv = allocOpenInvoices.find(i => i.invoice_id === allocSingle);
      const amount = Math.min(inv.remaining, payAmt);
      body.allocations = [{ invoice_id: allocSingle, amount: round2(amount) }];
    } else {
      // multi
      const allocs = Object.entries(allocAmounts)
        .map(([invId, v]) => ({ invoice_id: invId, amount: round2(parseFloat(v) || 0) }))
        .filter(a => a.amount > 0);
      if (allocs.length === 0) { toast.error(t('enterAllocationAmounts') || 'Enter at least one allocation amount'); return; }
      const total = allocs.reduce((s, a) => s + a.amount, 0);
      if (total > payAmt + 0.01) {
        toast.error(`${t('allocationExceedsPayment') || 'Allocation exceeds payment amount'} (€${total.toFixed(2)} > €${payAmt.toFixed(2)})`);
        return;
      }
      body.allocations = allocs;
    }
    try {
      setAllocSubmitting(true);
      await axios.post(`${API}/company/income/${allocIncome.income_id}/allocate`, body, axCfg);
      toast.success(t('allocationApplied') || 'Allocation applied');
      setAllocDialog(false);
      setAllocIncome(null);
      setAllocOpenInvoices([]);
      setAllocAmounts({});
      setAllocSingle('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setAllocSubmitting(false);
    }
  };

  // Expense CRUD — Phase 95: Purchases-only schema. No deduction, no source, no bank, no description.
  const openExpenseAdd = () => { setEditingExpense(null); setExpenseForm({ date: new Date().toISOString().split('T')[0], amount: '', net_amount: '', vat_amount: '', vat_rate: 19, category_id: purchaseCategories[0]?.category_id || '', notes: '', description: '', vendor_id: '', vendor_name: '', attachment_name: '', attachment_data: '', attachment_url: '' }); setVendorSearch(''); setExpenseDialog(true); };
  const openExpenseEdit = (e) => {
    setEditingExpense(e);
    // Phase 109 — infer VAT rate from existing data if it makes sense (net > 0), otherwise default 19.
    const n = Number(e.net_amount) || 0;
    const v = Number(e.vat_amount) || 0;
    const inferredRate = n > 0 ? Math.round((v / n) * 1000) / 10 : 19;
    setExpenseForm({ date: e.date, amount: String(e.amount), net_amount: String(e.net_amount ?? ''), vat_amount: String(e.vat_amount ?? ''), vat_rate: inferredRate || 19, category_id: e.category_id || '', notes: e.notes || '', description: e.description || '', vendor_id: e.vendor_id || '', vendor_name: e.vendor_name || '', attachment_name: e.attachment_name || '', attachment_data: '', attachment_url: e.attachment_url || '' });
    setVendorSearch(e.vendor_name || '');
    setExpenseDialog(true);
  };

  // Phase 435 — Expense Vendors catalog fetch.
  const fetchExpenseVendors = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/expense-vendors`, axCfg);
      setExpenseVendors(r.data || []);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { fetchExpenseVendors(); }, [fetchExpenseVendors]);

  // Phase 109 — Net/VAT/Total auto-calculation helpers. Editing any field updates the other two.
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const fmtNum = (n) => (n === 0 ? '' : round2(n).toFixed(2));
  const onExpenseNetChange = (raw) => setExpenseForm(f => {
    const net = parseFloat(raw);
    if (raw === '' || isNaN(net)) return { ...f, net_amount: raw, vat_amount: '', amount: '' };
    const rate = Number(f.vat_rate) || 0;
    const vat = round2(net * rate / 100);
    return { ...f, net_amount: raw, vat_amount: fmtNum(vat), amount: fmtNum(net + vat) };
  });
  const onExpenseTotalChange = (raw) => setExpenseForm(f => {
    const total = parseFloat(raw);
    if (raw === '' || isNaN(total)) return { ...f, amount: raw, net_amount: '', vat_amount: '' };
    const rate = Number(f.vat_rate) || 0;
    const net = round2(total / (1 + rate / 100));
    return { ...f, amount: raw, net_amount: fmtNum(net), vat_amount: fmtNum(total - net) };
  });
  const onExpenseVatChange = (raw) => setExpenseForm(f => {
    const vat = parseFloat(raw);
    const net = parseFloat(f.net_amount);
    if (raw === '' || isNaN(vat)) return { ...f, vat_amount: raw };
    if (!isNaN(net)) return { ...f, vat_amount: raw, amount: fmtNum(net + vat) };
    return { ...f, vat_amount: raw };
  });
  const onExpenseRateChange = (raw) => setExpenseForm(f => {
    const rate = parseFloat(raw);
    const next = { ...f, vat_rate: raw };
    if (isNaN(rate)) return next;
    // Recompute from Net if set, otherwise from Total.
    const net = parseFloat(f.net_amount);
    if (!isNaN(net)) {
      const vat = round2(net * rate / 100);
      return { ...next, vat_amount: fmtNum(vat), amount: fmtNum(net + vat) };
    }
    const total = parseFloat(f.amount);
    if (!isNaN(total)) {
      const nn = round2(total / (1 + rate / 100));
      return { ...next, net_amount: fmtNum(nn), vat_amount: fmtNum(total - nn) };
    }
    return next;
  });
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseForm.category_id) {
      toast.error(t('categoryRequired'));
      return;
    }
    const cat = purchaseCategories.find(c => c.category_id === expenseForm.category_id)
      || categories.find(c => c.category_id === expenseForm.category_id); // legacy fallback
    const net = parseFloat(expenseForm.net_amount) || 0;
    const vat = parseFloat(expenseForm.vat_amount) || 0;
    const amt = expenseForm.amount === '' ? (net + vat) : (parseFloat(expenseForm.amount) || 0);
    const payload = {
      date: expenseForm.date,
      category_id: expenseForm.category_id,
      category_key: cat?.key || 'other',
      net_amount: net,
      vat_amount: vat,
      amount: amt,
      description: expenseForm.description || '',
      vendor_id: expenseForm.vendor_id || '',
      vendor_name: expenseForm.vendor_name || '',
      attachment_name: expenseForm.attachment_name || '',
      // attachment_data: data-URL when a new file is selected; backend
      // stores it as attachment_url. Empty string preserves prior value
      // when editing without re-uploading.
      attachment_data: expenseForm.attachment_data || '',
      attachment_url: expenseForm.attachment_url || '',
      notes: expenseForm.notes || '',
    };
    try {
      if (editingExpense) await axios.put(`${API}/company/expenses/${editingExpense.expense_id}`, payload, axCfg);
      else await axios.post(`${API}/company/expenses`, payload, axCfg);
      toast.success(t('savedSuccess')); setExpenseDialog(false); fetchAll(); fetchExpenseVendors(); fetchPanelExpenses(expApplied);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Category CRUD — Phase 95: supports both Finance (expense_categories) and Purchases (purchase_categories) via `catScope`
  const openCatAdd = (scope = 'finance') => { setCatScope(scope); setEditingCat(null); setCatForm({ name_en: '', name_el: '', bucket: '' }); setCatDialog(true); };
  const openCatEdit = (c, scope = 'finance') => { setCatScope(scope); setEditingCat(c); setCatForm({ name_en: c.name_en || '', name_el: c.name_el || '', bucket: c.bucket || '' }); setCatDialog(true); };
  const catEndpoint = (scope) => scope === 'purchase' ? `${API}/company/purchase-categories` : `${API}/company/expense-categories`;
  const handleCatSubmit = async (e) => {
    e.preventDefault();
    try {
      const base = catEndpoint(catScope);
      // Phase 110 — bucket is only meaningful for Finance (expense) categories.
      const payload = catScope === 'finance'
        ? { name_en: catForm.name_en, name_el: catForm.name_el, bucket: catForm.bucket || null }
        : { name_en: catForm.name_en, name_el: catForm.name_el };
      if (editingCat) {
        await axios.put(`${base}/${editingCat.category_id}`, payload, axCfg);
      } else {
        await axios.post(base, payload, axCfg);
      }
      toast.success(t('savedSuccess'));
      setCatDialog(false);
      setEditingCat(null);
      setCatForm({ name_en: '', name_el: '', bucket: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Delete — Phase 108: supplier/invoice/payment routed here too so everything uses the shared AlertDialog.
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const { type, id, bankId, supplierId, extra, cascade } = deleteTarget;
      if (type === 'income') await axios.delete(`${API}/company/income/${id}`, axCfg);
      else if (type === 'expense') await axios.delete(`${API}/company/expenses/${id}`, axCfg);
      else if (type === 'bank') await axios.delete(`${API}/bank-accounts/${id}`, axCfg);
      else if (type === 'category') await axios.delete(`${API}/company/expense-categories/${id}`, axCfg);
      else if (type === 'purchase_category') await axios.delete(`${API}/company/purchase-categories/${id}`, axCfg);
      else if (type === 'bank_payment') await axios.delete(`${API}/bank-accounts/${bankId}/payments/${id}`, axCfg);
      else if (type === 'supplier') await axios.delete(`${API}/suppliers/${id}${cascade ? '?cascade=true' : ''}`, axCfg);
      else if (type === 'transfer') await axios.delete(`${API}/internal-transfers/${id}`, axCfg);
      else if (type === 'supplier_invoice') await axios.delete(`${API}/suppliers/${supplierId}/invoices/${id}`, axCfg);
      else if (type === 'supplier_payment') {
        // extra.source === 'bank_payment' → delete from the owning bank_payments collection
        if (extra && extra.source === 'bank_payment' && extra.bank_account_id) {
          await axios.delete(`${API}/bank-accounts/${extra.bank_account_id}/payments/${id}`, axCfg);
        } else {
          await axios.delete(`${API}/suppliers/${supplierId}/payments/${id}`, axCfg);
        }
      }
      toast.success(t('deletedSuccess'));
      setDeleteDialog(false); setDeleteTarget(null);
      fetchAll();
      if (type === 'expense') fetchPanelExpenses(expApplied);
      if (type === 'supplier') {
        if (selectedSupplierId === id) setSelectedSupplierId(null);
        fetchSuppliers();
      } else if (type === 'supplier_invoice' || type === 'supplier_payment') {
        fetchSupplierDetail(supplierId);
        fetchSuppliers();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Error';
      // Phase 108 — if the backend refused a supplier delete because of related records,
      // offer the user a one-click cascade retry (same AlertDialog, new message).
      if (deleteTarget?.type === 'supplier' && !deleteTarget?.cascade && /Cannot delete supplier/i.test(detail)) {
        setDeleteTarget({ ...deleteTarget, cascade: true, askCascade: true, cascadeMessage: detail });
        return; // keep dialog open; user confirms cascade from the same dialog
      }
      toast.error(detail);
    }
  };
  const confirmDelete = (type, id, bankId, supplierId, extra) => {
    setDeleteTarget({ type, id, bankId, supplierId, extra });
    setDeleteDialog(true);
  };

  // Phase 129 — Human-readable Paid From label (for Bank Payment & Statement tables).
  const paidFromLabel = (pf) => ({
    vat: t('paidFromVat'),
    social_insurance_10: t('paidFromSocialInsurance'),
    allocation_12_5: t('paidFrom125'),
    reserve_10: t('paidFromReserve'),
    final_balance: t('paidFromFinalBalance'),
  }[pf] || t('paidFromFinalBalance'));
  const paidFromBadgeClass = (pf) => ({
    vat: 'border-amber-200 bg-amber-50 text-amber-700',
    social_insurance_10: 'border-teal-200 bg-teal-50 text-teal-700',
    allocation_12_5: 'border-violet-200 bg-violet-50 text-violet-700',
    reserve_10: 'border-orange-200 bg-orange-50 text-orange-700',
    final_balance: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  }[pf] || 'border-zinc-200 bg-zinc-50 text-zinc-700');
  // Phase 112/113/126 — bucket helpers. The Paid From fund drives available balance and overdraft checks.
  // Supports all 5 funds: VAT, 12.5%, Reserve, Social Insurance, and Final Balance.
  // Phase 126: `final_balance` reads from `calculation.final_balance` (authoritative) so the Add Payment
  // modal always shows the same number as the top strip, with no drift.
  const bucketBalance = (bucket) => {
    if (!summary) return null;
    const calc = summary.calculation || null;
    if (bucket === 'vat') return calc ? (calc.deductions?.vat?.remaining ?? 0) : (summary.balance_vat || 0);
    if (bucket === 'allocation_12_5') return calc ? (calc.deductions?.social_12_5?.remaining ?? 0) : (summary.balance_12_5 || 0);
    if (bucket === 'reserve_10') return calc ? (calc.deductions?.reserve_10?.remaining ?? 0) : (summary.balance_reserve_10 || 0);
    if (bucket === 'social_insurance_10') return calc ? (calc.deductions?.social_insurance_10?.remaining ?? 0) : (summary.balance_social_insurance_10 || 0);
    if (bucket === 'final_balance') {
      // Phase 232 — Final Balance = Current Balance − VAT − Social Insurance
      // − 12.5% Tax − Reserve. "Current Balance" is the sum of per-account
      // current balances for accounts where `include_in_summary` is ON
      // (backend field `calculation.current_balance_summary`). When all accounts
      // are included this equals the bank_balance; when a user toggles OFF
      // a noisy account it drops out of the formula.
      const currentBal = calc ? (calc.current_balance_summary ?? calc.bank_balance ?? 0) : (summary.bank_balance ?? 0);
      const vat = bucketBalanceRaw('vat');
      const a125 = bucketBalanceRaw('allocation_12_5');
      const r10 = bucketBalanceRaw('reserve_10');
      const si10 = bucketBalanceRaw('social_insurance_10');
      return Math.round((currentBal - vat - a125 - r10 - si10) * 100) / 100;
    }
    return null;
  };

  // Internal helper — returns the raw bucket display value used by the
  // Pay From dropdown. Mirrors `bucketBalance` for the 4 deduction
  // buckets but never recurses into `final_balance`.
  const bucketBalanceRaw = (bucket) => {
    if (!summary) return 0;
    const calc = summary.calculation;
    if (bucket === 'vat') return calc ? (calc.deductions?.vat?.remaining ?? 0) : (summary.balance_vat || 0);
    if (bucket === 'allocation_12_5') return calc ? (calc.deductions?.social_12_5?.remaining ?? 0) : (summary.balance_12_5 || 0);
    if (bucket === 'reserve_10') return calc ? (calc.deductions?.reserve_10?.remaining ?? 0) : (summary.balance_reserve_10 || 0);
    if (bucket === 'social_insurance_10') return calc ? (calc.deductions?.social_insurance_10?.remaining ?? 0) : (summary.balance_social_insurance_10 || 0);
    return 0;
  };
  const bucketLabelOf = (bucket) => ({
    vat: t('bucketVat'),
    allocation_12_5: t('bucketAllocation'),
    reserve_10: t('bucketReserve'),
    social_insurance_10: t('bucketSocialInsurance'),
    final_balance: t('finalBalance'),
  }[bucket] || '');
  // Phase 111 — VAT Paid state
  const [vatPaid, setVatPaid] = useState({ supplier_invoices: [], expenses: [], total_supplier_vat: 0, total_expense_vat: 0, grand_total: 0 });
  const [vatFrom, setVatFrom] = useState('');
  const [vatTo, setVatTo] = useState('');
  useEffect(() => {
    if (activeTab !== 'vatPaid') return;
    const params = new URLSearchParams();
    if (vatFrom) params.append('date_from', vatFrom);
    if (vatTo) params.append('date_to', vatTo);
    const q = params.toString() ? `?${params.toString()}` : '';
    axios.get(`${API}/company/vat-paid${q}`, axCfg)
      .then(r => setVatPaid(r.data))
      .catch(() => {});
  }, [activeTab, vatFrom, vatTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const [overdraftDialog, setOverdraftDialog] = useState(null); // {diff, bucket, run} | null
  const round2Safe = (n) => Math.round((Number(n) || 0) * 100) / 100;

  // Phase 96 — Bank Payment CRUD (Phase 101: amount-only, Phase 113: Paid From fund selector, Phase 126: refresh on open)
  const openPaymentAdd = (bankId) => {
    setEditingPayment(null);
    setPaymentBankId(bankId);
    setPaymentForm({ date: new Date().toISOString().split('T')[0], amount: '', category_id: '', supplier_id: '', paid_from: '', notes: '', bill_id: '', vendor_account_id: '' });
    // Phase 462 — Reset the opt-in bill-link toggle for new payments.
    setShowBillLink(false);
    fetchAll();
    setPaymentDialog(true);
  };
  const openPaymentEdit = (p) => {
    setEditingPayment(p);
    setPaymentBankId(p.bank_account_id);
    setPaymentForm({ date: p.date, amount: String(p.amount), category_id: p.category_id || '', supplier_id: p.supplier_id || '', paid_from: p.paid_from || 'final_balance', notes: p.notes || '', bill_id: p.bill_id || '', vendor_account_id: p.vendor_account_id || '' });
    // Phase 462 — Auto-reveal the bill-link section when editing a payment
    // that already had a vendor linked, so the user can adjust/clear it.
    setShowBillLink(!!p.vendor_account_id || !!p.bill_id);
    fetchAll();
    setPaymentDialog(true);
  };
  const submitPayment = async (force = false) => {
    if (!paymentForm.category_id && !paymentForm.supplier_id) {
      toast.error(t('categoryOrSupplierRequired'));
      return;
    }
    // Phase 545 — The bucket Paid From selector was removed entirely. All
    // bank payments are recorded as a plain treasury movement against the
    // selected bank account. We pass `final_balance` to the backend so the
    // historical paid_from column stays populated, but no UI bucket picker
    // is offered anymore.
    const effectiveFund = paymentForm.paid_from || 'final_balance';
    const amt = parseFloat(paymentForm.amount) || 0;
    if (amt <= 0) {
      toast.error(t('amountMustBePositive') || 'Amount must be positive');
      return;
    }
    const payload = {
      date: paymentForm.date,
      category_id: paymentForm.category_id || null,
      supplier_id: paymentForm.supplier_id || null,
      paid_from: effectiveFund,
      net_amount: 0,
      vat_amount: 0,
      amount: amt,
      notes: paymentForm.notes || '',
      // Phase 224 — Optional Bills linkage. When set, backend creates a
      // matching bill_payments row so the bill balance + status auto-update.
      bill_id: paymentForm.bill_id || null,
      // Phase 230 — Vendor-account linkage. When set, backend FIFO-allocates
      // the amount across the vendor's outstanding bills (oldest-first) and
      // creates one bill_payments row per consumed bill.
      vendor_account_id: paymentForm.vendor_account_id || null,
    };
    try {
      if (editingPayment) await axios.put(`${API}/bank-accounts/${paymentBankId}/payments/${editingPayment.payment_id}`, payload, axCfg);
      else await axios.post(`${API}/bank-accounts/${paymentBankId}/payments`, payload, axCfg);
      toast.success(t('savedSuccess')); setPaymentDialog(false); setOverdraftDialog(null); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const handlePaymentSubmit = (e) => { e.preventDefault(); submitPayment(false); };

  // Phase 134 — Internal Transfer handlers.
  const openTransferAdd = (fromAccountId = '') => {
    setEditingTransfer(null);
    setTransferForm({ date: new Date().toISOString().split('T')[0], from_account_id: fromAccountId, to_account_id: '', amount: '', reference: '', notes: '' });
    setTransferDialog(true);
  };
  const openTransferEdit = (x) => {
    setEditingTransfer(x);
    setTransferForm({ date: x.date, from_account_id: x.from_account_id, to_account_id: x.to_account_id, amount: String(x.amount), reference: x.reference || '', notes: x.notes || '' });
    setTransferDialog(true);
  };
  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.from_account_id || !transferForm.to_account_id) { toast.error(t('fromToRequired')); return; }
    if (transferForm.from_account_id === transferForm.to_account_id) { toast.error(t('fromToMustDiffer')); return; }
    const amt = parseFloat(transferForm.amount) || 0;
    if (amt <= 0) { toast.error(t('amountMustBePositive') || 'Amount must be positive'); return; }
    const payload = { date: transferForm.date, from_account_id: transferForm.from_account_id, to_account_id: transferForm.to_account_id, amount: amt, reference: transferForm.reference || '', notes: transferForm.notes || '' };
    try {
      if (editingTransfer) await axios.put(`${API}/internal-transfers/${editingTransfer.transfer_id}`, payload, axCfg);
      else await axios.post(`${API}/internal-transfers`, payload, axCfg);
      toast.success(t('savedSuccess'));
      setTransferDialog(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const deleteTransfer = async (id) => {
    try {
      await axios.delete(`${API}/internal-transfers/${id}`, axCfg);
      toast.success(t('deleted'));
      fetchAll();
    } catch { toast.error(t('errorOccurred') || 'Error'); }
  };

  const SourceSelector = ({ value, onChange, showBankSelect, bankValue, onBankChange }) => (
    <div className="space-y-2">
      <div><Label className="text-xs">{t('paymentSource')}</Label>
        <Select value={value} onValueChange={onChange}><SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="cash">{t('cashSource')}</SelectItem><SelectItem value="bank">{t('bankSource')}</SelectItem></SelectContent>
        </Select></div>
      {showBankSelect && value === 'bank' && bankAccounts.length > 0 && (
        <div><Label className="text-xs">{t('selectBank')}</Label>
          <Select value={bankValue} onValueChange={onBankChange}><SelectTrigger className="rounded-none mt-1"><SelectValue placeholder={t('selectBank')} /></SelectTrigger>
            <SelectContent>{bankAccounts.map(b => <SelectItem key={b.account_id} value={b.account_id}>{b.name}</SelectItem>)}</SelectContent>
          </Select></div>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="space-y-3" data-testid="finance-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-bold tracking-tight" data-testid="finance-title">{t('finance')}</h1>
          <a
            href="/sync-audit"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-700 hover:text-blue-700 border border-zinc-200 px-2.5 py-1 bg-white hover:border-blue-400 transition-colors"
            data-testid="finance-sync-audit-link"
            title={t('syncAuditHint') || 'Compare Finance with Cleaning & Building payments'}
          >
            🔄 {t('syncAudit') || 'Sync Audit'}
          </a>
        </div>

        {/* Filters row */}
        <div className="bg-white border border-zinc-200" data-testid="finance-filters">
          <div className="flex items-end gap-3 flex-wrap px-4 py-2.5">
            {/* Filters (left) */}
            <div>
              <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('fromDate')}</Label>
              <DatePickerDMY value={dateFrom} onChange={setDateFrom} className="h-8 w-32 font-mono text-xs" data-testid="finance-date-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('toDate')}</Label>
              <DatePickerDMY value={dateTo} onChange={setDateTo} className="h-8 w-32 font-mono text-xs" data-testid="finance-date-to" />
            </div>
            {(dateFrom || dateTo) && (
              <Button variant="outline" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setClientFilter('all'); }} className="rounded-none h-8 text-xs gap-1" data-testid="clear-filters-btn">
                <Funnel size={12} />{t('clearFilter')}
              </Button>
            )}
            {/* Phase 216 — Global sort-order toggle. Default is "Newest
                first" (DESC) so the latest entry the user creates always
                appears at the top of every transaction table on this
                page. The toggle propagates through `sortDesc` + the
                inline statement display sorts. */}
            <div className="ml-auto flex items-end gap-2">
              <div>
                <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('sortOrder') || 'Sort Order'}</Label>
                <button
                  type="button"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  className="h-8 px-3 text-xs border border-zinc-200 hover:bg-zinc-50 transition-colors flex items-center gap-1.5 font-medium"
                  data-testid="finance-sort-order-toggle"
                  title={sortOrder === 'desc' ? (t('newestFirstHint') || 'Newest first (click to flip)') : (t('oldestFirstHint') || 'Oldest first (click to flip)')}
                >
                  <span aria-hidden>{sortOrder === 'desc' ? '↓' : '↑'}</span>
                  {sortOrder === 'desc' ? (t('newestFirst') || 'Newest → Oldest') : (t('oldestFirst') || 'Oldest → Newest')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Phase 416 — Top summary strip removed. The redesigned Overview
            cards (Opening Balance / Total Income / Total Supplier Expenses /
            Total Expenses) now live inside the Overview tab below. */}

        {/* Left sidebar + main content layout (Phase 72) */}
        <div className="flex gap-6">
          {/* LEFT SIDEBAR — configuration sections */}
          <aside className="w-56 flex-shrink-0 border-r border-zinc-200 pr-4" data-testid="finance-sidebar">
            <nav className="flex flex-col">
              {/* Phase 414 — Restructured Finance sidebar.
                  Groups: Finance · Sales · Purchases · Banking.
                  Transfers removed (already visible inside Bank Statements). */}

              {/* FINANCE group */}
              <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400" data-testid="sidebar-group-finance">{t('finance')}</div>
              <button
                onClick={() => setActiveTab('overview')}
                data-testid="sidebar-overview"
                className={`text-left px-3 py-2 text-sm border-l-2 transition-colors ${activeTab === 'overview'
                  ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                  : 'border-l-transparent text-zinc-600 hover:bg-zinc-50'}`}
              >{t('overview')}</button>

              {/* SALES group — Phase 438: Customers + global searchable
                  Invoices/Payments indexes (NOT standalone accounting modules
                  anymore). Clicking a Customer still opens the nested detail
                  view (Summary/Invoices/Payments/Statement). */}
              <div className="mt-3 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400" data-testid="sidebar-group-sales">{t('sales') || 'Sales'}</div>
              <button
                onClick={() => setActiveTab('sales:customers')}
                data-testid="sidebar-sales-customers"
                className={`text-left px-3 py-2 text-sm border-l-2 transition-colors ${activeTab === 'sales:customers'
                  ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                  : 'border-l-transparent text-zinc-600 hover:bg-zinc-50'}`}
              >{t('customers') || 'Customers'}</button>
              {/* Phase 1026 — User opted to keep the legacy invoicing system
                  for Customer Invoices and Payments. The two sidebar entries
                  (sales:invoices and sales:payments) are hidden from the UI;
                  the underlying data and endpoints are preserved intact.
                  Only Internal Invoices and Internal Cash remain here. */}
              {/* Phase 524 — Dedicated Internal Invoices sub-tab so internal
                  bookkeeping is visibly separated from official customer
                  invoices everywhere in the Finance module. */}
              <button
                onClick={() => setActiveTab('sales:internal_invoices')}
                data-testid="sidebar-sales-internal-invoices"
                className={`text-left px-3 py-1.5 pl-7 text-xs border-l-2 transition-colors ${activeTab === 'sales:internal_invoices'
                  ? 'border-l-amber-600 bg-amber-50 text-amber-900 font-semibold'
                  : 'border-l-transparent text-amber-700 hover:bg-amber-50/60'}`}
              >↳ {t('internalInvoicesLabel') || 'Internal Invoices'}</button>
              {/* Phase 526/528 — Dedicated Internal Cash sub-tab. Phase 528:
                  the previous "Internal Payments" tab is replaced by a
                  per-customer Internal Cash ledger view. */}
              <button
                onClick={() => setActiveTab('sales:internal_cash')}
                data-testid="sidebar-sales-internal-cash"
                className={`text-left px-3 py-1.5 pl-7 text-xs border-l-2 transition-colors ${activeTab === 'sales:internal_cash'
                  ? 'border-l-amber-600 bg-amber-50 text-amber-900 font-semibold'
                  : 'border-l-transparent text-amber-700 hover:bg-amber-50/60'}`}
              >↳ {t('internalCashLabel') || 'Internal Cash'}</button>

              {/* PURCHASES group — Phase 438: Suppliers + global searchable
                  Invoices/Payments indexes (NOT standalone accounting modules
                  anymore). Clicking a Supplier opens the nested detail view. */}
              <div className="mt-3 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400" data-testid="sidebar-group-purchases">{t('purchases')}</div>
              <button
                onClick={() => setActiveTab('purchases:suppliers')}
                data-testid="sidebar-suppliers"
                className={`text-left px-3 py-2 text-sm border-l-2 transition-colors ${activeTab === 'purchases:suppliers'
                  ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                  : 'border-l-transparent text-zinc-600 hover:bg-zinc-50'}`}
              >{t('suppliers')}</button>
              <button
                onClick={() => setActiveTab('purchases:invoices')}
                data-testid="sidebar-supplier-invoices"
                className={`text-left px-3 py-1.5 pl-7 text-xs border-l-2 transition-colors ${activeTab === 'purchases:invoices'
                  ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                  : 'border-l-transparent text-zinc-500 hover:bg-zinc-50'}`}
              >↳ {t('invoices') || 'Invoices'}</button>
              <button
                onClick={() => setActiveTab('purchases:payments')}
                data-testid="sidebar-supplier-payments"
                className={`text-left px-3 py-1.5 pl-7 text-xs border-l-2 transition-colors ${activeTab === 'purchases:payments'
                  ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                  : 'border-l-transparent text-zinc-500 hover:bg-zinc-50'}`}
              >↳ {t('payments') || 'Payments'}</button>
              {/* Phase 532 — Purchases sidebar consistency.
                  Reordered so "Vendors" is the parent and "Expenses" is
                  nested underneath, mirroring the Customers → Invoices /
                  Payments hierarchy on the Sales side. Routes/data are
                  unchanged: clicking "Vendors" still opens the Expense
                  Vendors page (`expenseVendors`) and "Expenses" still
                  opens the Expenses list (`expensesList`). */}
              <button
                onClick={() => setActiveTab('expenseVendors')}
                data-testid="sidebar-expense-vendors"
                className={`text-left px-3 py-2 text-sm border-l-2 transition-colors ${activeTab === 'expenseVendors'
                  ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                  : 'border-l-transparent text-zinc-600 hover:bg-zinc-50'}`}
              >{t('vendors') || 'Vendors'}</button>
              <button
                onClick={() => setActiveTab('expensesList')}
                data-testid="sidebar-expenses-list"
                className={`text-left px-3 py-1.5 pl-7 text-xs border-l-2 transition-colors ${activeTab === 'expensesList'
                  ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                  : 'border-l-transparent text-zinc-500 hover:bg-zinc-50'}`}
              >↳ {t('expenses')}</button>

              {/* BANKING group — Phase 419: "Reference Categories" (renamed from
                  "Expense Categories") moved here. These are helper labels for
                  manual bank movement categorization and DO NOT contribute to
                  Total Expenses in the Overview. */}
              <div className="mt-3 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400" data-testid="sidebar-group-banking">{t('banking') || 'Banking'}</div>
              <button
                onClick={() => setActiveTab('banks')}
                data-testid="sidebar-banks"
                className={`text-left px-3 py-2 text-sm border-l-2 transition-colors ${activeTab === 'banks'
                  ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                  : 'border-l-transparent text-zinc-600 hover:bg-zinc-50'}`}
              >{t('bankAccounts')}</button>
              <button
                onClick={() => setActiveTab('expenseCategories')}
                data-testid="sidebar-reference-categories"
                className={`text-left px-3 py-2 text-sm border-l-2 transition-colors ${activeTab === 'expenseCategories'
                  ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                  : 'border-l-transparent text-zinc-600 hover:bg-zinc-50'}`}
              >{t('referenceCategories') || 'Reference Categories'}</button>
            </nav>
          </aside>

          {/* MAIN CONTENT — driven purely by sidebar (no global operation tabs) */}
          <div className="flex-1 min-w-0">
            {/* Phase 415 — Sales embedded inside Finance (master-detail). */}
            {activeTab.startsWith('sales:') ? (
              <Sales
                embed
                activeTab={activeTab.split(':')[1]}
                setActiveTab={(t) => setActiveTab(`sales:${t}`)}
              />
            ) : activeTab.startsWith('purchases:') ? (
              /* Phase 421 — Purchases embedded inside Finance (mirrors Sales). */
              <Purchases
                embed
                activeTab={activeTab.split(':')[1]}
                setActiveTab={(t) => setActiveTab(`purchases:${t}`)}
              />
            ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4 mt-4" data-testid="overview-panel">
            {/* Phase 416 — Compact overview cards (Opening Balance / Total Income / Total Supplier Expenses / Total Expenses) */}
            <FinanceOverviewCards t={t} dateFrom={dateFrom} dateTo={dateTo} />
            {/* Phase 90 — Overview is now actionable: who do I owe money to? */}
            {(() => {
              const outstanding = suppliers
                .filter(s => (s.balance ?? 0) > 0)
                .sort((a, b) => (b.balance ?? 0) - (a.balance ?? 0));
              const grandTotal = outstanding.reduce((s, x) => s + (x.balance || 0), 0);
              // Severity thresholds (relative to the largest balance so it scales with any currency of data)
              const maxBal = outstanding[0]?.balance || 0;
              const tierClass = (b) => {
                if (maxBal === 0) return 'text-emerald-700';
                const ratio = b / maxBal;
                if (ratio >= 0.66) return 'text-red-700';
                if (ratio >= 0.33) return 'text-orange-600';
                return 'text-emerald-700';
              };
              const tierBg = (b) => {
                if (maxBal === 0) return 'bg-white';
                const ratio = b / maxBal;
                if (ratio >= 0.66) return 'bg-red-50/60';
                if (ratio >= 0.33) return 'bg-orange-50/50';
                return 'bg-emerald-50/40';
              };
              return (
                <div className="space-y-3" data-testid="suppliers-outstanding-section">
                  <div className="flex items-end justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-semibold">{t('suppliersOutstandingBalances')}</h3>
                      <p className="text-[11px] text-zinc-400 mt-0.5">{t('suppliersOutstandingDesc')}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{t('totalOwed')}</div>
                      <div className={`text-xl font-mono font-bold ${grandTotal > 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="overview-total-owed">&euro;{grandTotal.toFixed(2)}</div>
                    </div>
                  </div>

                  <div className="border border-zinc-200 bg-white overflow-x-auto">
                    {outstanding.length === 0 ? (
                      <div className="p-10 text-center text-sm">
                        <div className="text-emerald-700 font-semibold mb-1">{t('allSuppliersPaid')} ✓</div>
                        <div className="text-zinc-400 text-xs">{t('allSuppliersPaidDesc')}</div>
                      </div>
                    ) : (
                      <table className="excel-table" data-testid="suppliers-outstanding-table">
                        <thead>
                          <tr>
                            <th>{t('supplierName')}</th>
                            <th className="text-right w-40">{t('totalInvoices')}</th>
                            <th className="text-right w-40">{t('totalPaid')}</th>
                            <th className="text-right w-40">{t('outstandingBalance')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {outstanding.map(s => (
                            <tr
                              key={s.supplier_id}
                              className={`cursor-pointer hover:brightness-95 transition-all ${tierBg(s.balance || 0)}`}
                              onClick={() => { setActiveTab('suppliers'); setSelectedSupplierId(s.supplier_id); setSupplierTab('statement'); }}
                              data-testid={`outstanding-row-${s.supplier_id}`}
                            >
                              <td className="text-sm">
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tierClass(s.balance || 0).replace('text-', 'bg-')}`} />
                                  <span className="font-semibold">{s.name}</span>
                                </div>
                              </td>
                              <td className="font-mono text-right text-sm text-zinc-700">&euro;{(s.total_invoices || 0).toFixed(2)}</td>
                              <td className="font-mono text-right text-sm text-emerald-700">&euro;{(s.total_payments || 0).toFixed(2)}</td>
                              <td className={`font-mono text-right text-sm font-bold ${tierClass(s.balance || 0)}`} data-testid={`outstanding-balance-${s.supplier_id}`}>&euro;{(s.balance || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                          <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300" data-testid="outstanding-total-row">
                            <td className="text-sm">{t('total').toUpperCase()}</td>
                            <td className="font-mono text-right text-sm text-zinc-700">&euro;{outstanding.reduce((s, x) => s + (x.total_invoices || 0), 0).toFixed(2)}</td>
                            <td className="font-mono text-right text-sm text-emerald-700">&euro;{outstanding.reduce((s, x) => s + (x.total_payments || 0), 0).toFixed(2)}</td>
                            <td className="font-mono text-right text-sm font-bold text-red-800">&euro;{grandTotal.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          {/* BANK ACCOUNTS TAB — master-detail (accounts sub-list + single-account detail) */}
          <TabsContent value="banks" className="mt-4">
            <div className="flex gap-4">

              {/* Sub-sidebar: vertical list of accounts (Alpha, Hellenic, Cash) */}
              <aside className="w-56 flex-shrink-0 border-r border-zinc-200 pr-3" data-testid="accounts-sublist">
                <div className="flex flex-col">
                  {/* Phase 528 — Internal Cash is no longer a bank account.
                      It now lives under Finance → Customers → Internal Cash
                      (per-customer payment ledger), so it must be hidden
                      from the Bank Accounts sidebar entirely. */}
                  {bankAccounts.filter(b => !b.is_internal_cash && b.account_id !== INTERNAL_CASH_ACCOUNT_ID).map(b => {
                    const curBal = computeBankBalance(b.account_id);
                    const active = selectedBankId === b.account_id;
                    return (
                      <button
                        key={b.account_id}
                        onClick={() => setSelectedBankId(b.account_id)}
                        data-testid={`sublist-bank-${b.account_id}`}
                        className={`text-left px-3 py-2 text-sm border-l-2 transition-colors flex items-center gap-2 ${active ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold' : 'border-l-transparent text-zinc-600 hover:bg-zinc-50'}`}
                      >
                        <Bank size={14} className="flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate flex items-center gap-1">
                            <span className="truncate">{b.name}</span>
                            {b.include_in_summary === false && (
                              <span
                                className="text-[8px] font-bold text-zinc-400 leading-none"
                                title={t('excludedFromSummary') || 'Excluded from summary'}
                                data-testid={`bank-side-excluded-${b.account_id}`}
                              >⊘</span>
                            )}
                          </div>
                          <div className={`text-[10px] font-mono ${active ? 'text-zinc-600' : 'text-zinc-400'}`}>&euro;{curBal.toFixed(2)}</div>
                        </div>
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setSelectedBankId('cash')}
                    data-testid="sublist-cash"
                    className={`text-left px-3 py-2 text-sm border-l-2 transition-colors flex items-center gap-2 ${selectedBankId === 'cash' ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold' : 'border-l-transparent text-zinc-600 hover:bg-zinc-50'}`}
                  >
                    <CurrencyEur size={14} className="flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div>{t('cash')}</div>
                      <div className={`text-[10px] font-mono ${selectedBankId === 'cash' ? 'text-zinc-600' : 'text-zinc-400'}`}>&euro;{cashIncome.reduce((s, e) => s + e.amount, 0).toFixed(2)}</div>
                    </div>
                  </button>
                </div>
                <Button variant="outline" size="sm" onClick={openBankAdd} className="rounded-none mt-3 w-full text-xs h-8" data-testid="add-bank-btn">
                  <Plus size={12} className="mr-1" /> {t('addBankAccount')}
                </Button>
              </aside>

              {/* Detail pane: renders ONLY the selected account */}
              <div className="flex-1 min-w-0" data-testid="accounts-detail">
                {bankAccounts.length === 0 && selectedBankId !== 'cash' && (
                  <div className="border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-400">{t('noBankAccountsConfigured')}</div>
                )}

                {bankAccounts.filter(b => b.account_id === selectedBankId).map(b => {
                // Phase 527 — Internal Cash account renders a SIMPLIFIED
                // view per user requirement: only "Payments" list + total
                // balance. No bank-style income/transfers/statement tabs.
                if (b.is_internal_cash || b.account_id === INTERNAL_CASH_ACCOUNT_ID) {
                  const internalIncomeRows = (allIncome || [])
                    .filter(e => e.affects_treasury === false && !e.is_deleted)
                    .map(e => ({
                      key: `inc:${e.income_id}`,
                      date: e.date,
                      amount: e.amount || 0,
                      client_id: e.client_id || null,
                      reference: e.income_id,
                      description: e.description || e.notes || '',
                      kind: 'income',
                    }));
                  const internalSalesRows = (salesPayments || [])
                    .filter(p => p.payment_account === 'internal_cash_tracking' && !p.is_deleted)
                    .map(p => ({
                      key: `pay:${p.sales_payment_id}`,
                      date: p.date,
                      amount: p.amount || 0,
                      client_id: p.client_id || null,
                      reference: p.reference || p.sales_payment_id,
                      description: p.notes || p.method || '',
                      kind: 'sales_payment',
                    }));
                  const combined = [...internalIncomeRows, ...internalSalesRows]
                    .sort((a, x) => (x.date || '').localeCompare(a.date || ''));
                  const totalBalance = combined.reduce((s, r) => s + (r.amount || 0), 0);
                  const clientName = (cid) => (clients.find(c => c.client_id === cid)?.name) || '—';
                  return (
                    <div key={b.account_id} className="border border-amber-300 bg-amber-50/40 p-4" data-testid={`bank-detail-internal-cash`}>
                      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                        <div className="flex items-center gap-3">
                          <Bank size={20} className="text-amber-700" />
                          <div>
                            <div className="text-xl font-semibold text-amber-900">{b.name}</div>
                            <div className="text-[10px] text-amber-700 uppercase tracking-wider font-bold">
                              {t('internalCashWalletHint') || 'Auto-managed wallet — Excluded from official treasury & VAT'}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">{t('totalBalance') || 'Total Balance'}</div>
                          <div className="text-2xl font-mono font-bold text-amber-900" data-testid="internal-cash-total-balance">&euro;{totalBalance.toFixed(2)}</div>
                          <div className="text-[10px] text-amber-700 font-mono">{combined.length} {combined.length === 1 ? (t('entry') || 'entry') : (t('entries') || 'entries')}</div>
                        </div>
                      </div>
                      {combined.length === 0 ? (
                        <div className="border border-dashed border-amber-300 p-8 text-center text-sm text-amber-700">
                          {t('noInternalCashPayments') || 'No Internal Cash payments yet. New entries are auto-routed here when you flag them as Internal Cash on Finance → Income or Sales → Internal Cash Payments.'}
                        </div>
                      ) : (
                        <div className="border border-amber-200 bg-white">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-amber-200 bg-amber-100 text-amber-900">
                                <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('date') || 'Date'}</th>
                                <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('client') || 'Client'}</th>
                                <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('description') || 'Description'}</th>
                                <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('source') || 'Source'}</th>
                                <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('amount') || 'Amount'}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {combined.map(r => (
                                <tr key={r.key} className="border-b border-amber-100 hover:bg-amber-50" data-testid={`internal-cash-row-${r.reference}`}>
                                  <td className="px-3 py-2 font-mono">{r.date}</td>
                                  <td className="px-3 py-2">{r.client_id ? clientName(r.client_id) : '—'}</td>
                                  <td className="px-3 py-2 text-zinc-700">{r.description}</td>
                                  <td className="px-3 py-2">
                                    <span className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${r.kind === 'income' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-sky-50 text-sky-700 border-sky-300'}`}>
                                      {r.kind === 'income' ? (t('finance') || 'Finance') : (t('customerPayment') || 'Customer Payment')}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">&euro;{r.amount.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-amber-100 font-bold text-amber-900">
                                <td colSpan={4} className="px-3 py-2 text-right uppercase tracking-wider text-[10px]">{t('totalBalance') || 'Total Balance'}</td>
                                <td className="px-3 py-2 text-right font-mono">&euro;{totalBalance.toFixed(2)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                }
                const bal = summary?.bank_accounts?.find(ba => ba.account_id === b.account_id);
                // Phase 136 — Derive Current Balance directly from raw data (includes internal transfers),
                // so it ALWAYS equals the last running balance of this bank's statement.
                const currentBalance = computeBankBalance(b.account_id);
                // Tooltip breakdown — mirrors the running balance math, so totals are consistent.
                const totalIncomeBank = allIncome.filter(e => e.source !== 'cash' && e.bank_account_id === b.account_id).reduce((s, e) => s + (e.amount || 0), 0);
                const totalPaymentsBank = allBankPayments.filter(p => p.bank_account_id === b.account_id).reduce((s, p) => s + (p.amount || 0), 0);
                const totalTransfersIn = transfers.filter(x => x.to_account_id === b.account_id).reduce((s, x) => s + (x.amount || 0), 0);
                const totalTransfersOut = transfers.filter(x => x.from_account_id === b.account_id).reduce((s, x) => s + (x.amount || 0), 0);
                // Phase 98 — use UNFILTERED datasets for bank tabs so per-bank From/To can reason about pre-range history.
                const allBankIncomeRows = allIncome.filter(e => e.source !== 'cash' && e.bank_account_id === b.account_id);
                const allBankPayRows = allBankPayments.filter(p => p.bank_account_id === b.account_id);
                const range = getBankRange(b.account_id);
                const bankIncomeRowsAllTypes = allBankIncomeRows.filter(e => inRange(e.date, range.from, range.to));
                // Phase 221 — apply income type filter on display only (totals follow filter).
                const bankIncomeRows = incomeTypeFilter === 'all'
                  ? bankIncomeRowsAllTypes
                  : bankIncomeRowsAllTypes.filter(e => (e.income_type || (e.client_id ? 'client_payment' : 'other')) === incomeTypeFilter);
                const bankPaymentRows = allBankPayRows.filter(p => inRange(p.date, range.from, range.to));
                // Pre-range (strictly before `from`) history establishes the range opening balance.
                // Phase 461 — Brought Forward MUST include internal
                // transfers (both directions) for the same account.
                // Otherwise filtering by date produces a B/F that doesn't
                // match yesterday's true closing balance.
                const preRangeIncome = range.from ? allBankIncomeRows.filter(e => (e.date || '') < range.from).reduce((s, e) => s + (e.amount || 0), 0) : 0;
                const preRangePayments = range.from ? allBankPayRows.filter(p => (p.date || '') < range.from).reduce((s, p) => s + (p.amount || 0), 0) : 0;
                const preRangeTransfersIn = range.from ? transfers.filter(x => x.to_account_id === b.account_id && (x.date || '') < range.from).reduce((s, x) => s + (x.amount || 0), 0) : 0;
                const preRangeTransfersOut = range.from ? transfers.filter(x => x.from_account_id === b.account_id && (x.date || '') < range.from).reduce((s, x) => s + (x.amount || 0), 0) : 0;
                const rangeOpeningBalance = Math.round(((b.opening_balance || 0) + preRangeIncome - preRangePayments + preRangeTransfersIn - preRangeTransfersOut) * 100) / 100;
                const bankExpRows = []; // legacy company_expenses no longer reflect here (Phase 95)
                const innerTab = getBankTab(b.account_id);

                // Build chronological statement — Phase 137: Opening Balance is a FIRST-CLASS transaction row
                // with its own date (required). It sorts into position by date like any other entry, but ties
                // always resolve in its favor so the running balance starts from the opening row.
                // Phase 138: secondary sort by created_at ASC so same-date transactions always appear in
                // insertion order — running balance and display both stay deterministic.
                // Phase 463 — When a From date filter is active, this row
                // is conceptually "Balance Brought Forward" (the closing
                // balance of the PREVIOUS day). Display date = day before
                // range.from, label = "Balance Brought Forward". When no
                // filter is active it stays as the account's true Opening
                // Balance (its own opening_balance_date).
                const _prevDay = (yyyyMmDd) => {
                  if (!yyyyMmDd) return '';
                  try {
                    const d = new Date(yyyyMmDd + 'T00:00:00Z');
                    d.setUTCDate(d.getUTCDate() - 1);
                    return d.toISOString().slice(0, 10);
                  } catch { return yyyyMmDd; }
                };
                const isFilteredOpening = !!range.from;
                const openingDate = isFilteredOpening
                  ? _prevDay(range.from)
                  : (b.opening_balance_date || (b.created_at || '').slice(0, 10) || '');
                const openingAmt = rangeOpeningBalance;
                const openingLabel = isFilteredOpening
                  ? (t('balanceBroughtForward') || 'Balance Brought Forward')
                  : t('openingBalance');
                const stmtAsc = [
                  { id: 'open', date: openingDate, source: 'opening', desc: openingLabel, amt: openingAmt, isOpening: true, isBroughtForward: isFilteredOpening, created_at: '' },
                  ...bankIncomeRows.map(e => {
                    const typeKey = e.income_type || (e.client_id ? 'client_payment' : 'other');
                    const isClientPay = typeKey === 'client_payment';
                    const sourceLabel = isClientPay
                      ? (e.client_name || (t('client') || 'Client'))
                      : (() => { const m = incomeTypeMeta(typeKey); return t(m.badgeKey); })();
                    return ({
                      id: `i-${e.income_id}`,
                      date: e.date,
                      source: 'income',
                      sourceLabel,
                      desc: e.description || e.notes || '',
                      amt: e.amount,
                      // Phase 223 — surface breakdown so the Statement table can show
                      // Gross / VAT / Net / Deductions / Final Net per income row.
                      gross: e.amount || 0,
                      vat: e.vat_amount || 0,
                      net: e.net_amount || 0,
                      deductions: (e.allocation_12_5 || 0) + (e.reserve_10_amount || 0) + (e.social_insurance_10 || 0),
                      final_net: e.final_net || 0,
                      income_type: typeKey,
                      client_name: e.client_name,
                      synced_with_building: !!(e.synced_building_payment_id || e.building_id),
                      // Phase 465 — Carry the duplicate flag so the Statement
                      // table can show a "Suspected Duplicate" badge. No
                      // record is hidden, deleted or merged automatically.
                      suspected_duplicate: !!e.suspected_duplicate,
                      duplicate_group_id: e.duplicate_group_id || null,
                      created_at: e.created_at || '',
                      created_by: e.created_by || '',
                      _raw: e,
                    });
                  }),
                  ...bankPaymentRows.map(p => {
                    const lbl = p.supplier_name
                      || (language === 'el' ? (p.category_name_el || p.category_name_en) : (p.category_name_en || p.category_name_el))
                      || t('payment');
                    const notes = (p.notes || '').trim();
                    const desc = notes ? `${lbl} (${notes})` : lbl;
                    return { id: `p-${p.payment_id}`, date: p.date, source: 'payment', sourceLabel: lbl, desc, amt: -p.amount, paid_from: p.paid_from || 'final_balance', created_at: p.created_at || '', _raw: p };
                  }),
                  // Phase 134 — Internal Transfers produce TWO rows: debit on the From account, credit on the To account.
                  // For the current account (b.account_id): if we're the "from", show negative debit; if we're the "to", show positive credit.
                  ...transfers.filter(x => (x.from_account_id === b.account_id || x.to_account_id === b.account_id) && inRange(x.date, range.from, range.to)).map(x => {
                    const isDebit = x.from_account_id === b.account_id;
                    const other = isDebit ? x.to_account_name : x.from_account_name;
                    const refPart = x.reference ? ` [${x.reference}]` : '';
                    const notesPart = x.notes ? ` (${x.notes})` : '';
                    const desc = `${t('internalTransfer')} ${isDebit ? '→' : '←'} ${other}${refPart}${notesPart}`;
                    return { id: `x-${x.transfer_id}-${isDebit ? 'd' : 'c'}`, date: x.date, source: 'transfer', sourceLabel: t('internalTransfer'), desc, amt: isDebit ? -x.amount : x.amount, transfer_id: x.transfer_id, created_at: x.created_at || '', _raw: x };
                  }),
                ].sort((x, y) => {
                  // Chronological pass for running balance: date ASC → created_at ASC → id ASC (stable tiebreak).
                  const cmp = (x.date || '').localeCompare(y.date || '');
                  if (cmp !== 0) return cmp;
                  if (x.isOpening) return -1;
                  if (y.isOpening) return 1;
                  const cc = (x.created_at || '').localeCompare(y.created_at || '');
                  if (cc !== 0) return cc;
                  return (x.id || '').localeCompare(y.id || '');
                });
                let run = 0;
                const stmtRows = stmtAsc.map(r => { run += r.amt; return { ...r, running: run }; });
                const finalRunningBalance = run; // = last running balance in chronological order
                // Phase 138/216 — Display order respects the global toggle:
                // DESC (default): date DESC, created_at DESC (newest first).
                // ASC: date ASC, created_at ASC (oldest first).
                // The Opening row stays anchored to the start of the
                // running-balance read direction so the balance column
                // always tells the same story top-to-bottom.
                const isDesc = sortOrder !== 'asc';
                const stmtDisplay = [...stmtRows].sort((x, y) => {
                  const cmp = isDesc
                    ? (y.date || '').localeCompare(x.date || '')
                    : (x.date || '').localeCompare(y.date || '');
                  if (cmp !== 0) return cmp;
                  if (isDesc) {
                    if (x.isOpening) return -1;
                    if (y.isOpening) return 1;
                    const cc = (y.created_at || '').localeCompare(x.created_at || '');
                    if (cc !== 0) return cc;
                    return (y.id || '').localeCompare(x.id || '');
                  }
                  if (x.isOpening) return -1;
                  if (y.isOpening) return 1;
                  const cc = (x.created_at || '').localeCompare(y.created_at || '');
                  if (cc !== 0) return cc;
                  return (x.id || '').localeCompare(y.id || '');
                });

                return (
                  <div key={b.account_id} className="border border-zinc-200 bg-white" data-testid={`bank-block-${b.account_id}`}>
                    {/* Phase 465 — Prominent "Viewing" banner so the user
                        can never accidentally save to the wrong account.
                        Solid emerald strip with bank name + current
                        balance restated. */}
                    <div className="px-5 py-2 bg-emerald-50 border-b-2 border-emerald-300 flex items-center justify-between gap-3" data-testid={`bank-viewing-banner-${b.account_id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold whitespace-nowrap">{t('currentlyViewing') || 'Currently viewing'}:</span>
                        <span className="text-sm font-bold text-emerald-900 truncate">{b.name}{b.bank_name ? ` · ${b.bank_name}` : ''}</span>
                      </div>
                      <span className={`text-sm font-mono font-bold ${currentBalance >= 0 ? 'text-emerald-900' : 'text-red-700'}`}>
                        &euro;{currentBalance.toFixed(2)}
                      </span>
                    </div>
                    {/* Header */}
                    <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 border border-zinc-200 bg-zinc-50 flex items-center justify-center flex-shrink-0">
                          <Bank size={18} className="text-zinc-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-base font-bold truncate flex items-center gap-2">
                            {b.name}
                            {/* Phase 232 — Visual badge so you can see at a glance
                                whether this account contributes to the global summary. */}
                            {b.include_in_summary === false && (
                              <span
                                className="inline-flex items-center text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-zinc-100 text-zinc-500 border border-zinc-300"
                                title={t('excludedFromSummary') || 'Excluded from summary'}
                                data-testid={`bank-excluded-badge-${b.account_id}`}
                              >
                                ⊘ {t('excludedFromSummary') || 'Excluded'}
                              </span>
                            )}
                          </div>
                          {b.bank_name && <div className="text-xs text-zinc-500 truncate">{b.bank_name}</div>}
                          {b.iban && <div className="text-[10px] text-zinc-400 font-mono truncate">IBAN: {b.iban}</div>}
                        </div>
                      </div>
                      <div className="flex items-start gap-4 flex-shrink-0">
                        <div className="text-right">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{t('currentBalance')}</div>
                          <div className={`text-xl font-mono font-bold ${currentBalance >= 0 ? 'text-zinc-900' : 'text-red-700'}`} data-testid={`bank-${b.account_id}-balance`}>&euro;{currentBalance.toFixed(2)}</div>
                          <div className="text-[10px] text-zinc-400 font-mono mt-0.5" title={t('openingBalance') + ' + ' + t('bankIncome') + ' + ' + t('transfers') + ' In − ' + t('expenses') + ' − ' + t('transfers') + ' Out'}>
                            {t('openingBalance')}: &euro;{(b.opening_balance || 0).toFixed(2)} &nbsp;+&euro;{(totalIncomeBank + totalTransfersIn).toFixed(2)} / &minus;&euro;{(totalPaymentsBank + totalTransfersOut).toFixed(2)}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={toggleDebugMode}
                            className={`p-2 text-xs uppercase font-semibold border ${debugMode ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-zinc-500 border-zinc-300 hover:bg-zinc-50'}`}
                            title={t('debugInspector') || 'Debug Inspector'}
                            data-testid={`debug-toggle-${b.account_id}`}
                          >
                            🔍 {debugMode ? (t('debugOn') || 'Debug ON') : (t('debug') || 'Debug')}
                          </button>
                          <button onClick={() => openBankEdit(b)} className="p-2 hover:bg-zinc-100" data-testid={`edit-bank-${b.account_id}`}><PencilSimple size={16} /></button>
                          <button onClick={() => confirmDelete('bank', b.account_id)} className="p-2 hover:bg-red-50 text-red-500" data-testid={`delete-bank-${b.account_id}`}><Trash size={16} /></button>
                        </div>
                      </div>
                    </div>

                    {/* Inner tabs — Phase 96: Income + Payments + Statement. Payments own their own CRUD. */}
                    {/* Phase 466 — Debug Inspector + filter-hides-rows
                        warning. If date range, income-type filter, or
                        income-source filter are dropping rows that DO
                        exist for this account, surface a clear amber
                        banner so the user immediately understands
                        nothing is "missing" — only filtered. */}
                    {(() => {
                      const totalForThisBank = allIncome.filter(e => e.source !== 'cash' && e.bank_account_id === b.account_id).length
                        + allBankPayments.filter(p => p.bank_account_id === b.account_id).length;
                      const visibleAfterRange = bankIncomeRowsAllTypes.length + bankPaymentRows.length;
                      const visibleAfterAll = bankIncomeRows.length + bankPaymentRows.length;
                      const hiddenByRange = (totalForThisBank - visibleAfterRange);
                      const hiddenByType = (visibleAfterRange - visibleAfterAll);
                      const totalHidden = hiddenByRange + hiddenByType;
                      if (totalHidden <= 0) return null;
                      const parts = [];
                      if (hiddenByRange > 0) parts.push(`${hiddenByRange} ${t('hiddenByDateRange') || 'by date range'}`);
                      if (hiddenByType > 0) parts.push(`${hiddenByType} ${t('hiddenByTypeFilter') || 'by income type filter'}`);
                      return (
                        <div className="px-5 py-2 bg-amber-50 border-b border-amber-300 flex items-center justify-between gap-3" data-testid={`bank-filter-warning-${b.account_id}`}>
                          <span className="text-xs text-amber-900">
                            ⚠ {totalHidden} {t('rowsHiddenByFilters') || 'rows hidden by filters'}: {parts.join(' · ')}
                          </span>
                          <div className="flex items-center gap-2">
                            <button onClick={() => { setBankRange(b.account_id, { from: '', to: '' }); setIncomeTypeFilter('all'); }} className="text-[10px] uppercase tracking-wider font-semibold text-amber-900 hover:underline" data-testid={`bank-clear-filters-${b.account_id}`}>
                              {t('clearFilters') || 'Clear filters'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="flex border-b border-zinc-200 bg-zinc-50">
                      {[
                        { key: 'income', label: t('bankIncome') },
                        { key: 'payments', label: t('payments') },
                        { key: 'statement', label: t('statement') },
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => setBankTab(b.account_id, tab.key)}
                          className={`px-4 py-2 text-xs font-medium transition-colors ${innerTab === tab.key ? 'bg-white text-zinc-900 border-b-2 -mb-px border-zinc-900' : 'text-zinc-500 hover:text-zinc-800'}`}
                          data-testid={`bank-${b.account_id}-tab-${tab.key}`}
                        >{tab.label}</button>
                      ))}
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      {/* Phase 98 — Per-bank date range filter (applies to Income, Payments, Statement) */}
                      <div className="flex items-end gap-2 flex-wrap mb-3 pb-3 border-b border-zinc-100" data-testid={`bank-${b.account_id}-range-bar`}>
                        <div>
                          <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('fromDate')}</Label>
                          <DatePickerDMY value={range.from} onChange={v => setBankRange(b.account_id, { from: v })} className="h-8 w-32 font-mono text-xs" data-testid={`bank-${b.account_id}-range-from`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('toDate')}</Label>
                          <DatePickerDMY value={range.to} onChange={v => setBankRange(b.account_id, { to: v })} className="h-8 w-32 font-mono text-xs" data-testid={`bank-${b.account_id}-range-to`} />
                        </div>
                        {(range.from || range.to) && (
                          <Button variant="outline" size="sm" onClick={() => setBankRange(b.account_id, { from: '', to: '' })} className="rounded-none h-8 text-xs gap-1" data-testid={`bank-${b.account_id}-range-clear`}>
                            <Funnel size={12} />{t('clearFilter')}
                          </Button>
                        )}
                      </div>

                      {innerTab === 'income' && (
                        <>
                          {/* Phase 218 — Transfer button is always available
                              next to Add Income / Add Payment so the user
                              never has to switch tabs to record one. */}
                          <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
                            {/* Phase 221 — Income type filter chips */}
                            <div className="flex items-center gap-1.5 flex-wrap" data-testid="income-type-filter">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mr-1">{t('filterByIncomeType') || 'Type'}:</span>
                              <button
                                onClick={() => setIncomeTypeFilter('all')}
                                className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${incomeTypeFilter === 'all' ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                                data-testid="income-type-filter-all"
                              >
                                {t('all') || 'All'}
                              </button>
                              {/* Phase 729 — Neutral monochrome filter chips to match
                                  the Finance → Invoices table aesthetic. Color is
                                  reserved for status/data, not filters. */}
                              {INCOME_TYPES.map(it => (
                                <button
                                  key={it.key}
                                  onClick={() => setIncomeTypeFilter(it.key)}
                                  className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${incomeTypeFilter === it.key ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                                  data-testid={`income-type-filter-${it.key}`}
                                >
                                  {t(it.badgeKey)}
                                </button>
                              ))}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button onClick={() => openTransferAdd(b.account_id)} className="rounded-none bg-sky-700 text-xs h-8" data-testid={`add-transfer-from-income-${b.account_id}`}><Plus size={14} className="mr-1" /> {t('newTransfer')}</Button>
                              <Button onClick={() => { openIncomeAdd(); setIncomeForm(f => ({ ...f, source: 'bank', bank_account_id: b.account_id })); }} className="rounded-none bg-emerald-700 text-xs h-8" data-testid={`add-income-bank-${b.account_id}`}><Plus size={14} className="mr-1" /> {t('addIncome')}</Button>
                            </div>
                          </div>
                          <div className="border border-zinc-200 overflow-x-auto">
                            {bankIncomeRows.length === 0 ? (
                              <div className="p-8 text-center text-zinc-400 text-sm">{t('noIncome')}</div>
                            ) : (
                              <table className="excel-table" data-testid={`bank-${b.account_id}-income-table`}>
                                <thead><tr>
                                  <th>{t('date')}</th>
                                  <th>{t('client') || 'Client Name'}</th>
                                  {/* Phase 1026c — Allocation column removed.
                                      Bank Accounts is now a pure reference log;
                                      bank income is no longer linked to
                                      customer invoices. Legacy
                                      `invoice_allocations` on existing rows
                                      is preserved in the DB but not surfaced. */}
                                  <th className="text-right">{t('total')} (&euro;)</th>
                                  <th className="text-right">{t('vatAmount')} (&euro;)</th>
                                  <th className="text-right border-r-2 border-zinc-300">{t('netAmount')} (&euro;)</th>
                                  {/* Phase 519 — Deductions column hidden per
                                      user request. Underlying 12.5%/10%/10%
                                      math is preserved at the data layer. */}
                                  <th className="text-right bg-blue-50/60">{t('finalNet')} (&euro;)</th>
                                  <th className="w-20">{t('edit')}</th>
                                </tr></thead>
                                <tbody>
                                  {sortDesc(bankIncomeRows).map(e => {
                                    // Phase 93 — row-level deductions = 12.5% + reserve + social (VAT is already out of Net)
                                    const rowDeductions = (e.allocation_12_5 || 0) + (e.reserve_10_amount || 0) + (e.social_insurance_10 || 0);
                                    // Phase 223 — Client Name column. For client_payment rows show
                                    // the resolved client name; otherwise fall back to the income
                                    // type label (BANK REFUND, INTEREST, etc.) so the column is
                                    // never empty.
                                    const typeKey = e.income_type || (e.client_id ? 'client_payment' : 'other');
                                    const meta = incomeTypeMeta(typeKey);
                                    const isClientPay = typeKey === 'client_payment';
                                    return (
                                    <tr key={e.income_id} data-testid={`bank-income-row-${e.income_id}`}>
                                      <td className="font-mono text-sm">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span>{fmtDate(e.date)}</span>
                                          {/* Phase 220 — Synced-with-Building badge (only label, no badge text duplication) */}
                                          {(e.synced_building_payment_id || e.building_id) && (
                                            <span
                                              className="inline-flex items-center text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 bg-sky-100 text-sky-800 border border-sky-200"
                                              title={`${t('syncedWithBuilding') || 'Synced with Building Management'}${e.synced_building_payment_id ? ' · ' + e.synced_building_payment_id : ''}`}
                                              data-testid={`income-sync-badge-${e.income_id}`}
                                            >
                                              ⇄ B
                                            </span>
                                          )}
                                          {/* Phase 523 — Internal Cash badge (does not affect bank balance). */}
                                          {e.affects_treasury === false && (
                                            <span
                                              className="inline-flex items-center text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 bg-amber-500 text-white border border-amber-600"
                                              title={t('treasuryNoHint') || 'Internal Cash — excluded from bank account balance'}
                                              data-testid={`income-internal-badge-${e.income_id}`}
                                            >
                                              {t('badgeInternalCash') || 'Internal'}
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="text-sm">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          {isClientPay && e.client_name ? (
                                            <span className="font-semibold text-zinc-800" data-testid={`income-client-${e.income_id}`}>{e.client_name}</span>
                                          ) : isClientPay ? (
                                            <span className="text-zinc-400 italic">{t('noClient') || '—'}</span>
                                          ) : (
                                            <span
                                              className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${meta.color}`}
                                              title={t(meta.labelKey)}
                                              data-testid={`income-type-badge-${e.income_id}`}
                                            >
                                              {t(meta.badgeKey)}
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      {/* Phase 1026c — Allocation cell removed
                                          from Bank Income (reference-only). */}
                                      <td className="font-mono text-right text-emerald-700 font-semibold">&euro;{e.amount.toFixed(2)}</td>
                                      <td className="font-mono text-right text-sm text-amber-700">&euro;{(e.vat_amount || 0).toFixed(2)}</td>
                                      <td className="font-mono text-right text-sm border-r-2 border-zinc-300">&euro;{(e.net_amount || 0).toFixed(2)}</td>
                                      {/* Phase 519 — Deductions cell hidden. */}
                                      <td className="font-mono text-right text-sm font-bold text-blue-700 bg-blue-50/60">&euro;{(e.final_net || 0).toFixed(2)}</td>
                                      <td>
                                        <div className="flex gap-1">
                                          {/* Phase 1026c — "Apply allocation" link
                                              icon removed (bank is now a
                                              standalone reference log). */}
                                          <button onClick={() => openIncomeEdit(e)} className="p-1.5 hover:bg-zinc-100" data-testid={`edit-income-${e.income_id}`}><PencilSimple size={16} /></button>
                                          <button onClick={() => confirmDelete('income', e.income_id)} className="p-1.5 hover:bg-red-50 text-red-500" data-testid={`delete-income-${e.income_id}`}><Trash size={16} /></button>
                                        </div>
                                      </td>
                                    </tr>
                                  );})}
                                  {(() => {
                                    const tTotal = bankIncomeRows.reduce((s, e) => s + (e.amount || 0), 0);
                                    const tVat = bankIncomeRows.reduce((s, e) => s + (e.vat_amount || 0), 0);
                                    const tNet = bankIncomeRows.reduce((s, e) => s + (e.net_amount || 0), 0);
                                    const tDed = bankIncomeRows.reduce((s, e) => s + (e.allocation_12_5 || 0) + (e.reserve_10_amount || 0) + (e.social_insurance_10 || 0), 0);
                                    const tFinal = bankIncomeRows.reduce((s, e) => s + (e.final_net || 0), 0);
                                    return (
                                      <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300" data-testid={`bank-${b.account_id}-income-totals`}>
                                        <td className="text-sm text-right">{t('total').toUpperCase()}</td>
                                        <td></td>
                                        {/* Phase 1026c — allocation totals cell removed */}
                                        <td className="font-mono text-right text-emerald-800">&euro;{tTotal.toFixed(2)}</td>
                                        <td className="font-mono text-right text-sm text-amber-800">&euro;{tVat.toFixed(2)}</td>
                                        <td className="font-mono text-right text-sm border-r-2 border-zinc-300">&euro;{tNet.toFixed(2)}</td>
                                        <td className="font-mono text-right text-sm text-red-800 border-r-2 border-zinc-300">&euro;{tDed.toFixed(2)}</td>
                                        <td className="font-mono text-right text-sm font-bold text-blue-800 bg-blue-50">&euro;{tFinal.toFixed(2)}</td>
                                        <td></td>
                                      </tr>
                                    );
                                  })()}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </>
                      )}

                      {innerTab === 'payments' && (
                        <>
                          <div className="flex justify-end mb-3 gap-2">
                            <Button onClick={() => openTransferAdd(b.account_id)} className="rounded-none bg-sky-700 text-xs h-8" data-testid={`add-transfer-from-payments-${b.account_id}`}><Plus size={14} className="mr-1" /> {t('newTransfer')}</Button>
                            <Button onClick={() => openPaymentAdd(b.account_id)} className="rounded-none bg-red-700 text-xs h-8" data-testid={`add-payment-bank-${b.account_id}`}><Plus size={14} className="mr-1" /> {t('addPayment')}</Button>
                          </div>
                          <div className="border border-zinc-200 overflow-x-auto">
                            {bankPaymentRows.length === 0 ? (
                              <div className="p-8 text-center text-zinc-400 text-sm">{t('noPayments')}</div>
                            ) : (
                              <table className="excel-table" data-testid={`bank-${b.account_id}-payments-table`}>
                                <thead><tr>
                                  <th>{t('date')}</th>
                                  <th>{t('categoryOrSupplier')}</th>
                                  <th>{t('linkedBill') || 'Linked Bill'}</th>
                                  <th className="w-32">{t('paidFrom')}</th>
                                  <th className="text-right">{t('amount')} (&euro;)</th>
                                  <th>{t('notes')}</th>
                                  <th className="w-20">{t('edit')}</th>
                                </tr></thead>
                                <tbody>
                                  {sortDesc(bankPaymentRows).map(p => {
                                    const lbl = p.supplier_name
                                      || (language === 'el' ? (p.category_name_el || p.category_name_en) : (p.category_name_en || p.category_name_el))
                                      || '—';
                                    const isSup = !!p.supplier_id;
                                    const pf = p.paid_from || 'final_balance';
                                    return (
                                      <tr key={p.payment_id} data-testid={`bank-payment-row-${p.payment_id}`}>
                                        <td className="font-mono text-sm">{fmtDate(p.date)}</td>
                                        <td className="text-sm">
                                          <span className={`px-2 py-0.5 border text-xs ${isSup ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-zinc-200 bg-zinc-50 text-zinc-700'}`}>
                                            {isSup ? `${t('supplier')}: ${lbl}` : lbl}
                                          </span>
                                        </td>
                                        <td className="text-xs">
                                          {p.bill_id ? (
                                            <span className="inline-flex items-center gap-1.5 flex-wrap">
                                              <span className="font-semibold text-zinc-700">{p.bill_label || p.bill_id}</span>
                                              <span
                                                className="inline-flex items-center text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200"
                                                title={t('syncedToBills') || 'Synced to Bills'}
                                                data-testid={`payment-bill-sync-badge-${p.payment_id}`}
                                              >
                                                ⇄ {t('syncedToBills') || 'Synced to Bills'}
                                              </span>
                                            </span>
                                          ) : <span className="text-zinc-300">—</span>}
                                        </td>
                                        <td className="text-xs">
                                          <span className={`px-2 py-0.5 border ${paidFromBadgeClass(pf)}`} data-testid={`bank-payment-paid-from-${p.payment_id}`}>
                                            {paidFromLabel(pf)}
                                          </span>
                                        </td>
                                        <td className="font-mono text-right text-sm font-semibold text-red-700">&euro;{(p.amount || 0).toFixed(2)}</td>
                                        <td className="text-xs text-zinc-500 truncate max-w-xs">{p.notes || '—'}</td>
                                        <td>
                                          <div className="flex gap-1">
                                            <button onClick={() => openPaymentEdit(p)} className="p-1.5 hover:bg-zinc-100" data-testid={`edit-payment-${p.payment_id}`}><PencilSimple size={16} /></button>
                                            <button onClick={() => confirmDelete('bank_payment', p.payment_id, b.account_id)} className="p-1.5 hover:bg-red-50 text-red-500" data-testid={`delete-payment-${p.payment_id}`}><Trash size={16} /></button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  <tr className="bg-zinc-50 font-semibold border-t-2 border-zinc-300" data-testid={`bank-${b.account_id}-payments-totals`}>
                                    <td colSpan={4} className="text-sm text-right">{t('total').toUpperCase()}</td>
                                    <td className="font-mono text-right text-red-800">&euro;{bankPaymentRows.reduce((s, p) => s + (p.amount || 0), 0).toFixed(2)}</td>
                                    <td colSpan={2} className="text-[10px] text-zinc-400 font-mono">{bankPaymentRows.length} {bankPaymentRows.length === 1 ? t('entry') : t('entries')}</td>
                                  </tr>
                                </tbody>
                              </table>
                            )}
                          </div>
                        </>
                      )}


                      {innerTab === 'statement' && (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center gap-3 flex-wrap">
                            {/* Phase 218 — Statement type filter chips. */}
                            {(() => {
                              const stmtFilter = bankStmtFilters[b.account_id] || 'all';
                              const chips = [
                                { key: 'all', label: t('all') || 'All' },
                                { key: 'income', label: t('bankIncome') || 'Income' },
                                { key: 'payments', label: t('payments') || 'Payments' },
                                { key: 'transfers', label: t('transfers') || 'Transfers' },
                              ];
                              return (
                                <div className="inline-flex border border-zinc-200" data-testid={`bank-${b.account_id}-stmt-filter`}>
                                  {chips.map(c => (
                                    <button
                                      key={c.key}
                                      onClick={() => setBankStmtFilter(b.account_id, c.key)}
                                      className={`px-3 py-1 text-[11px] font-semibold uppercase tracking-wider border-r border-zinc-200 last:border-r-0 transition-colors ${stmtFilter === c.key ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                                      data-testid={`bank-${b.account_id}-stmt-filter-${c.key}`}
                                    >{c.label}</button>
                                  ))}
                                </div>
                              );
                            })()}
                            <Button onClick={() => openTransferAdd(b.account_id)} className="rounded-none bg-sky-700 text-xs h-8" data-testid={`add-transfer-bank-${b.account_id}`}><Plus size={14} className="mr-1" /> {t('newTransfer')}</Button>
                          </div>
                        <div className="border border-zinc-200 overflow-x-auto">
                          <table className="excel-table" data-testid={`bank-${b.account_id}-statement-table`}>
                            <thead><tr>
                              <th>{t('date')}</th>
                              <th>{t('client') || 'Client / Source'}</th>
                              <th>{t('incomeDescription') || 'Description'}</th>
                              <th className="text-right">{t('total') || 'Gross'} (&euro;)</th>
                              <th className="text-right">{t('vatAmount') || 'VAT'} (&euro;)</th>
                              <th className="text-right">{t('netAmount') || 'Net'} (&euro;)</th>
                              {/* Phase 519 — Deductions column removed at user
                                  request. The 12.5%/10%/10% breakdown still
                                  exists at the data layer for tax accounting,
                                  but the visual "Deductions" column was
                                  causing confusion in Bank Account reads. */}
                              <th className="text-right">{t('finalNet') || 'Final Net'} (&euro;)</th>
                              <th className="text-right">{t('runningBalance')} (&euro;)</th>
                              <th className="w-24 text-center">{t('actions') || 'Actions'}</th>
                            </tr></thead>
                            <tbody>
                              {stmtRows.length === 0 ? (
                                <tr><td colSpan={9} className="p-6 text-center text-zinc-400 text-sm">{t('noBankTransactions')}</td></tr>
                              ) : (
                              (() => {
                                // Phase 218 — Apply the per-bank Statement
                                // type filter just before render. Opening
                                // row stays anchored regardless. Running
                                // balance is intentionally NOT recomputed
                                // — filtering is a presentation concern;
                                // the math behind each row is still the
                                // true cumulative balance through that
                                // moment in time.
                                const stmtFilter = bankStmtFilters[b.account_id] || 'all';
                                const filtered = stmtDisplay.filter(r => {
                                  if (r.isOpening) return true;
                                  if (stmtFilter === 'all') return true;
                                  if (stmtFilter === 'income') return r.source === 'income';
                                  if (stmtFilter === 'payments') return r.source === 'payment' || r.source === 'expense';
                                  if (stmtFilter === 'transfers') return r.source === 'transfer';
                                  return true;
                                });
                                if (filtered.filter(r => !r.isOpening).length === 0) {
                                  return (<tr><td colSpan={10} className="p-6 text-center text-zinc-400 text-sm">{t('noBankTransactions')}</td></tr>);
                                }
                                return (
                                <>
                                  {filtered.map(r => (
                                    r.isOpening ? (
                                      <tr key={r.id} className={`${r.isBroughtForward ? 'bg-amber-50 border-amber-200' : 'bg-zinc-50 border-zinc-300'} border-t-2`} data-testid={`bank-${b.account_id}-opening-row`}>
                                        <td className="font-mono text-sm text-zinc-500">{r.date ? fmtDate(r.date) : '—'}</td>
                                        <td className="text-xs">
                                          <span className={`px-1.5 py-0.5 border text-[10px] ${r.isBroughtForward ? 'border-amber-300 bg-amber-100 text-amber-800' : 'border-zinc-300 bg-zinc-100 text-zinc-600'}`} data-testid={`bank-stmt-source-${r.id}`}>
                                            {r.isBroughtForward ? (t('broughtForward') || 'B/F') : t('openingBalance')}
                                          </span>
                                        </td>
                                        <td className="text-sm font-semibold text-zinc-700">
                                          {r.isBroughtForward
                                            ? `${t('balanceBroughtForward') || 'Balance Brought Forward'} (${t('asOf') || 'as of'} ${fmtDate(r.date)})`
                                            : t('openingBalance')}
                                        </td>
                                        <td colSpan={4}></td>
                                        <td className={`font-mono text-right text-sm font-bold ${r.running >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>&euro;{r.running.toFixed(2)}</td>
                                        <td></td>
                                      </tr>
                                    ) : (
                                      <tr key={r.id} data-testid={`bank-${b.account_id}-stmt-row-${r.id}`}>
                                        <td className="font-mono text-sm">{fmtDate(r.date)}</td>
                                        <td className="text-xs">
                                          <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className={`px-1.5 py-0.5 border text-[10px] ${r.source === 'transfer' ? 'border-sky-200 bg-sky-50 text-sky-700' : r.source === 'payment' || r.source === 'expense' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`} data-testid={`bank-stmt-source-${r.id}`}>
                                              {r.sourceLabel || (r.source === 'transfer' ? t('internalTransfer') : r.source === 'payment' ? t('payment') : r.source === 'expense' ? t('expense') : t('income'))}
                                            </span>
                                            {/* Phase 223 — Synced badge for income rows linked to Building Mgmt */}
                                            {r.synced_with_building && (
                                              <span
                                                className="inline-flex items-center text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 bg-sky-100 text-sky-800 border border-sky-200"
                                                title={t('syncedWithBuilding') || 'Synced with Building Management'}
                                                data-testid={`stmt-synced-badge-${r.id}`}
                                              >
                                                ⇄ {t('synced') || 'Synced'}
                                              </span>
                                            )}
                                            {/* Phase 465 — Soft "Suspected
                                                Duplicate" badge. Both rows
                                                in the duplicate group stay
                                                visible; this only flags
                                                them for human review. */}
                                            {r.suspected_duplicate && (
                                              <span
                                                className="inline-flex items-center text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 bg-amber-100 text-amber-800 border border-amber-300"
                                                title={`${t('suspectedDuplicate') || 'Suspected duplicate'} · ${r.created_at ? new Date(r.created_at).toLocaleString() : ''} · ${r.created_by || ''}`}
                                                data-testid={`stmt-duplicate-badge-${r.id}`}
                                              >
                                                ⚠ {t('suspectedDuplicate') || 'Suspected Duplicate'}
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                        <td className="text-sm">
                                          {r.desc || '—'}
                                          {/* Phase 466 — Debug Inspector strip. Shows the raw record id,
                                              the source module, sync linkage, audit timestamps and the
                                              bank_account_id, so the user can prove a row exists and where. */}
                                          {debugMode && (
                                            <div className="mt-1 text-[10px] text-indigo-700 font-mono space-x-2" data-testid={`stmt-debug-${r.id}`}>
                                              <span className="bg-indigo-50 border border-indigo-200 px-1 py-0.5">id:{r.id}</span>
                                              <span className="bg-indigo-50 border border-indigo-200 px-1 py-0.5">src:{r.source}</span>
                                              {r._raw?.bank_account_id && <span className="bg-indigo-50 border border-indigo-200 px-1 py-0.5">bank:{r._raw.bank_account_id}</span>}
                                              {r._raw?.synced_payment_id && <span className="bg-emerald-50 border border-emerald-200 px-1 py-0.5">↔clpay:{r._raw.synced_payment_id}</span>}
                                              {r._raw?.synced_building_payment_id && <span className="bg-sky-50 border border-sky-200 px-1 py-0.5">↔bldg:{r._raw.synced_building_payment_id}</span>}
                                              {r._raw?.is_auto_imported && <span className="bg-amber-50 border border-amber-200 px-1 py-0.5">auto-imported</span>}
                                              {r.created_at && <span className="text-zinc-500">created {new Date(r.created_at).toLocaleString()}</span>}
                                              {r.created_by && <span className="text-zinc-500">by {r.created_by}</span>}
                                            </div>
                                          )}
                                        </td>
                                        {r.source === 'income' ? (
                                          <>
                                            <td className="font-mono text-right text-sm text-emerald-700 font-semibold">&euro;{(r.gross || 0).toFixed(2)}</td>
                                            <td className="font-mono text-right text-xs text-amber-700">&euro;{(r.vat || 0).toFixed(2)}</td>
                                            <td className="font-mono text-right text-xs">&euro;{(r.net || 0).toFixed(2)}</td>
                                            {/* Phase 519 — Deductions cell removed. */}
                                            <td className="font-mono text-right text-xs font-bold text-blue-700 bg-blue-50/40">&euro;{(r.final_net || 0).toFixed(2)}</td>
                                          </>
                                        ) : (
                                          <>
                                            <td className={`font-mono text-right text-sm ${r.amt < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{r.amt !== 0 ? `${r.amt < 0 ? '−' : ''}€${Math.abs(r.amt).toFixed(2)}` : ''}</td>
                                            <td className="text-zinc-300 text-right">—</td>
                                            <td className="text-zinc-300 text-right">—</td>
                                            <td className="text-zinc-300 text-right">—</td>
                                          </>
                                        )}
                                        <td className={`font-mono text-right text-sm font-semibold ${r.running >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>&euro;{r.running.toFixed(2)}</td>
                                        <td className="text-center">
                                          <div className="flex items-center justify-center gap-0.5">
                                            <button
                                              onClick={() => setStmtViewRow(r)}
                                              className="p-1 text-zinc-400 hover:text-blue-600 hover:bg-blue-50"
                                              title={t('view') || 'View'}
                                              data-testid={`stmt-view-${r.id}`}
                                            >
                                              <Eye size={13} />
                                            </button>
                                            <button
                                              onClick={() => openPdfInBrowser(`/company/bank-accounts/${b.account_id}/statement/pdf?row_id=${encodeURIComponent(r.id)}`)}
                                              className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
                                              title={t('pdf') || 'PDF'}
                                              data-testid={`stmt-pdf-${r.id}`}
                                            >
                                              <FilePdf size={13} />
                                            </button>
                                            {/* Phase 231 — Inline Edit/Delete for transfer & payment rows
                                                directly in the Statement so users can correct/remove
                                                without leaving the account view. Income rows continue
                                                to be edited from the Bank Income tab. */}
                                            {r.source === 'transfer' && r._raw && (
                                              <>
                                                <button
                                                  onClick={() => openTransferEdit(r._raw)}
                                                  className="p-1 text-zinc-400 hover:text-sky-700 hover:bg-sky-50"
                                                  title={t('edit') || 'Edit'}
                                                  data-testid={`stmt-edit-transfer-${r.transfer_id}`}
                                                >
                                                  <PencilSimple size={13} />
                                                </button>
                                                <button
                                                  onClick={() => confirmDelete('transfer', r.transfer_id)}
                                                  className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50"
                                                  title={t('delete') || 'Delete'}
                                                  data-testid={`stmt-delete-transfer-${r.transfer_id}`}
                                                >
                                                  <Trash size={13} />
                                                </button>
                                              </>
                                            )}
                                            {r.source === 'payment' && r._raw && (
                                              <>
                                                <button
                                                  onClick={() => openPaymentEdit(r._raw)}
                                                  className="p-1 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100"
                                                  title={t('edit') || 'Edit'}
                                                  data-testid={`stmt-edit-payment-${r._raw.payment_id}`}
                                                >
                                                  <PencilSimple size={13} />
                                                </button>
                                                <button
                                                  onClick={() => confirmDelete('bank_payment', r._raw.payment_id, b.account_id)}
                                                  className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50"
                                                  title={t('delete') || 'Delete'}
                                                  data-testid={`stmt-delete-payment-${r._raw.payment_id}`}
                                                >
                                                  <Trash size={13} />
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )
                                  ))}
                                  {/* Phase 230 — Per-account statement closing balance.
                                      Renamed from "Final Balance" to "Closing Balance" so it
                                      doesn't conflict with the global Final Balance formula. */}
                                  <tr className="bg-zinc-100 border-t-2 border-zinc-300" data-testid={`bank-${b.account_id}-final-row`}>
                                    <td></td>
                                    <td></td>
                                    <td className="text-sm font-semibold">{t('closingBalance')}</td>
                                    <td className="font-mono text-right text-sm text-emerald-700">&euro;{stmtRows.filter(r => !r.isOpening && r.amt > 0).reduce((s, r) => s + r.amt, 0).toFixed(2)}</td>
                                    <td colSpan={4}></td>
                                    <td className={`font-mono text-right text-sm font-bold ${finalRunningBalance >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>&euro;{finalRunningBalance.toFixed(2)}</td>
                                    <td></td>
                                  </tr>
                                </>
                                );
                              })()
                              )}
                            </tbody>
                          </table>
                        </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Cash block — only rendered when Cash is selected in the sub-list */}
              {selectedBankId === 'cash' && (
              <div className="border border-zinc-200 bg-white" data-testid="cash-block">
                <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 border border-green-200 bg-green-50 flex items-center justify-center flex-shrink-0">
                      <CurrencyEur size={18} className="text-green-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-base font-bold">{t('cash')}</div>
                      <div className="text-xs text-zinc-500">{t('cashSource')}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-green-600">{t('cashIncome')}</div>
                    <div className="text-xl font-mono font-bold text-green-700" data-testid="cash-income-total">&euro;{cashIncome.reduce((s, e) => s + e.amount, 0).toFixed(2)}</div>
                  </div>
                </div>

                <div className="flex border-b border-zinc-200 bg-zinc-50">
                  {[
                    { key: 'income', label: t('cashIncome') },
                    { key: 'statement', label: t('statement') },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setCashTab(tab.key)}
                      className={`px-4 py-2 text-xs font-medium transition-colors ${cashTab === tab.key ? 'bg-white text-zinc-900 border-b-2 -mb-px border-zinc-900' : 'text-zinc-500 hover:text-zinc-800'}`}
                      data-testid={`cash-tab-${tab.key}`}
                    >{tab.label}</button>
                  ))}
                </div>

                <div className="p-4">
                  {/* Phase 98 — Cash date range filter */}
                  <div className="flex items-end gap-2 flex-wrap mb-3 pb-3 border-b border-zinc-100" data-testid="cash-range-bar">
                    <div>
                      <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('fromDate')}</Label>
                      <DatePickerDMY value={cashDateRange.from} onChange={v => setCashDateRange(r => ({ ...r, from: v }))} className="h-8 w-32 font-mono text-xs" data-testid="cash-range-from" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('toDate')}</Label>
                      <DatePickerDMY value={cashDateRange.to} onChange={v => setCashDateRange(r => ({ ...r, to: v }))} className="h-8 w-32 font-mono text-xs" data-testid="cash-range-to" />
                    </div>
                    {(cashDateRange.from || cashDateRange.to) && (
                      <Button variant="outline" size="sm" onClick={() => setCashDateRange({ from: '', to: '' })} className="rounded-none h-8 text-xs gap-1" data-testid="cash-range-clear">
                        <Funnel size={12} />{t('clearFilter')}
                      </Button>
                    )}
                  </div>
                  {(() => { /* Phase 98 — filtered cash datasets for this block (unfiltered source). */ return null; })()}
                  {cashTab === 'income' && (() => {
                    const filteredCash = allCashIncome.filter(e => inRange(e.date, cashDateRange.from, cashDateRange.to));
                    return (
                    <>
                      <div className="flex justify-end mb-3">
                        <Button onClick={() => { openIncomeAdd(); setIncomeForm(f => ({ ...f, source: 'cash', bank_account_id: '' })); }} className="rounded-none bg-green-700 text-xs h-8" data-testid="add-cash-income-btn"><Plus size={14} className="mr-1" /> {t('addCashIncome')}</Button>
                      </div>
                      <div className="border border-zinc-200 overflow-x-auto">
                        {filteredCash.length === 0 ? (
                          <div className="p-8 text-center text-zinc-400 text-sm">{t('noCashIncome')}</div>
                        ) : (
                          <table className="excel-table" data-testid="cash-income-table">
                            <thead><tr>
                              <th>{t('date')}</th>
                              <th>{t('incomeDescription')}</th>
                              <th>{t('client')}</th>
                              <th className="text-right">{t('amount')} (&euro;)</th>
                              <th>{t('notes')}</th>
                              <th className="w-20">{t('edit')}</th>
                            </tr></thead>
                            <tbody>
                              {sortDesc(filteredCash).map(e => (
                                <tr key={e.income_id} data-testid={`cash-row-${e.income_id}`}>
                                  <td className="font-mono text-sm">{fmtDate(e.date)}</td>
                                  <td className="text-sm">{e.description}</td>
                                  <td className="text-sm text-zinc-600">{e.client_name || '-'}</td>
                                  <td className="font-mono text-right text-green-700 font-semibold">&euro;{e.amount.toFixed(2)}</td>
                                  <td className="text-sm text-zinc-500">{e.notes || '-'}</td>
                                  <td>
                                    <div className="flex gap-1">
                                      <button onClick={() => openIncomeEdit(e)} className="p-1.5 hover:bg-zinc-100" data-testid={`edit-cash-${e.income_id}`}><PencilSimple size={16} /></button>
                                      <button onClick={() => confirmDelete('income', e.income_id)} className="p-1.5 hover:bg-red-50 text-red-500" data-testid={`delete-cash-${e.income_id}`}><Trash size={16} /></button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              <tr className="bg-zinc-50 font-semibold border-t-2 border-zinc-300">
                                <td colSpan={3} className="text-sm text-right">{t('total')}</td>
                                <td className="font-mono text-right text-green-800">&euro;{filteredCash.reduce((s, e) => s + e.amount, 0).toFixed(2)}</td>
                                <td colSpan={2}></td>
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </div>
                    </>
                    );
                  })()}

                  {cashTab === 'statement' && (() => {
                    // Phase 98 — Cash statement obeys cash date range with pre-range opening balance.
                    const allCashExp = expenses.filter(e => e.source === 'cash');
                    const cashIncFilt = allCashIncome.filter(e => inRange(e.date, cashDateRange.from, cashDateRange.to));
                    const cashExpFilt = allCashExp.filter(e => inRange(e.date, cashDateRange.from, cashDateRange.to));
                    const preFrom = cashDateRange.from;
                    const preIncome = preFrom ? allCashIncome.filter(e => (e.date || '') < preFrom).reduce((s, e) => s + (e.amount || 0), 0) : 0;
                    const preExp = preFrom ? allCashExp.filter(e => (e.date || '') < preFrom).reduce((s, e) => s + (e.amount || 0), 0) : 0;
                    const cashOpening = Math.round((preIncome - preExp) * 100) / 100;
                    const rowsAsc = [
                      ...cashIncFilt.map(e => ({
                        id: `ci-${e.income_id}`,
                        date: e.date,
                        source: 'income',
                        desc: e.client_name || e.description || t('payment'),
                        amt: e.amount,
                        created_at: e.created_at || '',
                      })),
                      ...cashExpFilt.map(e => {
                        const cat = getCatName(e.category_id, e.category_name_en, e.category_name_el);
                        const notes = (e.notes || '').trim();
                        const desc = cat ? (notes ? `${cat} (${notes})` : cat) : (e.description || t('expense'));
                        return { id: `ce-${e.expense_id}`, date: e.date, source: 'expense', desc, amt: -e.amount, created_at: e.created_at || '' };
                      }),
                    ].sort((x, y) => {
                      // Phase 138 — date ASC, then created_at ASC (insertion order), then id ASC for stability.
                      const cmp = (x.date || '').localeCompare(y.date || '');
                      if (cmp !== 0) return cmp;
                      const cc = (x.created_at || '').localeCompare(y.created_at || '');
                      if (cc !== 0) return cc;
                      return (x.id || '').localeCompare(y.id || '');
                    });
                    let run = cashOpening;
                    const rows = rowsAsc.map(r => { run += r.amt; return { ...r, running: run }; });
                    // Phase 216 — Display order respects the global toggle.
                    const isDescCash = sortOrder !== 'asc';
                    const rowsDisplay = [...rows].sort((x, y) => {
                      const cmp = isDescCash
                        ? (y.date || '').localeCompare(x.date || '')
                        : (x.date || '').localeCompare(y.date || '');
                      if (cmp !== 0) return cmp;
                      const cc = isDescCash
                        ? (y.created_at || '').localeCompare(x.created_at || '')
                        : (x.created_at || '').localeCompare(y.created_at || '');
                      if (cc !== 0) return cc;
                      return isDescCash
                        ? (y.id || '').localeCompare(x.id || '')
                        : (x.id || '').localeCompare(y.id || '');
                    });
                    const totalCredit = cashIncFilt.reduce((s, e) => s + e.amount, 0);
                    const totalDebit = cashExpFilt.reduce((s, e) => s + e.amount, 0);
                    return (
                      <div className="border border-zinc-200 overflow-x-auto">
                        <table className="excel-table" data-testid="cash-statement-table">
                          <thead><tr>
                            <th>{t('date')}</th>
                            <th className="w-20">{t('source')}</th>
                            <th>{t('incomeDescription')}</th>
                            <th className="text-right">{t('credit')} (&euro;)</th>
                            <th className="text-right">{t('debit')} (&euro;)</th>
                            <th className="text-right">{t('runningBalance')} (&euro;)</th>
                          </tr></thead>
                          <tbody>
                            {/* Phase 98 — Cash opening row when a From date is set */}
                            {cashDateRange.from && (
                              <tr className="bg-zinc-50 border-b-2 border-zinc-300" data-testid="cash-stmt-opening-row">
                                <td className="font-mono text-sm text-zinc-500">{fmtDate(cashDateRange.from)}</td>
                                <td></td>
                                <td className="text-sm font-semibold text-zinc-600">{t('openingBalance')} ({t('asOf')} {fmtDate(cashDateRange.from)})</td>
                                <td></td>
                                <td></td>
                                <td className="font-mono text-right text-sm font-bold text-zinc-900">&euro;{cashOpening.toFixed(2)}</td>
                              </tr>
                            )}
                            {rows.length === 0 ? (
                              <tr><td colSpan={6} className="p-6 text-center text-zinc-400 text-sm">{t('noBankTransactions')}</td></tr>
                            ) : (
                              <>
                                {[...rowsDisplay].map(r => (
                                  <tr key={r.id} data-testid={`cash-stmt-row-${r.id}`}>
                                    <td className="font-mono text-sm">{fmtDate(r.date)}</td>
                                    <td className="text-xs">
                                      <span className={`px-1.5 py-0.5 border text-[10px] ${r.source === 'expense' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`} data-testid={`cash-stmt-source-${r.id}`}>
                                        {r.source === 'expense' ? t('expense') : t('income')}
                                      </span>
                                    </td>
                                    <td className="text-sm">{r.desc || '-'}</td>
                                    <td className="font-mono text-right text-sm text-emerald-700">{r.amt > 0 ? `€${r.amt.toFixed(2)}` : ''}</td>
                                    <td className="font-mono text-right text-sm text-red-700">{r.amt < 0 ? `€${Math.abs(r.amt).toFixed(2)}` : ''}</td>
                                    <td className={`font-mono text-right text-sm font-semibold ${r.running >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>&euro;{r.running.toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="bg-zinc-100 border-t-2 border-zinc-300" data-testid="cash-stmt-final-row">
                                  <td></td>
                                  <td></td>
                                  <td className="text-sm font-semibold">{t('closingBalance')}</td>
                                  <td className="font-mono text-right text-sm text-emerald-700">&euro;{totalCredit.toFixed(2)}</td>
                                  <td className="font-mono text-right text-sm text-red-700">&euro;{totalDebit.toFixed(2)}</td>
                                  <td className={`font-mono text-right text-sm font-bold ${run >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>&euro;{run.toFixed(2)}</td>
                                </tr>
                              </>
                            )}
                          </tbody>
                        </table>
                        <div className="px-3 py-2 border-t border-zinc-100 bg-zinc-50/60 text-[10px] text-zinc-500" data-testid="cash-reflection-note">
                          {t('bankExpenseReflectionNote')}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              )}
              </div>
            </div>
          </TabsContent>

          {/* TRANSFERS TAB — Phase 134. Global list of internal transfers between bank accounts. */}
          <TabsContent value="transfers" className="mt-4" data-testid="transfers-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold">{t('internalTransfers')}</h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">{t('internalTransfersDesc')}</p>
              </div>
              <Button onClick={() => openTransferAdd()} className="rounded-none h-8 text-xs bg-sky-700" data-testid="add-transfer-btn"><Plus size={14} className="mr-1" /> {t('newTransfer')}</Button>
            </div>
            {transfers.length === 0 ? (
              <div className="border border-zinc-200 bg-white p-10 text-center text-zinc-400 text-sm" data-testid="transfers-empty">{t('noTransfers')}</div>
            ) : (
              <div className="border border-zinc-200 bg-white overflow-x-auto">
                <table className="excel-table" data-testid="transfers-table">
                  <thead><tr>
                    <th>{t('date')}</th>
                    <th>{t('fromAccount')}</th>
                    <th>{t('toAccount')}</th>
                    <th className="text-right">{t('amount')} (&euro;)</th>
                    <th>{t('reference')}</th>
                    <th>{t('notes')}</th>
                    <th className="w-20">{t('actions')}</th>
                  </tr></thead>
                  <tbody>
                    {transfers.map(x => (
                      <tr key={x.transfer_id} data-testid={`transfer-row-${x.transfer_id}`}>
                        <td className="font-mono text-sm">{fmtDate(x.date)}</td>
                        <td className="text-sm">{x.from_account_name || x.from_account_id}</td>
                        <td className="text-sm">{x.to_account_name || x.to_account_id}</td>
                        <td className="font-mono text-right text-sm font-semibold text-sky-700">&euro;{(x.amount || 0).toFixed(2)}</td>
                        <td className="font-mono text-xs text-zinc-500">{x.reference || '—'}</td>
                        <td className="text-xs text-zinc-500 truncate max-w-xs">{x.notes || '—'}</td>
                        <td>
                          <div className="flex gap-1">
                            <button onClick={() => openTransferEdit(x)} className="p-1.5 hover:bg-zinc-100" data-testid={`edit-transfer-${x.transfer_id}`}><PencilSimple size={16} /></button>
                            <button onClick={() => confirmDelete('transfer', x.transfer_id)} className="p-1.5 hover:bg-red-50 text-red-500" data-testid={`delete-transfer-${x.transfer_id}`}><Trash size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-zinc-50 font-semibold border-t-2 border-zinc-300" data-testid="transfers-totals-row">
                      <td colSpan={3} className="text-sm text-right">{t('total').toUpperCase()}</td>
                      <td className="font-mono text-right text-sky-800">&euro;{transfers.reduce((s, x) => s + (x.amount || 0), 0).toFixed(2)}</td>
                      <td colSpan={3} className="text-[10px] text-zinc-400 font-mono">{transfers.length} {transfers.length === 1 ? t('entry') : t('entries')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* EXPENSE CATEGORIES TAB (Phase 85 — master-detail with history) */}
          <TabsContent value="expenseCategories" className="mt-4" data-testid="expense-categories-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold">{t('expenseCategories')}</h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">{t('expenseCategoriesDesc')}</p>
              </div>
              <Button onClick={openCatAdd} className="rounded-none h-8 text-xs" data-testid="add-expense-category-btn"><Plus size={14} className="mr-1" /> {t('addCategory')}</Button>
            </div>

            {categories.length === 0 ? (
              <div className="border border-dashed border-zinc-300 p-10 text-center text-zinc-400 text-sm">{t('noCategories')}</div>
            ) : (
              <div className="flex gap-4">
                {/* LEFT — compact clickable category list */}
                <aside className="w-56 flex-shrink-0 border-r border-zinc-200 pr-3" data-testid="category-master-list">
                  <div className="flex flex-col">
                    {categories.map(c => {
                      const displayName = language === 'el' ? (c.name_el || c.name_en || '—') : (c.name_en || c.name_el || '—');
                      const active = selectedCategoryId === c.category_id;
                      return (
                        <div key={c.category_id} className="group relative">
                          <button
                            onClick={() => setSelectedCategoryId(c.category_id)}
                            className={`w-full text-left px-3 py-1.5 text-xs border-l-2 transition-colors flex items-center gap-1.5 ${active ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold' : 'border-l-transparent text-zinc-600 hover:bg-zinc-50'}`}
                            data-testid={`category-item-${c.category_id}`}
                          >
                            <span className="truncate flex-1">{displayName}</span>
                            {c.bucket && <span className="text-[8px] px-1 py-0.5 border border-amber-200 bg-amber-50 text-amber-700 uppercase tracking-wide" title={bucketLabelOf(c.bucket)}>§</span>}
                          </button>
                          {/* Inline edit/delete actions — revealed on hover or when active */}
                          <div className={`absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5 ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                            <button onClick={(e) => { e.stopPropagation(); openCatEdit(c); }} className="p-1 hover:bg-white rounded" data-testid={`edit-expense-category-${c.category_id}`} title={t('edit')}>
                              <PencilSimple size={12} />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); confirmDelete('category', c.category_id); }} className="p-1 hover:bg-red-50 text-red-500 rounded" data-testid={`delete-expense-category-${c.category_id}`} title={t('delete')}>
                              <Trash size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </aside>

                {/* RIGHT — detail pane (instructions or selected category history) */}
                <div className="flex-1 min-w-0" data-testid="category-detail-pane">
                  {!selectedCategoryId ? (
                    <div className="border border-dashed border-zinc-200 p-10 text-center text-zinc-400 text-sm">
                      {t('selectCategoryToSeeHistory')}
                    </div>
                  ) : (() => {
                    const selected = categories.find(c => c.category_id === selectedCategoryId);
                    if (!selected) return null;
                    const selName = language === 'el' ? (selected.name_el || selected.name_en) : (selected.name_en || selected.name_el);
                    const total = catExpenses.reduce((s, e) => s + (e.amount || 0), 0);
                    return (
                      <div>
                        {/* Header + local From/To filters */}
                        <div className="flex items-end justify-between flex-wrap gap-3 mb-3 pb-3 border-b border-zinc-200">
                          <div>
                            <h4 className="text-base font-semibold">{selName}</h4>
                            <p className="text-[10px] text-zinc-400">{t('expenseHistory')}</p>
                          </div>
                          <div className="flex items-end gap-2 flex-wrap">
                            <div>
                              <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('fromDate')}</Label>
                              <DatePickerDMY value={catFilterFrom} onChange={setCatFilterFrom} className="h-8 w-32 font-mono text-xs" data-testid="cat-filter-from" />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('toDate')}</Label>
                              <DatePickerDMY value={catFilterTo} onChange={setCatFilterTo} className="h-8 w-32 font-mono text-xs" data-testid="cat-filter-to" />
                            </div>
                            {(catFilterFrom || catFilterTo) && (
                              <Button variant="outline" size="sm" onClick={() => { setCatFilterFrom(''); setCatFilterTo(''); }} className="rounded-none h-8 text-xs gap-1" data-testid="cat-filter-clear">
                                <Funnel size={12} />{t('clearFilter')}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Phase 110 — Bucket-aware balance strip (shown only for bucket-linked categories). */}
                        {catLedger.bucket && (
                          <div className="flex items-stretch w-full border border-zinc-200 divide-x divide-zinc-200 mb-3" data-testid="cat-bucket-strip">
                            <div className="flex-1 min-w-[120px] basis-0 px-3 py-1.5">
                              <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">{t('bucket')}</div>
                              <div className="text-xs font-semibold text-amber-700">{bucketLabelOf(catLedger.bucket)}</div>
                            </div>
                            <div className="flex-1 min-w-[120px] basis-0 px-3 py-1.5">
                              <div className="text-[9px] font-semibold uppercase tracking-wider text-emerald-600">{t('totalAllocated') || 'Allocated'}</div>
                              <div className="text-sm font-mono font-bold tabular-nums text-emerald-800">&euro;{(catLedger.allocated || 0).toFixed(2)}</div>
                            </div>
                            <div className="flex-1 min-w-[120px] basis-0 px-3 py-1.5">
                              <div className="text-[9px] font-semibold uppercase tracking-wider text-red-600">{t('totalPaid') || 'Paid'}</div>
                              <div className="text-sm font-mono font-bold tabular-nums text-red-800">&euro;{(catLedger.paid || 0).toFixed(2)}</div>
                            </div>
                            <div className={`flex-1 min-w-[120px] basis-0 px-3 py-1.5 ${(catLedger.balance || 0) >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                              <div className={`text-[9px] font-semibold uppercase tracking-wider ${(catLedger.balance || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{t('availableBalance')}</div>
                              <div className={`text-sm font-mono font-extrabold tabular-nums ${(catLedger.balance || 0) >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>&euro;{(catLedger.balance || 0).toFixed(2)}</div>
                            </div>
                          </div>
                        )}

                        {/* History table — Phase 110: if bucket set, show Allocation+Payment with running balance. Otherwise compact payments-only. */}
                        <div className="border border-zinc-200 overflow-hidden">
                          {catExpensesLoading ? (
                            <div className="p-4 text-center text-zinc-400 text-xs">{t('loading')}…</div>
                          ) : catLedger.bucket ? (
                            catLedger.entries.length === 0 ? (
                              <div className="p-4 text-center text-zinc-400 text-xs">{t('noEntries') || t('noExpenses')}</div>
                            ) : (
                              <div className="max-h-[380px] overflow-y-auto">
                                <table className="excel-table w-full" data-testid="category-ledger-table">
                                  <thead className="sticky top-0 z-10 bg-zinc-50">
                                    <tr>
                                      <th className="w-28">{t('date')}</th>
                                      <th className="w-28">{t('type')}</th>
                                      <th>{t('reference') || t('source')}</th>
                                      <th className="text-right w-28">{t('amount')} (&euro;)</th>
                                      <th className="text-right w-28">{t('runningBalance')} (&euro;)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {catLedger.entries.map(r => (
                                      <tr key={r.id} data-testid={`cat-ledger-row-${r.id}`}>
                                        <td className="font-mono text-xs py-1">{fmtDate(r.date)}</td>
                                        <td className="text-xs py-1">
                                          <span className={`px-1.5 py-0.5 border text-[10px] ${r.type === 'allocation' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                                            {r.type === 'allocation' ? t('allocation') : t('payment')}
                                          </span>
                                        </td>
                                        <td className="text-xs text-zinc-600 py-1 truncate max-w-[280px]">{r.ref}{r.notes ? ` — ${r.notes}` : ''}</td>
                                        <td className={`font-mono text-right text-xs font-semibold py-1 ${r.amount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{r.amount >= 0 ? '+' : '-'}&euro;{Math.abs(r.amount).toFixed(2)}</td>
                                        <td className={`font-mono text-right text-xs font-bold py-1 ${r.running >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>&euro;{r.running.toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )
                          ) : catExpenses.length === 0 ? (
                            <div className="p-4 text-center text-zinc-400 text-xs">{t('noExpenses')}</div>
                          ) : (
                            <div className="max-h-[380px] overflow-y-auto">
                              <table className="excel-table w-full" data-testid="category-history-table">
                                <thead className="sticky top-0 z-10 bg-zinc-50">
                                  <tr>
                                    <th className="w-28">{t('date')}</th>
                                    <th className="w-32">{t('source')}</th>
                                    <th className="text-right w-28">{t('amount')} (&euro;)</th>
                                    <th>{t('notes')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortDesc(catExpenses).map(e => (
                                    <tr key={e.expense_id} data-testid={`cat-history-row-${e.expense_id}`}>
                                      <td className="font-mono text-xs py-1">{fmtDate(e.date)}</td>
                                      <td className="text-xs py-1">
                                        <span className={`px-1.5 py-0.5 border text-[10px] ${e.source === 'bank' ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
                                          {e.source === 'bank' ? (e.bank_account_name || getBankName(e.bank_account_id) || t('bankSource')) : t('cashSource')}
                                        </span>
                                      </td>
                                      <td className="font-mono text-right text-xs font-semibold text-red-700 py-1">&euro;{(e.amount || 0).toFixed(2)}</td>
                                      <td className="text-xs text-zinc-500 py-1 truncate max-w-[360px]">{e.notes || e.description || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot className="sticky bottom-0 z-10 bg-zinc-100 border-t-2 border-zinc-300">
                                  <tr>
                                    <td colSpan={2} className="text-[10px] text-zinc-500 font-mono py-1.5 px-2">
                                      {catExpenses.length} {catExpenses.length === 1 ? t('entry') : t('entries')}
                                    </td>
                                    <td className="font-mono text-right text-sm font-bold text-red-800 py-1.5 px-2" data-testid="cat-history-total">&euro;{total.toFixed(2)}</td>
                                    <td></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </TabsContent>

          {/* SUPPLIERS / PURCHASES TAB (Phase 86) — master-detail */}
          <TabsContent value="suppliers" className="mt-4" data-testid="suppliers-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold">{t('suppliers')}</h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">{t('suppliersDesc')}</p>
              </div>
              <Button onClick={openSupplierAdd} className="rounded-none h-8 text-xs" data-testid="add-supplier-btn"><Plus size={14} className="mr-1" /> {t('addSupplier')}</Button>
            </div>

            <div className="flex gap-4">
              {/* LEFT — supplier list */}
              <aside className="w-56 flex-shrink-0 border-r border-zinc-200 pr-3" data-testid="supplier-master-list">
                {suppliers.length === 0 ? (
                  <div className="text-xs text-zinc-400 px-3 py-6 text-center">{t('noSuppliers')}</div>
                ) : (
                  <div className="flex flex-col">
                    {suppliers.map(s => {
                      const active = selectedSupplierId === s.supplier_id;
                      const bal = s.balance ?? 0;
                      return (
                        <div key={s.supplier_id} className="group relative">
                          <button
                            onClick={() => { setSelectedSupplierId(s.supplier_id); setSupplierTab('invoices'); }}
                            className={`w-full text-left px-3 py-2 text-xs border-l-2 transition-colors ${active ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold' : 'border-l-transparent text-zinc-600 hover:bg-zinc-50'}`}
                            data-testid={`supplier-item-${s.supplier_id}`}
                          >
                            <div className="truncate">{s.name}</div>
                            <div className={`text-[10px] font-mono mt-0.5 ${active ? 'text-zinc-600' : 'text-zinc-400'}`}>&euro;{bal.toFixed(2)}</div>
                          </button>
                          <div className={`absolute right-1 top-1 flex items-center gap-0.5 ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                            <button onClick={(e) => { e.stopPropagation(); openSupplierEdit(s); }} className="p-1 hover:bg-white rounded" data-testid={`edit-supplier-${s.supplier_id}`} title={t('edit')}><PencilSimple size={12} /></button>
                            <button onClick={(e) => { e.stopPropagation(); deleteSupplier(s.supplier_id); }} className="p-1 hover:bg-red-50 text-red-500 rounded" data-testid={`delete-supplier-${s.supplier_id}`} title={t('delete')}><Trash size={12} /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </aside>

              {/* RIGHT — detail pane */}
              <div className="flex-1 min-w-0" data-testid="supplier-detail-pane">
                {!selectedSupplierId ? (
                  <div className="border border-dashed border-zinc-200 p-10 text-center text-zinc-400 text-sm">{t('selectSupplier')}</div>
                ) : (() => {
                  const s = suppliers.find(x => x.supplier_id === selectedSupplierId);
                  if (!s) return null;
                  const isStatement = s.type === 'statement';
                  return (
                    <div>
                      {/* Supplier header */}
                      <div className="flex items-start justify-between gap-4 pb-3 mb-3 border-b border-zinc-200">
                        <div className="min-w-0">
                          <h4 className="text-base font-bold truncate">{s.name}</h4>
                          <p className="text-[11px] text-zinc-500">{t('statementSupplierDesc')}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{t('outstandingBalance')}</div>
                          <div className={`text-xl font-mono font-bold ${(s.balance ?? 0) > 0 ? 'text-red-700' : 'text-zinc-900'}`} data-testid={`supplier-${s.supplier_id}-balance`}>&euro;{(s.balance ?? 0).toFixed(2)}</div>
                          <div className="text-[10px] text-zinc-400 font-mono mt-0.5">
                            {t('invoicesLabel')}: &euro;{(s.total_invoices || 0).toFixed(2)} / {t('paymentsLabel')}: &euro;{(s.total_payments || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>

                      {/* ONE-TIME branch removed in Phase 87 — simple purchases now live under Expenses */}
                      {!isStatement && (
                        <div className="border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">
                          {t('legacyOneTimeSupplierNotice')}
                        </div>
                      )}

                      {/* STATEMENT: Invoices / Payments / Statement — Phase 419:
                          The tab bar is removed; the active section is driven
                          purely by the sidebar (Suppliers / Supplier Payments /
                          Supplier Statements). */}
                      {isStatement && (
                        <>
                          <div className="pt-3">
                            {supplierTab === 'invoices' && (() => {
                              const inRange = (d) => (!invFilter.from || d >= invFilter.from) && (!invFilter.to || d <= invFilter.to);
                              const matchType = (i) => {
                                const t_ = i.invoice_type || 'credit';
                                if (invFilter.type === 'all') return true;
                                if (invFilter.type === 'credit') return t_ === 'credit';
                                if (invFilter.type === 'cash') return t_ === 'cash';
                                return true;
                              };
                              const matchStatus = (i) => {
                                if (invFilter.status === 'all') return true;
                                if (invFilter.status === 'paid') return i.status === 'paid';
                                if (invFilter.status === 'unpaid') return i.status === 'unpaid' || i.status === 'partial';
                                return true;
                              };
                              const filtered = supplierInvoices
                                .filter(i => inRange(i.date || ''))
                                .filter(matchType)
                                .filter(matchStatus);
                              const sumNet = filtered.reduce((s, i) => s + (i.net || 0), 0);
                              const sumVat = filtered.reduce((s, i) => s + (i.vat || 0), 0);
                              const sumTot = filtered.reduce((s, i) => s + (i.total || 0), 0);
                              // Phase 944 — Breakdown for the summary footer.
                              const cntCredit = supplierInvoices.filter(i => (i.invoice_type || 'credit') === 'credit').length;
                              const cntCash = supplierInvoices.filter(i => i.invoice_type === 'cash').length;
                              const cntPaid = supplierInvoices.filter(i => i.status === 'paid').length;
                              const cntUnpaid = supplierInvoices.filter(i => i.status === 'unpaid' || i.status === 'partial').length;
                              return (
                                <>
                                  <div className="flex items-end justify-between mb-3 gap-2 flex-wrap">
                                    <Button onClick={openInvoiceAdd} className="rounded-none bg-red-700 h-8 text-xs" data-testid="add-invoice-btn"><Plus size={14} className="mr-1" /> {t('addInvoice')}</Button>
                                    <div className="flex items-end gap-2 flex-wrap" data-testid="invoice-filters">
                                      <div><Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('fromDate')}</Label><DatePickerDMY value={invFilter.from} onChange={v => setInvFilter(f => ({ ...f, from: v }))} className="h-8 w-32 font-mono text-xs" data-testid="inv-filter-from" /></div>
                                      <div><Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('toDate')}</Label><DatePickerDMY value={invFilter.to} onChange={v => setInvFilter(f => ({ ...f, to: v }))} className="h-8 w-32 font-mono text-xs" data-testid="inv-filter-to" /></div>
                                      {(invFilter.from || invFilter.to) && <Button variant="outline" size="sm" onClick={() => setInvFilter(f => ({ ...f, from: '', to: '' }))} className="rounded-none h-8 text-xs gap-1" data-testid="inv-filter-clear"><Funnel size={12} />{t('clearFilter')}</Button>}
                                    </div>
                                  </div>
                                  {/* Phase 944 — Type + Status filter chips. */}
                                  <div className="flex items-center gap-1 flex-wrap mb-2 text-xs" data-testid="inv-type-status-filters">
                                    {[
                                      { k: 'all',    label: language === 'en' ? 'All'    : 'Όλα',        n: supplierInvoices.length },
                                      { k: 'credit', label: language === 'en' ? 'Credit' : 'Πιστωτικά',  n: cntCredit },
                                      { k: 'cash',   label: language === 'en' ? 'Cash'   : 'Μετρητοίς', n: cntCash },
                                    ].map(o => {
                                      const on = invFilter.type === o.k;
                                      return (
                                        <button
                                          key={o.k} type="button"
                                          onClick={() => setInvFilter(f => ({ ...f, type: o.k }))}
                                          className={`px-2 py-1 border ${on ? 'bg-zinc-900 text-white border-transparent' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'}`}
                                          data-testid={`inv-type-${o.k}`}
                                        >{o.label} ({o.n})</button>
                                      );
                                    })}
                                    <span className="text-zinc-300 mx-1">|</span>
                                    {[
                                      { k: 'all',    label: language === 'en' ? 'All'    : 'Όλα',        n: supplierInvoices.length },
                                      { k: 'paid',   label: language === 'en' ? 'Paid'   : 'Πληρωμένα', n: cntPaid },
                                      { k: 'unpaid', label: language === 'en' ? 'Unpaid' : 'Ανοιχτά',    n: cntUnpaid },
                                    ].map(o => {
                                      const on = invFilter.status === o.k;
                                      return (
                                        <button
                                          key={o.k} type="button"
                                          onClick={() => setInvFilter(f => ({ ...f, status: o.k }))}
                                          className={`px-2 py-1 border ${on ? 'bg-emerald-700 text-white border-transparent' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'}`}
                                          data-testid={`inv-status-${o.k}`}
                                        >{o.label} ({o.n})</button>
                                      );
                                    })}
                                  </div>
                                  <div className="border border-zinc-200 overflow-x-auto">
                                    {filtered.length === 0 ? (
                                      <div className="p-6 text-center text-zinc-400 text-sm">{t('noInvoices')}</div>
                                    ) : (
                                      <table className="excel-table" data-testid="supplier-invoices-table">
                                        <thead><tr>
                                          <th>{t('date')}</th>
                                          <th>{t('invoiceNumber')}</th>
                                          <th className="w-24">{t('invoiceType') || 'Type'}</th>
                                          <th className="w-24">{t('status') || 'Status'}</th>
                                          <th className="text-right">{t('netAmount')} (&euro;)</th>
                                          <th className="text-right">{t('vatAmount')} (&euro;)</th>
                                          <th className="text-right">{t('total')} (&euro;)</th>
                                          <th>{t('notes')}</th>
                                          <th className="w-20">{t('edit')}</th>
                                        </tr></thead>
                                        <tbody>
                                          {sortDesc(filtered).map(i => {
                                            const iType = i.invoice_type || 'credit';
                                            const status = i.status || (iType === 'cash' ? 'paid' : 'unpaid');
                                            const typeChip = iType === 'cash'
                                              ? (language === 'en' ? 'Cash' : 'Μετρητοίς')
                                              : (language === 'en' ? 'Credit' : 'Πιστωτικό');
                                            const statusChip =
                                              status === 'paid' ? (language === 'en' ? 'Paid' : 'Πληρωμένο')
                                              : status === 'partial' ? (language === 'en' ? 'Partial' : 'Μερικώς')
                                              : (language === 'en' ? 'Unpaid' : 'Ανοιχτό');
                                            return (
                                            <tr key={i.invoice_id} data-testid={`invoice-row-${i.invoice_id}`}>
                                              <td className="font-mono text-sm">{fmtDate(i.date)}</td>
                                              <td className="text-sm font-medium">#{i.invoice_number || '—'}</td>
                                              <td>
                                                <span
                                                  className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-semibold ${iType === 'cash' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}
                                                  data-testid={`invoice-type-${i.invoice_id}`}
                                                >{typeChip}</span>
                                              </td>
                                              <td>
                                                <span
                                                  className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 font-semibold ${status === 'paid' ? 'bg-emerald-600 text-white' : status === 'partial' ? 'bg-amber-500 text-white' : 'bg-zinc-200 text-zinc-700'}`}
                                                  data-testid={`invoice-status-${i.invoice_id}`}
                                                >{statusChip}</span>
                                              </td>
                                              <td className="font-mono text-right text-sm">&euro;{(i.net || 0).toFixed(2)}</td>
                                              <td className="font-mono text-right text-sm text-amber-600">&euro;{(i.vat || 0).toFixed(2)}</td>
                                              <td className="font-mono text-right text-sm font-semibold text-red-700">&euro;{(i.total || 0).toFixed(2)}</td>
                                              <td className="text-xs text-zinc-500 truncate max-w-xs">{i.notes || '—'}</td>
                                              <td>
                                                <div className="flex gap-1">
                                                  <button onClick={() => openInvoiceEdit(i)} className="p-1.5 hover:bg-zinc-100" data-testid={`edit-invoice-${i.invoice_id}`}><PencilSimple size={14} /></button>
                                                  <button onClick={() => deleteInvoice(i.invoice_id)} className="p-1.5 hover:bg-red-50 text-red-500" data-testid={`delete-invoice-${i.invoice_id}`}><Trash size={14} /></button>
                                                </div>
                                              </td>
                                            </tr>
                                            );
                                          })}
                                          {/* Phase 88 — Net / VAT / Grand Total footer */}
                                          <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300" data-testid="invoices-total-row">
                                            <td colSpan={4} className="text-sm">{t('total').toUpperCase()}</td>
                                            <td className="font-mono text-right text-sm" data-testid="invoices-total-net">&euro;{sumNet.toFixed(2)}</td>
                                            <td className="font-mono text-right text-sm text-amber-700" data-testid="invoices-total-vat">&euro;{sumVat.toFixed(2)}</td>
                                            <td className="font-mono text-right text-sm font-bold text-red-800" data-testid="invoices-total-grand">&euro;{sumTot.toFixed(2)}</td>
                                            <td colSpan={2} className="text-[10px] text-zinc-400 font-mono">{filtered.length} {filtered.length === 1 ? t('entry') : t('entries')}</td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                </>
                              );
                            })()}

                            {supplierTab === 'payments' && (() => {
                              const inRange = (d) => (!payFilter.from || d >= payFilter.from) && (!payFilter.to || d <= payFilter.to);
                              const filtered = supplierPayments.filter(p => inRange(p.date || ''));
                              const sumAmt = filtered.reduce((s, p) => s + (p.amount || 0), 0);
                              return (
                                <>
                                  <div className="flex items-end justify-between mb-3 gap-2 flex-wrap">
                                    <Button onClick={openPayAdd} className="rounded-none bg-emerald-700 h-8 text-xs" data-testid="add-payment-btn"><Plus size={14} className="mr-1" /> {t('addPayment')}</Button>
                                    <div className="flex items-end gap-2 flex-wrap" data-testid="payment-filters">
                                      <div><Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('fromDate')}</Label><DatePickerDMY value={payFilter.from} onChange={v => setPayFilter(f => ({ ...f, from: v }))} className="h-8 w-32 font-mono text-xs" data-testid="pay-filter-from" /></div>
                                      <div><Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('toDate')}</Label><DatePickerDMY value={payFilter.to} onChange={v => setPayFilter(f => ({ ...f, to: v }))} className="h-8 w-32 font-mono text-xs" data-testid="pay-filter-to" /></div>
                                      {(payFilter.from || payFilter.to) && <Button variant="outline" size="sm" onClick={() => setPayFilter({ from: '', to: '' })} className="rounded-none h-8 text-xs gap-1" data-testid="pay-filter-clear"><Funnel size={12} />{t('clearFilter')}</Button>}
                                    </div>
                                  </div>
                                  <div className="border border-zinc-200 overflow-x-auto">
                                    {filtered.length === 0 ? (
                                      <div className="p-6 text-center text-zinc-400 text-sm">{t('noPayments')}</div>
                                    ) : (
                                      <table className="excel-table" data-testid="supplier-payments-table">
                                        <thead><tr>
                                          <th>{t('date')}</th>
                                          <th>{t('supplier') || 'Supplier'}</th>
                                          <th>{t('reference') || 'Reference'}</th>
                                          <th>{t('paymentMethod')}</th>
                                          <th className="text-right">{t('amount')} (&euro;)</th>
                                          <th>{t('notes')}</th>
                                          <th className="w-20">{t('edit')}</th>
                                        </tr></thead>
                                        <tbody>
                                          {sortDesc(filtered).map(p => (
                                            <tr key={p.payment_id} data-testid={`payment-row-${p.payment_id}`}>
                                              <td className="font-mono text-sm">{fmtDate(p.date)}</td>
                                              <td className="text-sm">{s.name}</td>
                                              <td className="text-sm font-medium">{p.payment_number ? `#${p.payment_number}` : (p.reference || '—')}</td>
                                              <td className="text-xs">
                                                <span className={`px-1.5 py-0.5 border text-[10px] ${p.method === 'bank' ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
                                                  {p.method === 'bank' ? (getBankName(p.bank_account_id) || t('bankSource')) : t('cashSource')}
                                                </span>
                                              </td>
                                              <td className="font-mono text-right text-sm font-semibold text-emerald-700">&euro;{(p.amount || 0).toFixed(2)}</td>
                                              <td className="text-xs text-zinc-500 truncate max-w-xs">{p.notes || '—'}</td>
                                              <td>
                                                <div className="flex gap-1">
                                                  {p.source === 'bank_payment' ? (
                                                    // Bank-originated payment — edit from Bank tab, but delete is routed correctly here.
                                                    <button
                                                      onClick={() => { setActiveTab('banks'); setSelectedBankId(p.bank_account_id); setBankTab(p.bank_account_id, 'payments'); }}
                                                      className="p-1.5 hover:bg-zinc-100 text-zinc-500"
                                                      title={t('editFromBank') || 'Edit from Bank tab'}
                                                      data-testid={`goto-bank-${p.payment_id}`}
                                                    ><ArrowSquareOut size={14} /></button>
                                                  ) : (
                                                    <button onClick={() => openPayEdit(p)} className="p-1.5 hover:bg-zinc-100" data-testid={`edit-payment-${p.payment_id}`}><PencilSimple size={14} /></button>
                                                  )}
                                                  <button onClick={() => deletePay(p)} className="p-1.5 hover:bg-red-50 text-red-500" data-testid={`delete-payment-${p.payment_id}`}><Trash size={14} /></button>
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                          <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300" data-testid="payments-total-row">
                                            <td colSpan={4} className="text-sm">{t('total').toUpperCase()}</td>
                                            <td className="font-mono text-right text-sm font-bold text-emerald-800" data-testid="payments-total-amount">&euro;{sumAmt.toFixed(2)}</td>
                                            <td colSpan={2} className="text-[10px] text-zinc-400 font-mono">{filtered.length} {filtered.length === 1 ? t('entry') : t('entries')}</td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                </>
                              );
                            })()}

                            {supplierTab === 'statement' && (() => {
                              const inRange = (d) => (!stmtFilter.from || d >= stmtFilter.from) && (!stmtFilter.to || d <= stmtFilter.to);
                              const fInv = supplierInvoices.filter(i => inRange(i.date || ''));
                              const fPay = supplierPayments.filter(p => inRange(p.date || ''));
                              // Invoices = debit (we owe), Payments = credit (we paid).
                              const methodLabel = (p) => {
                                if (p.method === 'cash') return t('cashSource');
                                return p.bank_account_name || getBankName(p.bank_account_id) || t('bankSource');
                              };
                              const rows = [
                                ...fInv.map(i => ({
                                  id: `i-${i.invoice_id}`,
                                  date: i.date,
                                  type: 'invoice',
                                  reference: i.invoice_number || '',
                                  desc: i.description || i.notes || t('invoice'),
                                  debit: i.total || 0,
                                  credit: 0,
                                  created_at: i.created_at || '',
                                })),
                                ...fPay.map(p => {
                                  const method = methodLabel(p);
                                  const notes = p.notes ? ` — ${p.notes}` : '';
                                  return {
                                    id: `p-${p.payment_id}`,
                                    date: p.date,
                                    type: 'payment',
                                    reference: p.payment_number || '',
                                    desc: `${t('payment')} (${method})${notes}`,
                                    debit: 0,
                                    credit: p.amount || 0,
                                    created_at: p.created_at || '',
                                  };
                                }),
                              ].sort((x, y) => {
                                // Phase 451 — Statement ALWAYS renders oldest → newest
                                // so the running balance flows naturally from a zero
                                // opening to the current closing balance.
                                const cmp = (x.date || '').localeCompare(y.date || '');
                                if (cmp !== 0) return cmp;
                                const cc = (x.created_at || '').localeCompare(y.created_at || '');
                                if (cc !== 0) return cc;
                                return (x.id || '').localeCompare(y.id || '');
                              });
                              let run = 0;
                              const ledger = rows.map(r => { run += r.debit - r.credit; return { ...r, running: run }; });
                              const totalDebit = fInv.reduce((x, i) => x + (i.total || 0), 0);
                              const totalCredit = fPay.reduce((x, p) => x + (p.amount || 0), 0);
                              const outstanding = totalDebit - totalCredit;
                              return (
                                <>
                                  {/* Phase 451 — Summary totals AT THE TOP of the statement
                                      so the user can read the picture instantly without
                                      scrolling to the footer. Reflects the active date filter. */}
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3" data-testid="supplier-stmt-summary">
                                    <div className="bg-white border border-zinc-200 p-3" data-testid="stmt-total-invoiced">
                                      <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{t('totalInvoiced') || 'Total Invoice Amount'}</p>
                                      <p className="font-mono text-xl font-bold mt-1">€{totalDebit.toFixed(2)}</p>
                                      <p className="text-[10px] text-zinc-400 mt-0.5">{fInv.length} {t('invoicesLabel') || 'invoices'}</p>
                                    </div>
                                    <div className="bg-white border border-emerald-200 p-3" data-testid="stmt-total-payments">
                                      <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{t('totalPayments') || 'Total Payments'}</p>
                                      <p className="font-mono text-xl font-bold text-emerald-700 mt-1">€{totalCredit.toFixed(2)}</p>
                                      <p className="text-[10px] text-zinc-400 mt-0.5">{fPay.length} {t('paymentsLabel') || 'payments'}</p>
                                    </div>
                                    <div className={`bg-white border p-3 ${outstanding > 0 ? 'border-red-200' : 'border-emerald-200'}`} data-testid="stmt-outstanding">
                                      <p className={`text-[10px] font-bold uppercase tracking-wider ${outstanding > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{t('outstandingBalance') || 'Outstanding Balance'}</p>
                                      <p className={`font-mono text-xl font-bold mt-1 ${outstanding > 0 ? 'text-red-700' : 'text-emerald-700'}`}>€{outstanding.toFixed(2)}</p>
                                      <p className="text-[10px] text-zinc-400 mt-0.5">{outstanding > 0 ? (t('weOwe') || 'We owe') : outstanding < 0 ? (t('credit') || 'Credit') : (t('settled') || 'Settled')}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-end justify-end mb-3 gap-2 flex-wrap" data-testid="statement-filters">
                                    <div><Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('fromDate')}</Label><DatePickerDMY value={stmtFilter.from} onChange={v => setStmtFilter(f => ({ ...f, from: v }))} className="h-8 w-32 font-mono text-xs" data-testid="stmt-filter-from" /></div>
                                    <div><Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('toDate')}</Label><DatePickerDMY value={stmtFilter.to} onChange={v => setStmtFilter(f => ({ ...f, to: v }))} className="h-8 w-32 font-mono text-xs" data-testid="stmt-filter-to" /></div>
                                    {(stmtFilter.from || stmtFilter.to) && <Button variant="outline" size="sm" onClick={() => setStmtFilter({ from: '', to: '' })} className="rounded-none h-8 text-xs gap-1" data-testid="stmt-filter-clear"><Funnel size={12} />{t('clearFilter')}</Button>}
                                  </div>
                                  <div className="border border-zinc-200 overflow-x-auto">
                                    <table className="excel-table" data-testid="supplier-statement-table">
                                      <thead><tr>
                                        <th>{t('date') || 'Date'}</th>
                                        <th>{t('type') || 'Type'}</th>
                                        <th>{t('reference') || 'Reference'}</th>
                                        <th>{t('description') || 'Description'}</th>
                                        <th className="text-right">{t('debit') || 'Debit'} (&euro;)</th>
                                        <th className="text-right">{t('credit') || 'Credit'} (&euro;)</th>
                                        <th className="text-right">{t('runningBalance') || 'Running Balance'} (&euro;)</th>
                                      </tr></thead>
                                      <tbody>
                                        {ledger.length === 0 ? (
                                          <tr><td colSpan={7} className="p-6 text-center text-zinc-400 text-sm">{t('noTransactions')}</td></tr>
                                        ) : (
                                          <>
                                            {ledger.map(r => (
                                              <tr key={r.id} data-testid={`supplier-stmt-row-${r.id}`}>
                                                <td className="font-mono text-sm">{fmtDate(r.date)}</td>
                                                <td>
                                                  <span className={`px-1.5 py-0.5 text-[10px] border uppercase tracking-wider font-mono ${r.type === 'invoice' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                                                    {r.type === 'invoice' ? (t('invoice') || 'Invoice') : (t('payment') || 'Payment')}
                                                  </span>
                                                </td>
                                                <td className="font-mono text-xs">{r.reference || '—'}</td>
                                                <td className="text-sm">{r.desc}</td>
                                                <td className="font-mono text-right text-sm text-red-700">{r.debit > 0 ? `€${r.debit.toFixed(2)}` : ''}</td>
                                                <td className="font-mono text-right text-sm text-emerald-700">{r.credit > 0 ? `€${r.credit.toFixed(2)}` : ''}</td>
                                                <td className={`font-mono text-right text-sm font-semibold ${r.running > 0 ? 'text-red-700' : r.running < 0 ? 'text-emerald-700' : 'text-zinc-700'}`}>&euro;{r.running.toFixed(2)}</td>
                                              </tr>
                                            ))}
                                            <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300">
                                              <td colSpan={3}></td>
                                              <td>{t('outstandingBalance')}</td>
                                              <td className="font-mono text-right text-sm text-red-700">&euro;{totalDebit.toFixed(2)}</td>
                                              <td className="font-mono text-right text-sm text-emerald-700">&euro;{totalCredit.toFixed(2)}</td>
                                              <td className={`font-mono text-right text-sm font-bold ${outstanding > 0 ? 'text-red-700' : 'text-zinc-900'}`}>&euro;{outstanding.toFixed(2)}</td>
                                            </tr>
                                          </>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </TabsContent>

          {/* EXPENSES LIST (Phase 89) — standalone one-time purchases page under Purchases */}
          <TabsContent value="expensesList" className="mt-4" data-testid="expenses-list-panel">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">{t('expenses')}</h3>
                  {/* Phase 113 — Purchase Categories is now a lightweight dropdown next to the title (no sidebar, no tabs). */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900 border border-zinc-200 px-2 py-0.5 rounded-none bg-white hover:bg-zinc-50"
                        data-testid="purchase-categories-dropdown-trigger"
                        title={t('managePurchaseCategories')}
                      >
                        <SlidersHorizontal size={12} />
                        <span>{t('purchaseCategories')}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[360px] p-0 rounded-none" align="start" data-testid="purchase-categories-dropdown">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 bg-zinc-50">
                        <div>
                          <div className="text-xs font-semibold">{t('purchaseCategories')}</div>
                          <div className="text-[10px] text-zinc-400">{t('purchaseCategoriesDesc')}</div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => openCatAdd('purchase')}
                          className="rounded-none h-7 text-[11px] px-2"
                          data-testid="add-purchase-category-btn"
                        ><Plus size={12} className="mr-1" />{t('addCategory')}</Button>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {purchaseCategories.length === 0 ? (
                          <div className="p-4 text-center text-[11px] text-zinc-400">{t('noPurchaseCategories')}</div>
                        ) : (
                          <ul className="divide-y divide-zinc-100">
                            {purchaseCategories.map(c => {
                              const count = expenses.filter(e => e.category_id === c.category_id).length;
                              const label = language === 'el' ? (c.name_el || c.name_en) : (c.name_en || c.name_el);
                              return (
                                <li key={c.category_id} className="flex items-center justify-between px-3 py-1.5 hover:bg-zinc-50" data-testid={`purchase-cat-row-${c.category_id}`}>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs truncate">{label}</div>
                                    <div className="text-[10px] text-zinc-400 font-mono">{count} {count === 1 ? t('entry') : t('entries')}</div>
                                  </div>
                                  <div className="flex gap-1 shrink-0">
                                    <button onClick={() => openCatEdit(c, 'purchase')} className="p-1 hover:bg-zinc-200" data-testid={`edit-purchase-cat-${c.category_id}`} title={t('edit')}><PencilSimple size={12} /></button>
                                    <button onClick={() => confirmDelete('purchase_category', c.category_id)} className="p-1 hover:bg-red-50 text-red-500" data-testid={`delete-purchase-cat-${c.category_id}`} title={t('delete')}><Trash size={12} /></button>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <p className="text-[11px] text-zinc-400 mt-0.5">{t('expensesListDesc')}</p>
              </div>
              <div className="flex items-end gap-2 flex-wrap" data-testid="expenses-filter-bar">
                <div>
                  <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('fromDate')}</Label>
                  <DatePickerDMY
                    value={expDraft.from}
                    onChange={(v) => setExpDraft(f => ({ ...f, from: v }))}
                    onKeyDown={onExpFilterKeyDown}
                    className="h-8 w-32 font-mono text-xs"
                    data-testid="expenses-filter-from"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('toDate')}</Label>
                  <DatePickerDMY
                    value={expDraft.to}
                    onChange={(v) => setExpDraft(f => ({ ...f, to: v }))}
                    onKeyDown={onExpFilterKeyDown}
                    className="h-8 w-32 font-mono text-xs"
                    data-testid="expenses-filter-to"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('expenseCategory')}</Label>
                  <Select
                    value={expDraft.category_id || 'all'}
                    onValueChange={(v) => setExpDraft(f => ({ ...f, category_id: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger className="h-8 w-40 text-xs rounded-none" data-testid="expenses-filter-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('allCategories') || 'All Categories'}</SelectItem>
                      {purchaseCategories.map(c => (
                        <SelectItem key={c.category_id} value={c.category_id}>
                          {language === 'el' ? (c.name_el || c.name_en) : (c.name_en || c.name_el)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('vendor') || 'Vendor'}</Label>
                  <Select
                    value={expDraft.vendor_id || 'all'}
                    onValueChange={(v) => setExpDraft(f => ({ ...f, vendor_id: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger className="h-8 w-40 text-xs rounded-none" data-testid="expenses-filter-vendor">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('allVendors') || 'All Vendors'}</SelectItem>
                      {[...expenseVendors].sort((a, b) => a.name.localeCompare(b.name)).map(v => (
                        <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('search') || 'Search'}</Label>
                  <Input
                    type="text"
                    value={expDraft.q}
                    onChange={(e) => setExpDraft(f => ({ ...f, q: e.target.value }))}
                    onKeyDown={onExpFilterKeyDown}
                    placeholder={t('searchExpensesPlaceholder') || 'Description, notes, vendor…'}
                    className="h-8 w-56 text-xs rounded-none"
                    data-testid="expenses-filter-search"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearExpFilters}
                  className="rounded-none h-8 text-xs gap-1"
                  data-testid="expenses-clear-btn"
                >
                  <Funnel size={12} />{t('clearFilter')}
                </Button>
                <Button
                  onClick={applyExpFilters}
                  disabled={panelExpLoading}
                  className="rounded-none h-8 text-xs gap-1 bg-zinc-900 hover:bg-zinc-800 text-white"
                  data-testid="expenses-search-btn"
                >
                  <MagnifyingGlass size={12} />
                  {panelExpLoading ? (t('loading') || 'Loading…') : (t('search') || 'Search')}
                </Button>
                <Button onClick={() => openExpenseAdd()} className="rounded-none bg-red-700 h-8 text-xs" data-testid="add-expense-list-btn"><Plus size={14} className="mr-1" /> {t('addExpense')}</Button>
              </div>
            </div>
            {purchaseCategories.length === 0 && (
              <div className="mb-3 p-3 border border-dashed border-amber-300 bg-amber-50/40 text-[11px] text-amber-800" data-testid="no-purchase-cats-warning">
                {t('noPurchaseCategoriesHint')}
              </div>
            )}
            <div className="border border-zinc-200 overflow-x-auto">
              {panelExpenses.length === 0 ? (
                <div className="p-8 text-center text-zinc-400 text-sm" data-testid="expenses-list-empty">{t('noExpenses')}</div>
              ) : (
                <table className="excel-table" data-testid="expenses-list-table">
                  <thead>
                    <tr>
                      <th>{t('date')}</th>
                      <th>{t('expenseCategory')}</th>
                      <th>{t('vendor') || 'Vendor'}</th>
                      <th>{t('description') || 'Description'}</th>
                      <th className="text-right">{t('netAmount')} (&euro;)</th>
                      <th className="text-right">{t('vatAmount')} (&euro;)</th>
                      <th className="text-right">{t('total')} (&euro;)</th>
                      <th>{t('notes')}</th>
                      <th className="w-20">{t('edit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortDesc(panelExpenses).map(e => (
                      <tr key={e.expense_id} data-testid={`expense-list-row-${e.expense_id}`}>
                        <td className="font-mono text-sm">{fmtDate(e.date)}</td>
                        <td className="text-sm"><span className="px-2 py-0.5 border border-zinc-200 bg-zinc-50 text-xs">{getCatName(e.category_id, e.category_name_en, e.category_name_el)}</span></td>
                        <td className="text-xs text-zinc-700" data-testid={`expense-list-vendor-${e.expense_id}`}>{e.vendor_name || '—'}</td>
                        <td className="text-xs text-zinc-500 truncate max-w-[200px]" data-testid={`expense-list-description-${e.expense_id}`}>{e.description || '—'}</td>
                        <td className="font-mono text-right text-sm">&euro;{(e.net_amount || 0).toFixed(2)}</td>
                        <td className="font-mono text-right text-sm text-amber-600">&euro;{(e.vat_amount || 0).toFixed(2)}</td>
                        <td className="font-mono text-right text-sm font-semibold text-red-700">&euro;{(e.amount || 0).toFixed(2)}</td>
                        <td className="text-xs text-zinc-500 truncate max-w-xs">{e.notes || '—'}</td>
                        <td>
                          <div className="flex gap-1">
                            <button onClick={() => openExpenseEdit(e)} className="p-1.5 hover:bg-zinc-100" data-testid={`edit-expense-list-${e.expense_id}`}><PencilSimple size={14} /></button>
                            <button onClick={() => confirmDelete('expense', e.expense_id)} className="p-1.5 hover:bg-red-50 text-red-500" data-testid={`delete-expense-list-${e.expense_id}`}><Trash size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300" data-testid="expenses-list-total-row">
                      <td colSpan={4} className="text-sm">{t('total').toUpperCase()}</td>
                      <td className="font-mono text-right text-sm">&euro;{panelExpenses.reduce((s, e) => s + (e.net_amount || 0), 0).toFixed(2)}</td>
                      <td className="font-mono text-right text-sm text-amber-700">&euro;{panelExpenses.reduce((s, e) => s + (e.vat_amount || 0), 0).toFixed(2)}</td>
                      <td className="font-mono text-right text-sm font-bold text-red-800">&euro;{panelExpenses.reduce((s, e) => s + (e.amount || 0), 0).toFixed(2)}</td>
                      <td colSpan={2} className="text-[10px] text-zinc-400 font-mono">{panelExpenses.length} {panelExpenses.length === 1 ? t('entry') : t('entries')}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* EXPENSE VENDORS — Phase 437: dedicated management page for the
              vendors used by Finance → Expenses. Completely independent from
              Suppliers / Bills / Building Management. */}
          <TabsContent value="expenseVendors" className="mt-4" data-testid="expense-vendors-panel">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div>
                <h3 className="text-sm font-semibold">{t('expenseVendors') || 'Expense Vendors'}</h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">{t('expenseVendorsDesc') || 'Manage payees used in Finance → Expenses. Not linked to Suppliers, Bills or Buildings.'}</p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={vendorPageSearch}
                  onChange={e => setVendorPageSearch(e.target.value)}
                  placeholder={t('searchVendors') || 'Search vendors…'}
                  className="rounded-none h-8 w-56 text-xs"
                  data-testid="expense-vendors-search"
                />
                <Button
                  onClick={() => { setEditingVendor(null); setVendorForm({ name: '', phone: '', email: '', notes: '' }); setVendorDialog(true); }}
                  className="rounded-none h-8 text-xs"
                  data-testid="add-expense-vendor-btn"
                ><Plus size={14} className="mr-1" /> {t('addVendor') || 'Add Vendor'}</Button>
              </div>
            </div>

            <div className="border border-zinc-200 overflow-x-auto">
              {expenseVendors.length === 0 ? (
                <div className="p-8 text-center text-zinc-400 text-sm" data-testid="expense-vendors-empty">{t('noVendors') || 'No vendors yet. Vendors are created automatically when added inline from the Expense form, or manually here.'}</div>
              ) : (() => {
                const q = vendorPageSearch.toLowerCase();
                const filtered = expenseVendors.filter(v =>
                  !q ||
                  v.name.toLowerCase().includes(q) ||
                  (v.email || '').toLowerCase().includes(q) ||
                  (v.phone || '').toLowerCase().includes(q)
                );
                if (filtered.length === 0) {
                  return <div className="p-8 text-center text-zinc-400 text-sm">{t('noMatches') || 'No vendors match your search.'}</div>;
                }
                return (
                  <table className="excel-table" data-testid="expense-vendors-table">
                    <thead>
                      <tr>
                        <th>{t('vendorName') || 'Vendor Name'}</th>
                        <th>{t('telephone') || 'Telephone'}</th>
                        <th>{t('email') || 'Email'}</th>
                        <th>{t('notes') || 'Notes'}</th>
                        <th className="text-right">{t('totalExpenses') || 'Total Expenses'}</th>
                        <th className="w-24 text-right">{t('actions') || 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(v => (
                        <tr key={v.vendor_id} data-testid={`expense-vendor-row-${v.vendor_id}`}>
                          <td className="font-medium">{v.name}</td>
                          <td className="font-mono text-xs text-zinc-700">{v.phone || '—'}</td>
                          <td className="text-xs text-zinc-700">{v.email || '—'}</td>
                          <td className="text-xs text-zinc-500 truncate max-w-xs">{v.notes || '—'}</td>
                          <td className="font-mono text-right text-sm font-semibold text-red-700" data-testid={`expense-vendor-total-${v.vendor_id}`}>
                            &euro;{(v.total_expenses || 0).toFixed(2)}
                            <span className="text-[10px] text-zinc-400 font-mono ml-1">({v.usage_count || 0}×)</span>
                          </td>
                          <td>
                            <div className="flex justify-end gap-1">
                              <button
                                onClick={() => {
                                  setEditingVendor(v);
                                  setVendorForm({ name: v.name, phone: v.phone || '', email: v.email || '', notes: v.notes || '' });
                                  setVendorDialog(true);
                                }}
                                className="p-1.5 hover:bg-zinc-100"
                                data-testid={`edit-expense-vendor-${v.vendor_id}`}
                              ><PencilSimple size={14} /></button>
                              <button
                                onClick={() => setVendorDeleteTarget(v)}
                                className="p-1.5 hover:bg-red-50 text-red-500"
                                data-testid={`delete-expense-vendor-${v.vendor_id}`}
                              ><Trash size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300" data-testid="expense-vendors-total-row">
                        <td colSpan={4} className="text-sm">{t('total').toUpperCase()}</td>
                        <td className="font-mono text-right text-sm font-bold text-red-800">&euro;{filtered.reduce((s, v) => s + (v.total_expenses || 0), 0).toFixed(2)}</td>
                        <td className="text-[10px] text-zinc-400 font-mono">{filtered.length} {filtered.length === 1 ? (t('vendor') || 'vendor') : (t('vendors') || 'vendors')}</td>
                      </tr>
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </TabsContent>

          {/* PURCHASE CATEGORIES — Phase 113: moved inline into Expenses via Popover dropdown.
              Legacy `purchaseCategories` tab key auto-redirects to the Expenses panel. */}

          {/* VAT PAID TAB — Phase 111: Input VAT report from Supplier Invoices + Expenses. Income VAT excluded. */}
          <TabsContent value="vatPaid" className="mt-4" data-testid="vat-paid-panel">
            <div className="mb-3">
              <h3 className="text-sm font-semibold">{t('vatPaid')}</h3>
              <p className="text-[11px] text-zinc-400 mt-0.5">{t('vatPaidDesc')}</p>
            </div>
            {/* Date filter */}
            <div className="flex items-end gap-2 flex-wrap mb-3 pb-3 border-b border-zinc-100" data-testid="vat-paid-filters">
              <div>
                <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('fromDate')}</Label>
                <DatePickerDMY value={vatFrom} onChange={setVatFrom} className="h-8 w-32 font-mono text-xs" data-testid="vat-paid-from" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-400 block mb-1">{t('toDate')}</Label>
                <DatePickerDMY value={vatTo} onChange={setVatTo} className="h-8 w-32 font-mono text-xs" data-testid="vat-paid-to" />
              </div>
              {(vatFrom || vatTo) && (
                <Button variant="outline" size="sm" onClick={() => { setVatFrom(''); setVatTo(''); }} className="rounded-none h-8 text-xs gap-1" data-testid="vat-paid-clear">
                  <Funnel size={12} />{t('clearFilter')}
                </Button>
              )}
            </div>

            {/* Totals strip */}
            <div className="flex items-stretch w-full border border-zinc-200 divide-x divide-zinc-200 mb-4" data-testid="vat-paid-totals">
              <div className="flex-1 min-w-[160px] basis-0 px-4 py-2">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-amber-600">{t('vatFromSuppliers')}</div>
                <div className="text-sm font-mono font-bold tabular-nums text-amber-800" data-testid="vat-paid-suppliers-total">&euro;{(vatPaid.total_supplier_vat || 0).toFixed(2)}</div>
                <div className="text-[10px] text-zinc-400 font-mono">{vatPaid.supplier_invoices.length} {vatPaid.supplier_invoices.length === 1 ? t('invoice') : t('invoices')}</div>
              </div>
              <div className="flex-1 min-w-[160px] basis-0 px-4 py-2">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-amber-600">{t('vatFromExpenses')}</div>
                <div className="text-sm font-mono font-bold tabular-nums text-amber-800" data-testid="vat-paid-expenses-total">&euro;{(vatPaid.total_expense_vat || 0).toFixed(2)}</div>
                <div className="text-[10px] text-zinc-400 font-mono">{vatPaid.expenses.length} {vatPaid.expenses.length === 1 ? t('entry') : t('entries')}</div>
              </div>
              <div className="flex-1 min-w-[160px] basis-0 px-4 py-2 bg-amber-50">
                <div className="text-[9px] font-semibold uppercase tracking-wider text-amber-700">{t('totalVatPaid')}</div>
                <div className="text-sm font-mono font-extrabold tabular-nums text-amber-900" data-testid="vat-paid-grand-total">&euro;{(vatPaid.grand_total || 0).toFixed(2)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Suppliers VAT breakdown */}
              <div>
                <h4 className="text-xs font-semibold text-zinc-700 mb-2">{t('suppliersVat')}</h4>
                <div className="border border-zinc-200 overflow-hidden">
                  {vatPaid.supplier_invoices.length === 0 ? (
                    <div className="p-4 text-center text-zinc-400 text-xs">{t('noVatEntries') || t('noEntries')}</div>
                  ) : (
                    <div className="max-h-[380px] overflow-y-auto">
                      <table className="excel-table w-full" data-testid="vat-paid-suppliers-table">
                        <thead className="sticky top-0 z-10 bg-zinc-50">
                          <tr>
                            <th className="w-24">{t('date')}</th>
                            <th>{t('supplier')}</th>
                            <th className="w-24">{t('invoice')}</th>
                            <th className="text-right w-24">{t('vatAmount')} (&euro;)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortDesc(vatPaid.supplier_invoices).map(r => (
                            <tr key={`inv-${r.supplier_id}-${r.invoice_number}-${r.date}`}>
                              <td className="font-mono text-xs py-1">{fmtDate(r.date)}</td>
                              <td className="text-xs py-1">{r.supplier_name}</td>
                              <td className="font-mono text-xs py-1">#{r.invoice_number || '—'}</td>
                              <td className="font-mono text-right text-xs font-semibold text-amber-700 py-1">&euro;{r.vat.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="sticky bottom-0 z-10 bg-zinc-100 border-t-2 border-zinc-300">
                          <tr>
                            <td colSpan={3} className="text-[10px] text-zinc-500 font-mono py-1.5 px-2">{vatPaid.supplier_invoices.length} {t('entries')}</td>
                            <td className="font-mono text-right text-sm font-bold text-amber-800 py-1.5 px-2">&euro;{(vatPaid.total_supplier_vat || 0).toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Expenses VAT breakdown */}
              <div>
                <h4 className="text-xs font-semibold text-zinc-700 mb-2">{t('expensesVat')}</h4>
                <div className="border border-zinc-200 overflow-hidden">
                  {vatPaid.expenses.length === 0 ? (
                    <div className="p-4 text-center text-zinc-400 text-xs">{t('noVatEntries') || t('noEntries')}</div>
                  ) : (
                    <div className="max-h-[380px] overflow-y-auto">
                      <table className="excel-table w-full" data-testid="vat-paid-expenses-table">
                        <thead className="sticky top-0 z-10 bg-zinc-50">
                          <tr>
                            <th className="w-24">{t('date')}</th>
                            <th>{t('expenseCategory')}</th>
                            <th className="text-right w-24">{t('vatAmount')} (&euro;)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortDesc(vatPaid.expenses).map(r => (
                            <tr key={`exp-${r.expense_id}`}>
                              <td className="font-mono text-xs py-1">{fmtDate(r.date)}</td>
                              <td className="text-xs py-1">{language === 'el' ? (r.category_name_el || r.category_name_en) : (r.category_name_en || r.category_name_el)}</td>
                              <td className="font-mono text-right text-xs font-semibold text-amber-700 py-1">&euro;{r.vat.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="sticky bottom-0 z-10 bg-zinc-100 border-t-2 border-zinc-300">
                          <tr>
                            <td colSpan={2} className="text-[10px] text-zinc-500 font-mono py-1.5 px-2">{vatPaid.expenses.length} {t('entries')}</td>
                            <td className="font-mono text-right text-sm font-bold text-amber-800 py-1.5 px-2">&euro;{(vatPaid.total_expense_vat || 0).toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

        </Tabs>
            )}
          </div>
        </div>

        {/* BANK DIALOG */}
        <Dialog open={bankDialog} onOpenChange={setBankDialog}>
          <DialogContent className="sm:max-w-md" data-testid="bank-dialog">
            <DialogHeader><DialogTitle>{editingBank ? t('editBankAccount') : t('addBankAccount')}</DialogTitle></DialogHeader>
            <form onSubmit={handleBankSubmit} className="space-y-3">
              <div><Label>{t('name')} *</Label><Input value={bankForm.name} onChange={e => setBankForm(f => ({ ...f, name: e.target.value }))} className="rounded-none mt-1" required data-testid="bank-form-name" /></div>
              <div><Label>{t('bankName')}</Label><Input value={bankForm.bank_name} onChange={e => setBankForm(f => ({ ...f, bank_name: e.target.value }))} className="rounded-none mt-1" data-testid="bank-form-bankname" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t('accountNumber')}</Label><Input value={bankForm.account_number} onChange={e => setBankForm(f => ({ ...f, account_number: e.target.value }))} className="rounded-none mt-1 font-mono" /></div>
                <div><Label>IBAN</Label><Input value={bankForm.iban} onChange={e => setBankForm(f => ({ ...f, iban: e.target.value }))} className="rounded-none mt-1 font-mono" data-testid="bank-form-iban" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t('openingBalance')} (&euro;) *</Label><Input type="number" step="0.01" value={bankForm.opening_balance} onChange={e => setBankForm(f => ({ ...f, opening_balance: e.target.value }))} className="rounded-none mt-1 font-mono" data-testid="bank-form-balance" /></div>
                <div><Label>{t('openingBalanceDate')} *</Label><DatePickerDMY value={bankForm.opening_balance_date} onChange={v => setBankForm(f => ({ ...f, opening_balance_date: v }))} className="mt-1 font-mono" data-testid="bank-form-balance-date" /></div>
              </div>
              {/* Phase 232 — Per-account "Include in Financial Summary" toggle.
                  When OFF, this account's current balance is NOT counted toward
                  the global Current Balance / Final Balance / Add Payment helpers. */}
              <div className="border border-zinc-200 bg-zinc-50/60 px-3 py-2.5 flex items-start gap-3">
                <input
                  id="bank-form-include"
                  type="checkbox"
                  checked={!!bankForm.include_in_summary}
                  onChange={e => setBankForm(f => ({ ...f, include_in_summary: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-emerald-700"
                  data-testid="bank-form-include-in-summary"
                />
                <label htmlFor="bank-form-include" className="text-sm cursor-pointer flex-1 min-w-0">
                  <div className="font-semibold text-zinc-800">{t('includeInFinancialSummary') || 'Include in Financial Summary'}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{t('includeInFinancialSummaryDesc') || 'When ON, this account\'s balance contributes to the global Current Balance, Final Balance and Add Payment calculations.'}</div>
                </label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setBankDialog(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="rounded-none" data-testid="bank-form-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* INCOME DIALOG */}
        <Dialog open={incomeDialog} onOpenChange={setIncomeDialog}>
          <DialogContent className="sm:max-w-3xl rounded-none" data-testid="income-dialog">
            <DialogHeader><DialogTitle>{editingIncome ? t('editIncome') : t('addIncome')}</DialogTitle></DialogHeader>
            {/* Phase 531 — Wider modal + 2-column grid so the form fits on
                one screen without scrolling. Treasury toggle removed: when a
                real bank account is selected the payment is by definition
                official; Internal Cash is handled exclusively from the
                Internal Cash module. Allocation 12.5 / Reserve 10 / Social
                Insurance 10 rows removed from the preview to keep the
                customer-payment entry simple. */}
            <form onSubmit={handleIncomeSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 pt-1">
              {/* Row 1 — Date + Amount */}
              <div><Label>{t('date')}</Label><DatePickerDMY value={incomeForm.date} onChange={v => setIncomeForm(f => ({ ...f, date: v }))} className="mt-1 font-mono" data-testid="income-form-date" /></div>
              <div><Label>{t('amount')} (&euro;) *</Label><Input type="number" step="0.01" min="0.01" value={incomeForm.amount} onChange={e => setIncomeForm(f => ({ ...f, amount: e.target.value }))} className="rounded-none mt-1 font-mono" required data-testid="income-form-amount" /></div>
              {/* Row 2 — Source / Bank selector spans full width (it
                  conditionally renders the bank dropdown inline). */}
              <div className="sm:col-span-2">
                <SourceSelector value={incomeForm.source} onChange={v => setIncomeForm(f => ({ ...f, source: v, bank_account_id: v === 'cash' ? '' : f.bank_account_id, affects_treasury: true }))} showBankSelect bankValue={incomeForm.bank_account_id} onBankChange={v => setIncomeForm(f => ({ ...f, bank_account_id: v }))} />
              </div>
              {incomeForm.source === 'cash' && (
                <div className="sm:col-span-2 border border-green-200 bg-green-50 p-2.5 text-[10px] text-green-700 font-medium" data-testid="cash-notice">{t('cashNoVatNotice')}</div>
              )}
              {incomeForm.source !== 'cash' && (
                <div className="sm:col-span-2 flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid="vat-toggle">
                    <input type="checkbox" checked={incomeForm.vat_included} onChange={e => setIncomeForm(f => ({ ...f, vat_included: e.target.checked }))} className="accent-zinc-700 w-3.5 h-3.5" />
                    <span className={incomeForm.vat_included ? 'text-zinc-700 font-medium' : 'text-zinc-400'}>{t('vatIncluded')}</span>
                  </label>
                  {!incomeForm.vat_included && <span className="text-[10px] text-amber-600">{t('vatNotIncluded')}</span>}
                  {incomePreview.gross > 0 && (
                    <div className="ml-auto inline-flex items-center gap-3 border border-zinc-200 bg-zinc-50 px-3 py-1.5" data-testid="vat-preview">
                      <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{t('grossIncome')}</span>
                      <span className="font-mono text-xs font-semibold">&euro;{incomePreview.gross.toFixed(2)}</span>
                      <span className="text-[10px] text-zinc-400">·</span>
                      <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{t('vatAmount')}</span>
                      <span className="font-mono text-xs">&euro;{incomePreview.vat.toFixed(2)}</span>
                      <span className="text-[10px] text-zinc-400">·</span>
                      <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{t('netAmount')}</span>
                      <span className="font-mono text-xs font-semibold">&euro;{incomePreview.net.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}
              {/* Row 3 — Income Type + Client side-by-side when applicable */}
              <div>
                <Label>{t('incomeType')} *</Label>
                <Select value={incomeForm.income_type} onValueChange={v => setIncomeForm(f => ({ ...f, income_type: v, ...(v !== 'client_payment' ? { client_id: '', sync_to_cleaning: false } : {}) }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="income-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INCOME_TYPES.map(it => (
                      <SelectItem key={it.key} value={it.key} data-testid={`income-type-${it.key}`}>
                        {t(it.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {incomeForm.income_type === 'client_payment' ? (
                <div><Label>{t('client')} *</Label>
                  <Select value={incomeForm.client_id || '__none__'} onValueChange={v => onIncomeClientChange(v === '__none__' ? '' : v)} required>
                    <SelectTrigger className="rounded-none mt-1" data-testid="income-form-client"><SelectValue placeholder={t('selectClient')} /></SelectTrigger>
                    <SelectContent><SelectItem value="__none__">-</SelectItem>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}{c.code ? ` (${c.code})` : ''}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : <div />}
              {/* Sync toggle — only visible when client is selected AND income type is client_payment */}
              {incomeForm.income_type === 'client_payment' && incomeForm.client_id && (
                <label className="sm:col-span-2 flex items-center gap-2 text-xs cursor-pointer select-none border border-teal-200 bg-teal-50/50 p-2.5" data-testid="sync-toggle">
                  <input type="checkbox" checked={incomeForm.sync_to_cleaning} onChange={e => setIncomeForm(f => ({ ...f, sync_to_cleaning: e.target.checked }))} className="accent-teal-700 w-3.5 h-3.5" />
                  <ArrowsClockwise size={14} className="text-teal-600" />
                  <span className={incomeForm.sync_to_cleaning ? 'text-teal-700 font-medium' : 'text-zinc-400'}>{t('syncToCleaning')}</span>
                </label>
              )}
              {/* Notes (full width) */}
              <div className="sm:col-span-2">
                <Label>{t('notes')}</Label>
                <Textarea
                  value={incomeForm.notes}
                  onChange={e => setIncomeForm(f => ({ ...f, notes: e.target.value }))}
                  className="rounded-none mt-1 resize-none"
                  rows={2}
                  placeholder={t('addNotesOptional')}
                  data-testid="income-form-notes"
                />
              </div>
              <DialogFooter className="sm:col-span-2 mt-2">
                <Button type="button" variant="outline" onClick={() => setIncomeDialog(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="rounded-none bg-emerald-700" data-testid="income-form-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Phase 483 — INVOICE ALLOCATION DIALOG */}
        <Dialog open={allocDialog} onOpenChange={(o) => { if (!o) { setAllocDialog(false); setAllocIncome(null); } }}>
          <DialogContent className="sm:max-w-2xl" data-testid="alloc-dialog">
            <DialogHeader>
              <DialogTitle>{t('allocatePaymentTitle') || 'Allocate payment to invoices'}</DialogTitle>
              <p className="text-xs text-zinc-500 mt-1">
                {(t('allocatePaymentSubtitle') || 'Apply €{amount} from {client} to one or more open invoices')
                  .replace('{amount}', (allocIncome?.amount || 0).toFixed(2))
                  .replace('{client}', allocIncome?.client_name || '-')}
              </p>
            </DialogHeader>
            <div className="space-y-4">
              {/* Mode tabs */}
              <div className="flex gap-1 border-b border-zinc-200">
                {['single', 'multi', 'auto'].map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setAllocMode(m)}
                    className={`px-4 py-2 text-xs font-medium border-b-2 transition ${allocMode === m ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
                    data-testid={`alloc-mode-${m}`}
                  >
                    {m === 'single' ? (t('allocSingle') || 'Single invoice')
                      : m === 'multi' ? (t('allocMulti') || 'Multiple invoices')
                      : (t('allocAuto') || 'Auto FIFO')}
                  </button>
                ))}
              </div>

              {/* Open invoices table — Phase 729: shows both OFFICIAL and INTERNAL
                  invoices with a small type badge in the # column. */}
              <div className="border border-zinc-200 max-h-72 overflow-auto" data-testid="alloc-invoices-table">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-zinc-600 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-medium w-8"></th>
                      <th className="text-left px-2 py-1.5 font-medium">{t('type') || 'Type'}</th>
                      <th className="text-left px-2 py-1.5 font-medium">{t('invoiceNo') || 'Invoice #'}</th>
                      <th className="text-left px-2 py-1.5 font-medium">{t('referenceNumber') || 'Ref #'}</th>
                      <th className="text-left px-2 py-1.5 font-medium">{t('date')}</th>
                      <th className="text-right px-2 py-1.5 font-medium">{t('total') || 'Total'}</th>
                      <th className="text-right px-2 py-1.5 font-medium">{t('remaining') || 'Remaining'}</th>
                      {allocMode === 'multi' && <th className="text-right px-2 py-1.5 font-medium">{t('applyAmount') || 'Apply €'}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {allocOpenInvoices.map(inv => {
                      const fifoMatch = allocMode === 'auto' && allocFifoPreview.find(p => p.invoice_id === inv.invoice_id);
                      const isInternal = inv.invoice_type === 'internal_cash_tracking';
                      return (
                        <tr key={inv.invoice_id} className="border-t border-zinc-100">
                          <td className="px-2 py-1.5 align-middle">
                            {allocMode === 'single' && (
                              <input
                                type="radio"
                                name="alloc-single"
                                checked={allocSingle === inv.invoice_id}
                                onChange={() => setAllocSingle(inv.invoice_id)}
                                data-testid={`alloc-single-${inv.invoice_id}`}
                              />
                            )}
                            {allocMode === 'auto' && fifoMatch && (
                              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" title="FIFO" />
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <span
                              className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${isInternal ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-zinc-50 text-zinc-600 border-zinc-200'}`}
                              data-testid={`alloc-type-${inv.invoice_id}`}
                            >
                              {isInternal ? (t('internal') || 'Internal') : (t('official') || 'Official')}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 font-mono">{inv.invoice_no || inv.invoice_id?.slice(0, 8)}</td>
                          <td className="px-2 py-1.5 font-mono text-[10px] text-zinc-500">{inv.reference_no || '—'}</td>
                          <td className="px-2 py-1.5 font-mono text-zinc-500">{inv.date}</td>
                          <td className="px-2 py-1.5 text-right font-mono">€{(inv.total || 0).toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-amber-700">€{(inv.remaining || 0).toFixed(2)}</td>
                          {allocMode === 'multi' && (
                            <td className="px-2 py-1.5 text-right">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max={Math.max(0, Number(inv.remaining || 0))}
                                disabled={Number(inv.remaining || 0) <= 0}
                                value={allocAmounts[inv.invoice_id] || ''}
                                onChange={e => setAllocAmounts(a => ({ ...a, [inv.invoice_id]: e.target.value }))}
                                placeholder="0.00"
                                className="rounded-none h-7 text-xs font-mono text-right w-24 disabled:bg-zinc-100 disabled:cursor-not-allowed"
                                data-testid={`alloc-multi-${inv.invoice_id}`}
                              />
                            </td>
                          )}
                          {allocMode === 'auto' && (
                            <td className="px-2 py-1.5 text-right font-mono text-emerald-700">
                              {fifoMatch ? `€${fifoMatch.allocate.toFixed(2)}` : '-'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="border border-zinc-200 bg-zinc-50 p-3 grid grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="text-zinc-500">{t('paymentAmount') || 'Payment amount'}</div>
                  <div className="font-mono font-semibold text-zinc-800">€{(allocIncome?.amount || 0).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-zinc-500">{t('totalAllocated') || 'Total allocated'}</div>
                  <div className="font-mono font-semibold text-emerald-700" data-testid="alloc-total">€{allocAllocatedTotal.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-zinc-500">{t('remainingUnallocated') || 'Remaining unallocated'}</div>
                  <div className="font-mono font-semibold text-amber-700" data-testid="alloc-unallocated">
                    €{Math.max(0, _round2_local((allocIncome?.amount || 0) - allocAllocatedTotal)).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => { setAllocDialog(false); setAllocIncome(null); }} data-testid="alloc-skip">
                {t('allocateLater') || 'Skip / Allocate later'}
              </Button>
              <Button type="button" className="rounded-none bg-emerald-700" onClick={handleAllocationSubmit} disabled={allocSubmitting} data-testid="alloc-apply">
                {allocSubmitting ? (t('applying') || 'Applying...') : (t('applyAllocation') || 'Apply allocation')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* EXPENSE DIALOG — Phase 95: standalone Purchases. Only Date, Category, Net, VAT, Total, Notes. */}
        <Dialog open={expenseDialog} onOpenChange={setExpenseDialog}>
          <DialogContent className="sm:max-w-md" data-testid="expense-dialog">
            <DialogHeader><DialogTitle>{editingExpense ? t('editExpense') : t('addExpense')}</DialogTitle></DialogHeader>
            <form onSubmit={handleExpenseSubmit} className="space-y-3">
              <div>
                <Label>{t('date')}</Label>
                <DatePickerDMY value={expenseForm.date} onChange={v => setExpenseForm(f => ({ ...f, date: v }))} className="mt-1 font-mono" data-testid="expense-form-date" />
              </div>
              <div>
                <Label>{t('expenseCategory')} *</Label>
                <Select value={expenseForm.category_id} onValueChange={v => setExpenseForm(f => ({ ...f, category_id: v }))} required>
                  <SelectTrigger className="rounded-none mt-1" data-testid="expense-form-category"><SelectValue placeholder={t('selectCategory')} /></SelectTrigger>
                  <SelectContent>
                    {purchaseCategories.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-zinc-400" data-testid="no-purchase-categories-hint">{t('noPurchaseCategoriesHint')}</div>
                    ) : purchaseCategories.map(c => (
                      <SelectItem key={c.category_id} value={c.category_id}>{language === 'el' ? (c.name_el || c.name_en) : (c.name_en || c.name_el)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Phase 437 — Vendor (independent of Suppliers). Searchable +
                  inline create. Positioned directly under Category, per user
                  requested layout: Date / Category / Vendor / Description /
                  Net / VAT / Total / Attachment / Notes. */}
              <div className="relative">
                <Label>{t('vendor') || 'Vendor'}</Label>
                <Input
                  type="text"
                  value={vendorSearch}
                  onFocus={() => setVendorDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setVendorDropdownOpen(false), 150)}
                  onChange={(e) => {
                    setVendorSearch(e.target.value);
                    setExpenseForm(f => ({ ...f, vendor_id: '', vendor_name: e.target.value }));
                    setVendorDropdownOpen(true);
                  }}
                  className="rounded-none mt-1"
                  placeholder={t('vendorSearchPlaceholder') || 'Search or type a new vendor (e.g. Petrolina, Superhome)'}
                  data-testid="expense-form-vendor"
                />
                {vendorDropdownOpen && (
                  <div className="absolute z-50 left-0 right-0 mt-0.5 bg-white border border-zinc-200 max-h-44 overflow-y-auto shadow-md" data-testid="expense-form-vendor-dropdown">
                    {(() => {
                      const q = (vendorSearch || '').toLowerCase();
                      const matches = expenseVendors.filter(v => v.name.toLowerCase().includes(q));
                      const exact = expenseVendors.some(v => v.name.toLowerCase() === q);
                      return (
                        <>
                          {matches.slice(0, 20).map(v => (
                            <button
                              key={v.vendor_id}
                              type="button"
                              className="w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 flex items-center justify-between"
                              data-testid={`expense-form-vendor-opt-${v.vendor_id}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setExpenseForm(f => ({ ...f, vendor_id: v.vendor_id, vendor_name: v.name }));
                                setVendorSearch(v.name);
                                setVendorDropdownOpen(false);
                              }}
                            >
                              <span>{v.name}</span>
                              <span className="text-[10px] text-zinc-400">{v.usage_count || 0}×</span>
                            </button>
                          ))}
                          {!exact && (vendorSearch || '').trim().length > 0 && (
                            <button
                              type="button"
                              className="w-full text-left px-3 py-1.5 text-xs text-emerald-700 bg-emerald-50/40 hover:bg-emerald-100 border-t border-zinc-100"
                              data-testid="expense-form-vendor-create"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setExpenseForm(f => ({ ...f, vendor_id: '', vendor_name: vendorSearch.trim() }));
                                setVendorDropdownOpen(false);
                              }}
                            >
                              + {t('createVendor') || 'Create vendor'} <strong>“{vendorSearch.trim()}”</strong>
                            </button>
                          )}
                          {matches.length === 0 && !(vendorSearch || '').trim() && (
                            <div className="px-3 py-1.5 text-[11px] text-zinc-400 italic">{t('startTyping') || 'Start typing to search…'}</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div>
                <Label>{t('description') || 'Description'}</Label>
                <Input
                  type="text"
                  value={expenseForm.description}
                  onChange={e => setExpenseForm(f => ({ ...f, description: e.target.value }))}
                  className="rounded-none mt-1"
                  placeholder={t('optional') || 'Optional'}
                  data-testid="expense-form-description"
                />
              </div>

              {/* Phase 109 — Net / VAT / Total auto-calc. Editing any field updates the other two;
                  VAT rate is editable (defaults to 19%). */}
              <div className="grid grid-cols-[1fr_1fr_auto_1fr] gap-2 items-end">
                <div>
                  <Label>{t('netAmount')} (&euro;)</Label>
                  <Input type="number" step="0.01" value={expenseForm.net_amount} onChange={e => onExpenseNetChange(e.target.value)} className="rounded-none mt-1 font-mono" data-testid="expense-form-net" />
                </div>
                <div>
                  <Label>{t('vatAmount')} (&euro;)</Label>
                  <Input type="number" step="0.01" value={expenseForm.vat_amount} onChange={e => onExpenseVatChange(e.target.value)} className="rounded-none mt-1 font-mono" data-testid="expense-form-vat" />
                </div>
                <div className="w-20">
                  <Label className="text-[10px] text-zinc-400">{t('vatRate')}</Label>
                  <div className="mt-1 flex items-center border border-zinc-200">
                    <Input type="number" step="0.1" value={expenseForm.vat_rate} onChange={e => onExpenseRateChange(e.target.value)} className="rounded-none border-0 h-9 w-full font-mono text-xs px-2 focus-visible:ring-0" data-testid="expense-form-vat-rate" />
                    <span className="px-1.5 text-xs text-zinc-400">%</span>
                  </div>
                </div>
                <div>
                  <Label>{t('total')} (&euro;) *</Label>
                  <Input type="number" step="0.01" min="0.01" value={expenseForm.amount} onChange={e => onExpenseTotalChange(e.target.value)} className="rounded-none mt-1 font-mono" placeholder={t('autoCalc')} data-testid="expense-form-amount" />
                </div>
              </div>

              {/* Phase 437 — Attachment (e.g. invoice scan / receipt). Stored
                  as a base64 data URL on the expense; a simple file input
                  keeps the implementation portable across web + mobile. */}
              <div>
                <Label>{t('attachment') || 'Attachment'}</Label>
                <Input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) { setExpenseForm(f => ({ ...f, attachment_name: '', attachment_data: '' })); return; }
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error(t('fileTooLarge') || 'File is larger than 5 MB');
                      e.target.value = '';
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      setExpenseForm(f => ({ ...f, attachment_name: file.name, attachment_data: reader.result || '' }));
                    };
                    reader.readAsDataURL(file);
                  }}
                  className="rounded-none mt-1 h-9 text-xs file:mr-2 file:py-1 file:px-2 file:border-0 file:bg-zinc-100 file:text-zinc-700 file:text-xs"
                  data-testid="expense-form-attachment"
                />
                {(expenseForm.attachment_name || expenseForm.attachment_url) && (
                  <p className="text-[11px] text-zinc-500 mt-1 truncate" data-testid="expense-form-attachment-name">
                    {expenseForm.attachment_name || expenseForm.attachment_url}
                    <button type="button" onClick={() => setExpenseForm(f => ({ ...f, attachment_name: '', attachment_data: '', attachment_url: '' }))} className="ml-2 text-red-600 hover:underline" data-testid="expense-form-attachment-clear">{t('remove') || 'remove'}</button>
                  </p>
                )}
              </div>

              <div>
                <Label>{t('notes')}</Label>
                <Textarea value={expenseForm.notes} onChange={e => setExpenseForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none mt-1" rows={2} data-testid="expense-form-notes" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setExpenseDialog(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="rounded-none bg-red-700" data-testid="expense-form-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* EXPENSE VENDOR DIALOG — Phase 437. Create / edit vendors used by
            Finance → Expenses. Independent from Suppliers / Bills. */}
        <Dialog open={vendorDialog} onOpenChange={setVendorDialog}>
          <DialogContent className="sm:max-w-md" data-testid="expense-vendor-dialog">
            <DialogHeader>
              <DialogTitle>{editingVendor ? (t('editVendor') || 'Edit Vendor') : (t('addVendor') || 'Add Vendor')}</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const name = (vendorForm.name || '').trim();
                if (!name) { toast.error(t('nameRequired') || 'Name is required'); return; }
                try {
                  if (editingVendor) {
                    await axios.put(`${API}/expense-vendors/${editingVendor.vendor_id}`, {
                      name, phone: vendorForm.phone, email: vendorForm.email, notes: vendorForm.notes,
                    }, axCfg);
                  } else {
                    await axios.post(`${API}/expense-vendors`, {
                      name, phone: vendorForm.phone, email: vendorForm.email, notes: vendorForm.notes,
                    }, axCfg);
                  }
                  toast.success(t('savedSuccess') || t('saved') || 'Saved');
                  setVendorDialog(false);
                  fetchExpenseVendors();
                } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
              }}
              className="space-y-3"
            >
              <div>
                <Label>{t('vendorName') || 'Vendor Name'} *</Label>
                <Input value={vendorForm.name} onChange={e => setVendorForm(f => ({ ...f, name: e.target.value }))} className="rounded-none mt-1" required data-testid="expense-vendor-form-name" />
              </div>
              <div>
                <Label>{t('telephone') || 'Telephone'}</Label>
                <Input value={vendorForm.phone} onChange={e => setVendorForm(f => ({ ...f, phone: e.target.value }))} className="rounded-none mt-1 font-mono" placeholder={t('optional') || 'Optional'} data-testid="expense-vendor-form-phone" />
              </div>
              <div>
                <Label>{t('email') || 'Email'}</Label>
                <Input type="email" value={vendorForm.email} onChange={e => setVendorForm(f => ({ ...f, email: e.target.value }))} className="rounded-none mt-1" placeholder={t('optional') || 'Optional'} data-testid="expense-vendor-form-email" />
              </div>
              <div>
                <Label>{t('notes') || 'Notes'}</Label>
                <Textarea value={vendorForm.notes} onChange={e => setVendorForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none mt-1" rows={3} data-testid="expense-vendor-form-notes" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setVendorDialog(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="rounded-none" data-testid="expense-vendor-form-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* EXPENSE VENDOR DELETE — Phase 437 */}
        <AlertDialog open={!!vendorDeleteTarget} onOpenChange={(o) => !o && setVendorDeleteTarget(null)}>
          <AlertDialogContent className="rounded-none" data-testid="expense-vendor-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteVendor') || 'Delete Vendor'}</AlertDialogTitle>
              <AlertDialogDescription>
                {vendorDeleteTarget && (vendorDeleteTarget.usage_count || 0) > 0
                  ? (t('vendorInUseWarning') || `This vendor is linked to ${vendorDeleteTarget.usage_count} expense(s). Reassign or delete those first.`)
                  : (t('confirmDeleteVendor') || 'Are you sure you want to delete this vendor?')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="expense-vendor-delete-cancel">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  if (!vendorDeleteTarget) return;
                  try {
                    await axios.delete(`${API}/expense-vendors/${vendorDeleteTarget.vendor_id}`, axCfg);
                    toast.success(t('deleted') || 'Deleted');
                    setVendorDeleteTarget(null);
                    fetchExpenseVendors();
                  } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
                }}
                disabled={vendorDeleteTarget && (vendorDeleteTarget.usage_count || 0) > 0}
                className="rounded-none bg-red-600 hover:bg-red-700"
                data-testid="expense-vendor-delete-confirm"
              >{t('delete') || 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* BANK PAYMENT DIALOG — Phase 96 */}
        <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
          <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
            <DialogHeader><DialogTitle>{editingPayment ? t('editPayment') : t('addPayment')}</DialogTitle></DialogHeader>
            <form onSubmit={handlePaymentSubmit} className="space-y-3">
              <div>
                <Label>{t('date')}</Label>
                <DatePickerDMY value={paymentForm.date} onChange={v => setPaymentForm(f => ({ ...f, date: v }))} className="mt-1 font-mono" data-testid="payment-form-date" />
              </div>
              <div>
                <Label>{t('categoryOrSupplier')} *</Label>
                <Select
                  value={paymentForm.supplier_id ? `sup:${paymentForm.supplier_id}` : paymentForm.category_id ? `cat:${paymentForm.category_id}` : ''}
                  onValueChange={v => {
                    if (v.startsWith('sup:')) setPaymentForm(f => ({ ...f, supplier_id: v.slice(4), category_id: '' }));
                    else if (v.startsWith('cat:')) setPaymentForm(f => ({ ...f, category_id: v.slice(4), supplier_id: '' }));
                  }}
                  required
                >
                  <SelectTrigger className="rounded-none mt-1" data-testid="payment-form-target"><SelectValue placeholder={t('selectCategoryOrSupplier')} /></SelectTrigger>
                  <SelectContent>
                    {categories.length === 0 && suppliersList.length === 0 && (
                      <div className="px-3 py-2 text-xs text-zinc-400">{t('noExpenseCategoriesHint')}</div>
                    )}
                    {categories.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t('expenseCategories')}</div>
                        {categories.map(c => (
                          <SelectItem key={`cat-${c.category_id}`} value={`cat:${c.category_id}`}>
                            <span className="flex items-center gap-2">
                              <span>{language === 'el' ? (c.name_el || c.name_en) : (c.name_en || c.name_el)}</span>
                              {c.bucket && <span className="text-[9px] px-1 py-0.5 border border-amber-200 bg-amber-50 text-amber-700">{bucketLabelOf(c.bucket)}</span>}
                            </span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {suppliersList.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400 mt-1">{t('suppliers')}</div>
                        {suppliersList.map(s => (
                          <SelectItem key={`sup-${s.supplier_id}`} value={`sup:${s.supplier_id}`}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {/* Phase 230 — Linked Bill picker now selects a VENDOR ACCOUNT
                  (CYTA / EPIC / Universal …) instead of an individual invoice.
                  Backend FIFO-allocates the payment across the vendor's
                  outstanding bills oldest-first and creates one bill_payments
                  row per consumed bill. After save: vendor's Bills are
                  automatically updated (Payments / Statements / status). */}
              {/* Phase 462 — Hidden behind an explicit opt-in toggle so
                  generic bank expenses (cash withdrawal, fuel, supermarket,
                  bank charges, transfers, manual adjustments) don't see
                  any vendor / invoice allocation UI at all. */}
              {!showBillLink ? (
                <button
                  type="button"
                  onClick={() => setShowBillLink(true)}
                  className="text-xs text-zinc-500 hover:text-zinc-900 underline-offset-2 hover:underline self-start"
                  data-testid="payment-form-toggle-bill-link"
                >
                  + {t('linkToBillOptional') || 'Link this payment to a bill (optional)'}
                </button>
              ) : (
              <div className="min-w-0 max-w-full overflow-hidden">
                <div className="flex items-center justify-between">
                  <Label>{t('linkedBill') || 'Linked Bill'} <span className="text-zinc-400 text-xs">({t('optional') || 'optional'})</span></Label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowBillLink(false);
                      setPaymentForm(f => ({ ...f, vendor_account_id: '', bill_id: '' }));
                    }}
                    className="text-[10px] text-zinc-400 hover:text-red-700 uppercase tracking-wider"
                    data-testid="payment-form-clear-bill-link"
                  >
                    ✕ {t('remove') || 'Remove'}
                  </button>
                </div>
                <Select
                  value={paymentForm.vendor_account_id || '__none__'}
                  onValueChange={v => {
                    if (v === '__none__') {
                      setPaymentForm(f => ({ ...f, vendor_account_id: '', bill_id: '' }));
                    } else {
                      // When a vendor is picked, simplify the form: route to
                      // Final Balance, default amount to total outstanding.
                      const sel = payableVendors.find(b => b.vendor_id === v);
                      setPaymentForm(f => ({
                        ...f,
                        vendor_account_id: v,
                        bill_id: '',
                        paid_from: 'final_balance',
                        amount: f.amount || (sel ? String(sel.outstanding_balance) : f.amount),
                      }));
                    }
                  }}
                >
                  <SelectTrigger className="rounded-none mt-1 max-w-full overflow-hidden" data-testid="payment-form-bill">
                    <span className="block truncate text-left max-w-full">
                      {paymentForm.vendor_account_id ? (() => {
                        const v = payableVendors.find(x => x.vendor_id === paymentForm.vendor_account_id);
                        if (!v) return paymentForm.vendor_account_id;
                        if (v.bill_count === 0) return `${v.vendor_name} — ${t('noInvoices') || 'No invoices'}`;
                        if (v.outstanding_balance <= 0.005) return `${v.vendor_name} — ${t('paid') || 'Paid'} — €0.00`;
                        const overduePart = v.overdue_count > 0 ? `${v.overdue_count} ${t('overdue') || 'overdue'}` : `${v.bill_count} ${t('billsCount') || 'bills'}`;
                        return `${v.vendor_name} — ${overduePart} — €${(v.outstanding_balance || 0).toFixed(2)}`;
                      })() : (t('selectVendor') || 'Select a vendor account')}
                    </span>
                  </SelectTrigger>
                  <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                    <SelectItem value="__none__">— {t('none') || 'None'} —</SelectItem>
                    {payableVendors.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-zinc-400">{t('noBillVendors') || 'No bill vendors configured.'}</div>
                    ) : payableVendors.map(v => {
                      // Phase 234 — Three display styles based on vendor state:
                      //  • Outstanding → "NAME — N overdue — €X.XX" (red accent)
                      //  • Fully paid  → "NAME — Paid — €0.00"      (green accent)
                      //  • No invoices → "NAME — No invoices"        (grey accent)
                      const hasOutstanding = (v.outstanding_balance || 0) > 0.005;
                      const hasBills = (v.bill_count || 0) > 0;
                      let rightBits;
                      let rightClass;
                      if (hasOutstanding) {
                        const overduePart = v.overdue_count > 0 ? `${v.overdue_count} ${t('overdue') || 'overdue'}` : `${v.bill_count} ${t('billsCount') || 'bills'}`;
                        rightBits = `${overduePart} — €${(v.outstanding_balance || 0).toFixed(2)}`;
                        rightClass = v.overdue_count > 0 ? 'text-red-700 font-semibold' : 'text-amber-700 font-semibold';
                      } else if (hasBills) {
                        rightBits = `${t('paid') || 'Paid'} — €0.00`;
                        rightClass = 'text-emerald-700';
                      } else {
                        rightBits = t('noInvoices') || 'No invoices';
                        rightClass = 'text-zinc-400';
                      }
                      const fullLabel = `${v.vendor_name} — ${rightBits}`;
                      return (
                        <SelectItem key={v.vendor_id} value={v.vendor_id} data-testid={`payable-vendor-${v.vendor_id}`} title={fullLabel}>
                          <span className="flex items-center justify-between gap-3 w-full min-w-0">
                            <span className="truncate text-xs">{v.vendor_name}</span>
                            <span className={`text-[10px] shrink-0 ${rightClass}`}>{rightBits}</span>
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {paymentForm.vendor_account_id && (() => {
                  const sel = payableVendors.find(v => v.vendor_id === paymentForm.vendor_account_id);
                  if (!sel) return null;
                  return (
                    <div className="mt-2 border border-emerald-300 bg-emerald-50/60 px-3 py-2" data-testid="payment-form-pay-to">
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">{t('payTo') || 'Pay To'}</div>
                          <div className="text-sm font-bold text-emerald-900 truncate" title={sel.vendor_name} data-testid="payment-form-pay-to-name">{sel.vendor_name}</div>
                          <div className="text-[10px] text-emerald-700 mt-0.5">
                            {sel.bill_count} {t('billsCount') || 'bills'}
                            {sel.overdue_count > 0 ? ` · ${sel.overdue_count} ${t('overdue') || 'overdue'}` : ''}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t('outstanding') || 'Outstanding'}</div>
                          <div className="text-sm font-mono font-bold text-emerald-800">&euro;{(sel.outstanding_balance || 0).toFixed(2)}</div>
                        </div>
                      </div>
                      <div className="mt-1 text-[10px] text-emerald-700 font-semibold">
                        ⇄ {t('syncedToBills') || 'Will sync with Bills module'} · {t('fifoAllocation') || 'oldest invoice first'}
                      </div>
                    </div>
                  );
                })()}
              </div>
              )}
              <div>
                <Label>{t('amount')} (&euro;) *</Label>
                <Input type="number" step="0.01" min="0.01" value={paymentForm.amount} onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))} className="rounded-none mt-1 font-mono" required data-testid="payment-form-amount" />
              </div>
              {/* Phase 230 — Hide the bucket Paid From selector when a vendor
                  account is linked. The payment auto-deducts from Final Balance
                  (global formula), no manual bucket selection.
                  Phase 545 — Removed the Paid From bucket selector entirely.
                  Bank payments now behave like real treasury movements; the
                  internal-bucket allocation (VAT / Social Ins / 12.5% / Reserve /
                  Final Balance) is no longer surfaced in this dialog. Behind
                  the scenes the payment is recorded against `final_balance` so
                  it shows up as a plain bank deduction. The green balance
                  preview below remains visible so the user always sees the
                  Available-before-payment / Payment / After-payment impact. */}
              {!paymentForm.vendor_account_id && (
              <div>
                {/* Phase 545 — Green balance preview only. Dropdown removed. */}
                {/* Phase 230 — Live available-balance panel. Final Balance uses the
                    global formula (Bank Balance − VAT − Social Ins − 12.5% Tax − Reserve)
                    everywhere — no per-account override. Bucket funds (VAT/12.5%/Reserve/Social)
                    show their own remaining bucket balance. */}
                {(() => {
                  // Phase 545 — The bucket Paid From dropdown was removed,
                  // so this panel always renders in "bank view": Available
                  // before payment (computed from the selected bank account),
                  // Payment amount, and After payment. No fund-bucket math
                  // here anymore — bank payments are pure treasury movements.
                  if (!paymentBankId) return null;
                  const baseAvail = computeBankBalance(paymentBankId);
                  if (baseAvail == null) return null;
                  const bk = bankAccounts.find(b => b.account_id === paymentBankId);
                  const labelSuffix = bk ? `— ${bk.name || bk.bank_name}` : `— ${t('bankAccount') || 'Bank Account'}`;
                  const editBack = editingPayment ? (editingPayment.amount || 0) : 0;
                  const effective = round2Safe(baseAvail + editBack);
                  const amt = parseFloat(paymentForm.amount) || 0;
                  const remaining = round2Safe(effective - amt);
                  const negativeAfter = remaining < 0;
                  // Bank-account flow: allow negative (overdraft) with a soft
                  // warning instead of a hard block — future income will
                  // close the gap.
                  const showNegativeWarning = negativeAfter;
                  return (
                    <div className={`mt-2 border-2 px-3 py-2 space-y-1 ${showNegativeWarning ? 'border-amber-400 bg-amber-50' : 'border-emerald-200 bg-emerald-50/60'}`} data-testid="paid-from-balance-hint">
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t('availableBeforePayment') || 'Available before payment'} {labelSuffix}</div>
                        <div className={`text-lg font-mono font-extrabold tabular-nums ${effective < 0 ? 'text-red-700' : 'text-emerald-800'}`} data-testid="paid-from-available-amount">&euro;{effective.toFixed(2)}</div>
                      </div>
                      {amt > 0 && (
                        <>
                          <div className="flex items-center justify-between pt-1 border-t border-zinc-200/70">
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t('payment') || 'Payment'}</div>
                            <div className="text-sm font-mono font-bold tabular-nums text-zinc-900" data-testid="paid-from-payment-amount">&minus;&euro;{amt.toFixed(2)}</div>
                          </div>
                          <div className="flex items-center justify-between pt-1 border-t border-zinc-200/70">
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t('afterPayment') || 'After payment'}</div>
                            <div className={`text-sm font-mono font-bold tabular-nums ${remaining < 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="paid-from-after-amount">&euro;{remaining.toFixed(2)}</div>
                          </div>
                        </>
                      )}
                      {showNegativeWarning && (
                        <div className="text-[11px] text-amber-800 mt-1 font-semibold flex items-center gap-1.5" data-testid="paid-from-negative-warning">
                          <span className="inline-flex items-center text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-red-100 text-red-700 border border-red-300">{t('deficit') || 'Deficit'}</span>
                          <span>{t('negativeBalanceAllowed') || 'Bank balance will go negative. Payment is still allowed — future income will cover the deficit.'}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              )}
              <div>
                <Label>{t('notes')}</Label>
                <Textarea value={paymentForm.notes} onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none mt-1" rows={2} data-testid="payment-form-notes" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPaymentDialog(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="rounded-none bg-red-700" data-testid="payment-form-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* INTERNAL TRANSFER DIALOG — Phase 134. Moves money between bank accounts without affecting income/VAT/deductions. */}
        <Dialog open={transferDialog} onOpenChange={(open) => { setTransferDialog(open); if (!open) setEditingTransfer(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="transfer-dialog">
            <DialogHeader><DialogTitle>{editingTransfer ? t('editTransfer') : t('addTransfer')}</DialogTitle></DialogHeader>
            <form onSubmit={submitTransfer} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('date')}</Label>
                  <DatePickerDMY value={transferForm.date} onChange={v => setTransferForm(f => ({ ...f, date: v }))} className="mt-1 font-mono" data-testid="transfer-form-date" />
                </div>
                <div>
                  <Label>{t('amount')} (&euro;) *</Label>
                  <Input type="number" step="0.01" min="0.01" value={transferForm.amount} onChange={e => setTransferForm(f => ({ ...f, amount: e.target.value }))} className="rounded-none mt-1 font-mono" required data-testid="transfer-form-amount" />
                </div>
              </div>
              <div>
                <Label>{t('fromAccount')} *</Label>
                <Select value={transferForm.from_account_id} onValueChange={v => setTransferForm(f => ({ ...f, from_account_id: v }))} required>
                  <SelectTrigger className="rounded-none mt-1" data-testid="transfer-form-from"><SelectValue placeholder={t('selectAccount')} /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map(b => <SelectItem key={b.account_id} value={b.account_id} data-testid={`transfer-from-opt-${b.account_id}`}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('toAccount')} *</Label>
                <Select value={transferForm.to_account_id} onValueChange={v => setTransferForm(f => ({ ...f, to_account_id: v }))} required>
                  <SelectTrigger className="rounded-none mt-1" data-testid="transfer-form-to"><SelectValue placeholder={t('selectAccount')} /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.filter(b => b.account_id !== transferForm.from_account_id).map(b => <SelectItem key={b.account_id} value={b.account_id} data-testid={`transfer-to-opt-${b.account_id}`}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('reference')}</Label>
                <Input value={transferForm.reference} onChange={e => setTransferForm(f => ({ ...f, reference: e.target.value }))} className="rounded-none mt-1" data-testid="transfer-form-reference" />
              </div>
              <div>
                <Label>{t('notes')}</Label>
                <Textarea value={transferForm.notes} onChange={e => setTransferForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none mt-1" rows={2} data-testid="transfer-form-notes" />
              </div>
              <div className="border border-sky-200 bg-sky-50 p-2.5 text-[10px] text-sky-800 font-medium" data-testid="transfer-info-notice">
                {t('internalTransfersDesc')}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTransferDialog(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="rounded-none bg-sky-700" data-testid="transfer-form-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>


        {/* CATEGORY DIALOG */}
        <Dialog open={catDialog} onOpenChange={(open) => { setCatDialog(open); if (!open) setEditingCat(null); }}>
          <DialogContent className="sm:max-w-sm" data-testid="category-dialog">
            <DialogHeader><DialogTitle>{editingCat ? t('editCategory') : t('addCategory')}</DialogTitle></DialogHeader>
            <form onSubmit={handleCatSubmit} className="space-y-3">
              <div><Label>{t('englishName')} *</Label><Input value={catForm.name_en} onChange={e => setCatForm(f => ({ ...f, name_en: e.target.value }))} className="rounded-none mt-1" required data-testid="cat-form-name-en" /></div>
              <div><Label>{t('greekName')}</Label><Input value={catForm.name_el} onChange={e => setCatForm(f => ({ ...f, name_el: e.target.value }))} className="rounded-none mt-1" data-testid="cat-form-name-el" placeholder={t('optionalFallbackToEnglish')} /></div>
              {catScope === 'finance' && (
                <div>
                  <Label>{t('bucket')}</Label>
                  <Select value={catForm.bucket || '__none__'} onValueChange={v => setCatForm(f => ({ ...f, bucket: v === '__none__' ? '' : v }))}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="cat-form-bucket"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('none')}</SelectItem>
                      <SelectItem value="vat">{t('bucketVat')}</SelectItem>
                      <SelectItem value="allocation_12_5">{t('bucketAllocation')}</SelectItem>
                      <SelectItem value="reserve_10">{t('bucketReserve')}</SelectItem>
                      <SelectItem value="social_insurance_10">{t('bucketSocialInsurance')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-400 mt-1">{t('bucketHelp')}</p>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCatDialog(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="rounded-none" data-testid="cat-form-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* DEDUCTIONS DRILL-DOWN DIALOG (Phase 83; Phase 124: added Transactions ledger view)
            Phase 519 — Modal is hidden per user request (option 1a).
            Underlying VAT/12.5%/10%/10% accounting + the `/api/company/
            deductions-ledger` endpoint remain intact for tax compliance,
            but no UI surface opens this drill-down anymore. We keep the
            JSX commented out (not removed) so we can restore it instantly
            if the user changes their mind. */}
        {false && (
        <Dialog open={deductionsModal} onOpenChange={setDeductionsModal}>
          <DialogContent className="sm:max-w-3xl" data-testid="deductions-drilldown-dialog">
            <DialogHeader>
              <DialogTitle>{t('deductionsBreakdown')}</DialogTitle>
            </DialogHeader>
            {/* View switcher — Summary (per-bucket table) ↔ Transactions (full ledger with running balance) */}
            <div className="flex items-center gap-2 border-b border-zinc-200 -mt-2 mb-1">
              <button
                type="button"
                onClick={() => setDeductionsView('summary')}
                className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-colors ${deductionsView === 'summary' ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
                data-testid="deductions-tab-summary"
              >{t('summary')}</button>
              <button
                type="button"
                onClick={() => setDeductionsView('transactions')}
                className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-colors ${deductionsView === 'transactions' ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
                data-testid="deductions-tab-transactions"
              >{t('transactions')} {deductionsLedger?.count > 0 && <span className="text-[10px] text-zinc-400 font-mono">({deductionsLedger.count})</span>}</button>
            </div>
            {deductionsView === 'transactions' ? (
              // Phase 124 — Combined transactions ledger across all 4 deduction buckets with running balance.
              <div className="space-y-2 max-h-[60vh] overflow-auto">
                {!deductionsLedger ? (
                  <div className="text-center text-xs text-zinc-400 py-8">{t('loading')}...</div>
                ) : deductionsLedger.rows.length === 0 ? (
                  <div className="text-center text-xs text-zinc-400 py-8">{t('noTransactions')}</div>
                ) : (
                  <table className="excel-table text-[11px]" data-testid="deductions-ledger-table">
                    <thead>
                      <tr>
                        <th className="w-24">{t('date')}</th>
                        <th>{t('type')}</th>
                        <th>{t('description')}</th>
                        <th className="text-right w-24">{t('amount')} (&euro;)</th>
                        <th className="text-right w-28">{t('bucketBalance')}</th>
                        <th className="text-right w-28">{t('runningBalance')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deductionsLedger.rows.map((r, i) => {
                        const bg = r.type === 'income' ? 'bg-emerald-50/30' : 'bg-red-50/30';
                        const amtColor = r.amount >= 0 ? 'text-emerald-700' : 'text-red-700';
                        return (
                          <tr key={`${r.ref_id}-${r.bucket}-${i}`} className={bg} data-testid={`ded-row-${i}`}>
                            <td className="font-mono text-[11px] text-zinc-600">{formatDateDMY(r.date)}</td>
                            <td>
                              <span className="inline-flex items-center gap-1.5">
                                <span className={`w-1.5 h-1.5 rounded-full ${r.bucket === 'vat' ? 'bg-amber-400' : r.bucket === 'allocation_12_5' ? 'bg-violet-400' : r.bucket === 'reserve_10' ? 'bg-orange-400' : 'bg-teal-400'}`} />
                                <span className="text-[11px] font-medium">{r.label}</span>
                                <span className="text-[9px] text-zinc-400 uppercase">{r.type === 'income' ? '+' : '−'}</span>
                              </span>
                            </td>
                            <td className="text-[11px] text-zinc-600 truncate max-w-[220px]">{r.description || '—'}</td>
                            <td className={`font-mono text-right font-semibold tabular-nums ${amtColor}`}>{r.amount >= 0 ? '+' : ''}€{r.amount.toFixed(2)}</td>
                            <td className="font-mono text-right tabular-nums text-zinc-700">&euro;{r.bucket_balance.toFixed(2)}</td>
                            <td className="font-mono text-right font-bold tabular-nums text-zinc-900">&euro;{r.combined_balance.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-zinc-100 border-t-2 border-zinc-300 font-semibold">
                        <td colSpan={3} className="text-right text-[11px]">{t('totalRemaining')}</td>
                        <td colSpan={2}></td>
                        <td className="font-mono text-right font-extrabold tabular-nums text-zinc-900" data-testid="deductions-ledger-total">&euro;{(deductionsLedger.totals?.combined || 0).toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
                <div className="text-[10px] text-zinc-400 italic px-1">
                  {t('ledgerLegend')}
                </div>
              </div>
            ) : (
              summary && (() => {
              const gross = summary.total_income || 0;
              const netBase = summary.total_net_income || 0; // after VAT extracted — base for all deductions
              const pb = summary.payments_by_bucket || {};
              // Phase 116 — VAT is INFORMATIONAL ONLY and is NOT listed as a deduction.
              // Deductions are calculated on Net Income only: 12.5% + Reserve 10% + Social Insurance 10%.
              const rows = [
                { key: 'allocation_12_5', label: t('bucketAllocation'), pct: '12.5%', amount: summary.total_allocation_12_5 || 0, paid: pb.allocation_12_5 || 0, remaining: summary.balance_12_5 || 0, base: netBase, color: 'violet', route: '/finance/allocations/12-5' },
                { key: 'reserve_10', label: t('bucketReserve'), pct: '10%', amount: summary.total_reserve_10 || 0, paid: pb.reserve_10 || 0, remaining: summary.balance_reserve_10 || 0, base: netBase, color: 'orange', route: '/finance/reserve-10' },
                { key: 'social_insurance_10', label: t('bucketSocialInsurance'), pct: '10%', amount: summary.total_social_insurance_10 || 0, paid: pb.social_insurance_10 || 0, remaining: summary.balance_social_insurance_10 || 0, base: netBase, color: 'teal', route: '/finance/social-insurance-10' },
              ];
              const total = rows.reduce((s, r) => s + r.amount, 0);
              const totalPaid = rows.reduce((s, r) => s + r.paid, 0);
              const totalRemaining = rows.reduce((s, r) => s + r.remaining, 0);
              return (
                <div className="space-y-3">
                  <div className="border border-zinc-200 overflow-hidden">
                    <table className="excel-table" data-testid="deductions-drilldown-table">
                      <thead>
                        <tr>
                          <th>{t('type')}</th>
                          <th className="text-right w-16">{t('percent')}</th>
                          <th className="text-right w-28">{t('amount')} (&euro;)</th>
                          <th className="text-right w-24">{t('paid')} (&euro;)</th>
                          <th className="text-right w-28">{t('remaining')} (&euro;)</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(r => (
                          <tr
                            key={r.key}
                            className="cursor-pointer hover:bg-zinc-50 transition-colors"
                            onClick={() => { setDeductionsModal(false); navigate(r.route); }}
                            data-testid={`deduction-row-${r.key}`}
                          >
                            <td className="text-sm">
                              <span className="inline-flex items-center gap-1.5">
                                <span className={`w-2 h-2 bg-${r.color}-400 rounded-full`} />
                                <span className="font-medium">{r.label}</span>
                              </span>
                            </td>
                            <td className="font-mono text-right text-xs text-zinc-600">{r.pct}</td>
                            <td className={`font-mono text-right text-sm font-semibold text-${r.color}-700`} data-testid={`deduction-amount-${r.key}`}>&euro;{r.amount.toFixed(2)}</td>
                            <td className="font-mono text-right text-sm text-red-700" data-testid={`deduction-paid-${r.key}`}>&euro;{r.paid.toFixed(2)}</td>
                            <td className={`font-mono text-right text-sm font-bold ${r.remaining < 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid={`deduction-remaining-${r.key}`}>&euro;{r.remaining.toFixed(2)}</td>
                            <td className="text-right"><ArrowRight size={12} className="text-zinc-400" /></td>
                          </tr>
                        ))}
                        <tr className="bg-zinc-50 font-semibold border-t-2 border-zinc-300">
                          <td className="text-sm text-right" colSpan={2}>{t('totalDeductions')}</td>
                          <td className="font-mono text-right text-sm font-bold text-zinc-900" data-testid="deductions-drilldown-total">&euro;{total.toFixed(2)}</td>
                          <td className="font-mono text-right text-sm text-red-700" data-testid="deductions-drilldown-paid">&euro;{totalPaid.toFixed(2)}</td>
                          <td className={`font-mono text-right text-sm font-extrabold ${totalRemaining < 0 ? 'text-red-800' : 'text-emerald-800'}`} data-testid="deductions-drilldown-remaining">&euro;{totalRemaining.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {/* Phase 118 — footer formula: Net Income − Deductions Remaining = contribution to Final Balance.
                      Shows both accrued and remaining so the user sees the effect of their payments clearly. */}
                  {(() => {
                    const op = (summary.bank_accounts || []).reduce((s, b) => s + (b.opening_balance || 0), 0);
                    const remaining = totalRemaining;
                    const fb = Math.round((op + netBase - remaining) * 100) / 100;
                    return (
                      <div className="text-[11px] text-zinc-500 flex items-center gap-3 flex-wrap font-mono">
                        <span>{t('openingBalance')}: &euro;{op.toFixed(2)}</span>
                        <span>+</span>
                        <span>{t('netIncomeBeforeDeductions')}: &euro;{netBase.toFixed(2)}</span>
                        <span>&minus;</span>
                        <span title={`${t('accrued')}: €${total.toFixed(2)} − ${t('paid')}: €${totalPaid.toFixed(2)}`}>{t('deductionsRemaining')}: &euro;{remaining.toFixed(2)}</span>
                        <span>=</span>
                        <span className={`font-bold ${fb >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="deductions-drilldown-final-balance">&euro;{fb.toFixed(2)}</span>
                      </div>
                    );
                  })()}
                  <div className="text-[10px] text-zinc-400 italic">
                    {t('vatInfoOnlyNote')} {t('bucketVat')}: &euro;{(summary.total_vat_income || 0).toFixed(2)} ({t('reference')})
                  </div>
                </div>
              );
            })()
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeductionsModal(false)} className="rounded-none">{t('close')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}

        {/* SUPPLIER DIALOGS (Phase 86) */}
        <Dialog open={supplierDialog} onOpenChange={(open) => { setSupplierDialog(open); if (!open) setEditingSupplier(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="supplier-dialog">
            <DialogHeader><DialogTitle>{editingSupplier ? t('editSupplier') : t('addSupplier')}</DialogTitle></DialogHeader>
            <form onSubmit={handleSupplierSubmit} className="space-y-3">
              <div><Label>{t('name')} *</Label><Input value={supplierForm.name} onChange={e => setSupplierForm(f => ({ ...f, name: e.target.value }))} className="rounded-none mt-1" required data-testid="supplier-form-name" /></div>
              <p className="text-[11px] text-zinc-500">{t('statementSupplierDesc')}</p>
              <div><Label>{t('notes')}</Label><Textarea value={supplierForm.notes} onChange={e => setSupplierForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none mt-1 resize-none" rows={2} placeholder={t('addNotesOptional')} data-testid="supplier-form-notes" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSupplierDialog(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="rounded-none" data-testid="supplier-form-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* PURCHASE DIALOG removed in Phase 87 — one-time purchases are now regular Expenses */}

        {/* INVOICE DIALOG (statement supplier) */}
        <Dialog open={invoiceDialog} onOpenChange={(open) => { setInvoiceDialog(open); if (!open) setEditingInvoice(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="invoice-dialog">
            <DialogHeader><DialogTitle>{editingInvoice ? t('editInvoice') : t('addInvoice')}</DialogTitle></DialogHeader>
            <form onSubmit={handleInvoiceSubmit} className="space-y-3">
              {/* Phase 944 — Invoice Type toggle. Cash invoices are
                  self-settled (no payment receipt required). */}
              <div>
                <Label>{t('invoiceType') || 'Τύπος Τιμολογίου'}</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[
                    { k: 'credit', el: 'Πιστωτικό (με πληρωμή)', en: 'Credit (paid later)', color: 'bg-red-700' },
                    { k: 'cash',   el: 'Μετρητοίς (εξοφλημένο)', en: 'Cash (paid on issue)', color: 'bg-emerald-700' },
                  ].map(opt => {
                    const on = (invoiceForm.invoice_type || 'credit') === opt.k;
                    return (
                      <button
                        key={opt.k}
                        type="button"
                        onClick={() => setInvoiceForm(f => ({ ...f, invoice_type: opt.k }))}
                        className={`text-xs px-3 py-2 border transition-colors ${on ? `${opt.color} text-white border-transparent` : 'border-zinc-300 text-zinc-700 hover:bg-zinc-50'}`}
                        data-testid={`invoice-form-type-${opt.k}`}
                      >
                        {(t('lang') === 'en' || language === 'en') ? opt.en : opt.el}
                      </button>
                    );
                  })}
                </div>
                {invoiceForm.invoice_type === 'cash' && (
                  <p className="text-[11px] text-emerald-700 mt-1.5" data-testid="cash-hint">
                    {language === 'en'
                      ? 'This invoice will be automatically marked as PAID. No separate payment receipt is required.'
                      : 'Το τιμολόγιο θα σημειωθεί αυτόματα ως ΠΛΗΡΩΜΕΝΟ. Δεν χρειάζεται ξεχωριστή απόδειξη πληρωμής.'}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t('date')}</Label><Input type="date" value={invoiceForm.date} onChange={e => setInvoiceForm(f => ({ ...f, date: e.target.value }))} className="rounded-none mt-1" required /></div>
                <div><Label>{t('invoiceNumber')}</Label><Input value={invoiceForm.invoice_number} onChange={e => setInvoiceForm(f => ({ ...f, invoice_number: e.target.value }))} className="rounded-none mt-1" data-testid="invoice-form-number" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t('netAmount')} (€)</Label><Input type="number" step="0.01" value={invoiceForm.net} onChange={e => setInvoiceForm(f => ({ ...f, net: e.target.value }))} className="rounded-none mt-1" data-testid="invoice-form-net" /></div>
                <div><Label>{t('vatAmount')} (€)</Label><Input type="number" step="0.01" value={invoiceForm.vat} onChange={e => setInvoiceForm(f => ({ ...f, vat: e.target.value }))} className="rounded-none mt-1" data-testid="invoice-form-vat" /></div>
                <div><Label>{t('total')} (€)</Label><Input type="number" step="0.01" value={invoiceForm.total} onChange={e => setInvoiceForm(f => ({ ...f, total: e.target.value }))} className="rounded-none mt-1" placeholder={t('autoCalc')} data-testid="invoice-form-total" /></div>
              </div>
              <div><Label>{t('notes')}</Label><Textarea value={invoiceForm.notes} onChange={e => setInvoiceForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none mt-1 resize-none" rows={2} placeholder={t('addNotesOptional')} /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInvoiceDialog(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="rounded-none bg-red-700" data-testid="invoice-form-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* PAYMENT DIALOG (statement supplier) */}
        <Dialog open={payDialog} onOpenChange={(open) => { setPayDialog(open); if (!open) setEditingPay(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
            <DialogHeader><DialogTitle>{editingPay ? t('editPayment') : t('addPayment')}</DialogTitle></DialogHeader>
            <form onSubmit={handlePaySubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t('date')}</Label><Input type="date" value={payForm.date} onChange={e => setPayForm(f => ({ ...f, date: e.target.value }))} className="rounded-none mt-1" required /></div>
                <div><Label>{t('amount')} (€) *</Label><Input type="number" step="0.01" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} className="rounded-none mt-1" required data-testid="payment-form-amount" /></div>
              </div>
              <div><Label>{t('paymentNumber')}</Label><Input value={payForm.payment_number} onChange={e => setPayForm(f => ({ ...f, payment_number: e.target.value }))} className="rounded-none mt-1" placeholder={t('optionalShort')} data-testid="payment-form-number" /></div>
              <div>
                <Label>{t('paymentMethod')} *</Label>
                <Select value={payForm.method} onValueChange={v => setPayForm(f => ({ ...f, method: v }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="payment-form-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">{t('bankSource')}</SelectItem>
                    <SelectItem value="cash">{t('cashSource')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {payForm.method === 'bank' && (
                <div>
                  <Label>{t('bankAccount')}</Label>
                  <BankAccountCombobox
                    t={t}
                    bankAccounts={bankAccounts}
                    value={payForm.bank_account_id}
                    onChange={(id) => setPayForm(f => ({ ...f, bank_account_id: id }))}
                    onCreateBank={() => { setPayDialog(false); setActiveTab('banks'); }}
                    testid="payment-form-bank"
                  />
                </div>
              )}

              {/* Allocations — Phase 452: oldest open invoices first */}
              {!editingPay && (() => {
                const open = payOpenInvoices
                  .filter(i => (i.status || '') !== 'paid' && Number(i.outstanding ?? (i.total - (i.paid_amount || 0))) > 0.005)
                  .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
                if (open.length === 0) return null;
                const allocSum = (payForm.allocations || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
                const amt = Number(payForm.amount) || 0;
                const unallocated = Math.max(0, amt - allocSum);
                return (
                  <div className="border border-zinc-200 p-3 bg-zinc-50" data-testid="sup-pay-allocations">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t('matchPaymentToInvoice') || 'Allocate to invoices'} <span className="text-zinc-400 font-normal">({open.length} {t('openInvoices') || 'open'})</span></p>
                      <p className="text-[11px] text-zinc-500">
                        {t('allocated') || 'Allocated'}: <span className="font-mono font-semibold">€{allocSum.toFixed(2)}</span>
                        {' · '}
                        {t('unallocated') || 'Unallocated'}: <span className={`font-mono font-semibold ${unallocated > 0.005 ? 'text-amber-700' : 'text-emerald-700'}`}>€{unallocated.toFixed(2)}</span>
                      </p>
                    </div>
                    <div className="max-h-48 overflow-y-auto bg-white border border-zinc-100">
                      <table className="w-full text-xs">
                        <thead className="bg-zinc-50 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-semibold">#</th>
                            <th className="text-left px-2 py-1.5 font-semibold">{t('date') || 'Date'}</th>
                            <th className="text-right px-2 py-1.5 font-semibold">{t('total') || 'Total'}</th>
                            <th className="text-right px-2 py-1.5 font-semibold">{t('outstanding') || 'Outstanding'}</th>
                            <th className="text-right px-2 py-1.5 font-semibold w-32">{t('allocate') || 'Allocate'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {open.map(inv => {
                            const allocated = (payForm.allocations || []).find(a => a.invoice_id === inv.invoice_id)?.amount || '';
                            const outstanding = Number(inv.outstanding ?? (inv.total - (inv.paid_amount || 0)));
                            return (
                              <tr key={inv.invoice_id} className="border-t border-zinc-100" data-testid={`sup-pay-alloc-row-${inv.invoice_id}`}>
                                <td className="px-2 py-1.5 font-mono">{inv.invoice_number || '—'}</td>
                                <td className="px-2 py-1.5 font-mono text-zinc-500">{fmtDate(inv.date)}</td>
                                <td className="px-2 py-1.5 font-mono text-right">€{Number(inv.total || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 font-mono text-right text-red-700 font-semibold">€{outstanding.toFixed(2)}</td>
                                <td className="px-2 py-1">
                                  <div className="flex items-center justify-end gap-1">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      max={outstanding.toFixed(2)}
                                      min="0"
                                      value={allocated}
                                      onChange={e => setSupplierAllocation(inv.invoice_id, e.target.value)}
                                      className="rounded-none h-7 w-24 text-xs text-right font-mono"
                                      placeholder="0.00"
                                      data-testid={`sup-pay-alloc-${inv.invoice_id}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => setSupplierAllocation(inv.invoice_id, outstanding.toFixed(2))}
                                      className="text-[9px] text-blue-600 hover:underline whitespace-nowrap"
                                      data-testid={`sup-pay-alloc-full-${inv.invoice_id}`}
                                    >{t('full') || 'Full'}</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
              <div><Label>{t('notes')}</Label><Textarea value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none mt-1 resize-none" rows={2} placeholder={t('addNotesOptional')} /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPayDialog(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="rounded-none bg-emerald-700" data-testid="payment-form-save">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Phase 110 — Overdraft confirm dialog. Shown when a bucket-linked payment exceeds the available balance. */}
        <AlertDialog open={!!overdraftDialog} onOpenChange={(open) => { if (!open) setOverdraftDialog(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('exceedsBalanceTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {overdraftDialog && (
                  <span>
                    {t('bucket')}: <strong>{bucketLabelOf(overdraftDialog.bucket)}</strong><br />
                    {t('availableBalance')}: <strong>€{overdraftDialog.avail.toFixed(2)}</strong><br />
                    {t('paymentAmount')}: <strong>€{overdraftDialog.amt.toFixed(2)}</strong><br />
                    <span className="text-red-600">{t('overdraftBy')}: €{overdraftDialog.diff.toFixed(2)}</span>
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={() => submitPayment(true)} className="rounded-none bg-red-600" data-testid="confirm-overdraft">{t('proceedAnyway')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* DELETE CONFIRM — Phase 108: inline cascade prompt when backend refuses a supplier delete */}
        <AlertDialog open={deleteDialog} onOpenChange={(open) => { setDeleteDialog(open); if (!open) setDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{deleteTarget?.askCascade ? t('cascadeDeleteTitle') : t('confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget?.askCascade ? (deleteTarget.cascadeMessage || t('deleteConfirmation')) : t('deleteConfirmation')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="rounded-none bg-red-600" data-testid="confirm-delete">
                {deleteTarget?.askCascade ? t('cascadeDeleteConfirm') : t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Phase 223 — Statement Row Detail (View) Modal */}
        <Dialog open={!!stmtViewRow} onOpenChange={(open) => !open && setStmtViewRow(null)}>
          <DialogContent className="sm:max-w-md rounded-none" data-testid="stmt-view-dialog">
            <DialogHeader>
              <DialogTitle>{t('transactionDetails') || 'Transaction Details'}</DialogTitle>
            </DialogHeader>
            {stmtViewRow && (
              <div className="space-y-2.5 text-sm">
                {[
                  { lbl: t('date'), val: fmtDate(stmtViewRow.date) },
                  { lbl: t('client') || 'Client / Source', val: stmtViewRow.sourceLabel || '—' },
                  { lbl: t('incomeDescription') || 'Description', val: stmtViewRow.desc || '—' },
                  ...(stmtViewRow.source === 'income' ? [
                    { lbl: `${t('total')} (€)`, val: `€${(stmtViewRow.gross || 0).toFixed(2)}`, color: 'text-emerald-700' },
                    { lbl: `${t('vatAmount')} (€)`, val: `€${(stmtViewRow.vat || 0).toFixed(2)}`, color: 'text-amber-700' },
                    { lbl: `${t('netAmount')} (€)`, val: `€${(stmtViewRow.net || 0).toFixed(2)}` },
                    { lbl: `${t('deductions')} (€)`, val: `€${(stmtViewRow.deductions || 0).toFixed(2)}`, color: 'text-red-700' },
                    { lbl: `${t('finalNet')} (€)`, val: `€${(stmtViewRow.final_net || 0).toFixed(2)}`, color: 'text-blue-700 font-bold' },
                  ] : [
                    { lbl: `${t('amount')} (€)`, val: `${stmtViewRow.amt < 0 ? '−' : ''}€${Math.abs(stmtViewRow.amt || 0).toFixed(2)}`, color: stmtViewRow.amt < 0 ? 'text-red-700' : 'text-emerald-700' },
                  ]),
                  { lbl: `${t('runningBalance')} (€)`, val: `€${(stmtViewRow.running || 0).toFixed(2)}`, color: 'text-zinc-900 font-semibold' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-zinc-100 pb-1.5">
                    <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">{row.lbl}</span>
                    <span className={`font-mono text-sm ${row.color || 'text-zinc-900'}`}>{row.val}</span>
                  </div>
                ))}
                {stmtViewRow.synced_with_building && (
                  <div className="px-2 py-1.5 bg-sky-50 border border-sky-200 text-[11px] text-sky-800 font-semibold">
                    ⇄ {t('syncedWithBuilding') || 'Synced with Building Management'}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStmtViewRow(null)} className="rounded-none" data-testid="stmt-view-close">{t('close') || t('cancel')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
